import React, { useState, useRef, useEffect } from "react";
import { ProjectDocument, AppSettings, ExtractedDataItem, Shot, Asset, TaskType, AssetTaskType, ProjectSetup, MappingPreference, TopSheet, RateCard, AssetRateCard, ShotStatus, ShootingSchedule } from "../types";
import { GoogleGenAI, Type } from "@google/genai";
import { FileText, Film, Loader2, Trash2, Sparkles, Image as ImageIcon, LayoutGrid, ChevronDown, ChevronRight, Calendar, AlertCircle, CheckCircle2, X, Settings2, FilePlus, LogOut, FolderPlus, Upload, ExternalLink, Search, Folder, History, Eye, Save, RotateCcw, Plus, Table2, FileSpreadsheet, RefreshCw, Wand2, Download, Database, ArrowUpDown } from "lucide-react";
import { cn } from "../utils";
import { toast } from "sonner";
import Papa from "papaparse";
import { v4 as uuidv4 } from "uuid";
import { DOCUMENT_ANALYSIS_PROMPT } from "../lib/documentPrompt";
import { DOCUMENT_ANALYSIS_SCHEMA } from "../lib/documentSchema";
import { SCRIPT_DOCUMENT_PROMPT } from "../lib/scriptDocumentPrompt";
import { SCRIPT_DOCUMENT_SCHEMA } from "../lib/scriptDocumentSchema";
import {
  SHOOTING_SCHEDULE_PROMPT,
  SHOOTING_SCHEDULE_RECOVERY_PROMPT,
  SHOOTING_SCHEDULE_CAST_RECOVERY_PROMPT,
} from "../lib/shootingSchedulePrompt";
import {
  SHOOTING_SCHEDULE_SCHEMA,
  SHOOTING_SCHEDULE_RECOVERY_SCHEMA,
  SHOOTING_SCHEDULE_CAST_RECOVERY_SCHEMA,
} from "../lib/shootingScheduleSchema";
import { buildScriptSceneBatchPrompt } from "../lib/scriptSceneBatchPrompt";
import { SCRIPT_SCENE_BATCH_SCHEMA } from "../lib/scriptSceneBatchSchema";
import { TASK_ALIASES, DEFAULT_RATES, DEFAULT_ASSET_RATES } from "../constants";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore/lite";
import { db } from "../firebase";
import { DuplicateImportAssetCandidate, DuplicateImportShotCandidate, ImportProgressModal, ImportProgressData } from "./ImportProgressModal";
import { ImportResultReport } from "./ImportResultReport";
import { normalizeImportedBidStatus } from "../lib/bidStatus";
import { ShootingScheduleImportModal } from "./ShootingScheduleImportModal";
import { buildShootingScheduleImportDraft } from "../services/shootingScheduleService";
import { SectionPillHeader } from "./SectionPillHeader";
import type { Scene, Sequence } from "../models/production";
import { deriveHierarchyFromShotCode } from "../shared/shotCodeParser";

const MAX_CONCURRENT_ANALYSES = 2;

function DocumentAnalysisView({ result }: { result: any }) {
  if (!result) return null;
  const displayResult =
    result?.script_data
      ? {
          ...result,
          script_data: sanitizeScriptData(result.script_data),
        }
      : result;

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      return (
        <ul className="list-disc pl-5 space-y-1">
          {value.map((item, i) => (
            <li key={i}>{renderValue(item)}</li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'object') {
      return (
        <div className="space-y-2 mt-1">
          {Object.entries(value).map(([k, v]) => {
            if (v === null || v === undefined || (Array.isArray(v) && v.length === 0)) return null;
            return (
              <div key={k} className="bg-white/60 dark:bg-slate-900/70 p-2 rounded border border-slate-200/50 dark:border-slate-700/70">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">{k.replace(/_/g, ' ')}:</span>
                <span className="text-xs text-slate-800 dark:text-slate-200">{renderValue(v)}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return String(value);
  };

  const renderSection = (title: string, data: any) => {
    if (!data || (Array.isArray(data) && data.length === 0) || (typeof data === 'object' && Object.keys(data).length === 0)) return null;
    
    return (
      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm mb-3">
        <h5 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center">
          <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
          {title}
        </h5>
        <div className="text-xs text-slate-600 dark:text-slate-300">
          {typeof data === 'object' && !Array.isArray(data) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(data).map(([key, value]) => {
                if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return null;
                return (
                  <div key={key} className="bg-slate-50 dark:bg-slate-950 p-2 rounded border border-slate-100 dark:border-slate-800">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</div>
                    <div className="text-xs font-medium text-slate-800 dark:text-slate-100">
                      {renderValue(value)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            renderValue(data)
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {displayResult.document_summary && (
        <div className="bg-indigo-50 dark:bg-indigo-950/30 p-3 rounded-lg border border-indigo-100 dark:border-indigo-900/60 shadow-sm">
          <h5 className="text-sm font-bold text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
            <FileText className="w-4 h-4 mr-2 text-indigo-500" />
            Document Summary
          </h5>
          <div className="text-xs text-indigo-900 dark:text-indigo-100 whitespace-pre-wrap">{displayResult.document_summary}</div>
        </div>
      )}
      
      {renderSection("Global Fields", displayResult.global_fields)}
      {renderSection("Script Data", displayResult.script_data)}
      {renderSection("Legacy Bid CSV Data", displayResult.legacy_bid_csv_data)}
      {renderSection("Post Schedule Data", displayResult.post_schedule_data)}
      {renderSection("Shooting Schedule Data", displayResult.shooting_schedule_data)}
      {renderSection("Top Sheet Data", displayResult.top_sheet_data)}
      {renderSection("Quote/Estimate Data", displayResult.quote_estimate_data)}
      {renderSection("Cross Document Insights", displayResult.cross_document_insights)}
      {renderSection("Uncertainties", displayResult.uncertainties)}
    </div>
  );
}

interface Props {
  documents: ProjectDocument[];
  setDocuments: React.Dispatch<React.SetStateAction<ProjectDocument[]>>;
  globalSettings: AppSettings | null;
  onImportShots: (shots: Shot[], merge: boolean) => void;
  onImportAssets: (assets: Asset[], merge: boolean) => void;
  onImportOnset?: (onsetSupervision: number, merge: boolean) => void;
  onImportTopSheet?: (topSheet: TopSheet) => void;
  onImportShootingSchedule?: (schedule: ShootingSchedule) => void;
  existingShots: Shot[];
  existingAssets: Asset[];
  existingScenes?: Scene[];
  existingSequences?: Sequence[];
  onUpdateSetup: (setup: Partial<ProjectSetup> | ((prev: ProjectSetup) => Partial<ProjectSetup>)) => void;
  onUpdateRates: (rates: Partial<RateCard> | ((prev: RateCard) => Partial<RateCard>)) => void;
  onUpdateAssetRates: (rates: Partial<AssetRateCard> | ((prev: AssetRateCard) => Partial<AssetRateCard>)) => void;
  rates: RateCard;
  assetRates: AssetRateCard;
  setup: ProjectSetup;
  projectId?: string;
  user?: any;
  handleSetupDrive: () => void;
  handleLinkDrive: (driveId?: string, driveName?: string) => void;
  handleDisconnectDrive: () => void;
  isSettingUpDrive: boolean;
  driveConnected: boolean;
  isAdmin: boolean;
  onSaveDocument?: (doc: ProjectDocument) => Promise<void>;
  onRemoveDocument?: (docId: string) => Promise<void>;
  onPersistScriptStructure?: (doc: ProjectDocument) => Promise<void>;
  onImportStateChange?: (isImporting: boolean) => void;
  uiVisible?: boolean;
}

interface CSVMapping {
  csvHeader: string;
  appField: string;
}

function isLikelyScriptDocument(doc: ProjectDocument, parsed: any) {
  const name = `${doc.name} ${parsed?.filename || ""}`.toLowerCase();
  return (
    parsed?.detected_document_type === "script" ||
    Boolean(parsed?.script_data) ||
    name.includes("script") ||
    name.includes("readthrough") ||
    name.includes("draft")
  );
}

function isStoredScriptDocument(doc: ProjectDocument) {
  const lowerName = doc.name.toLowerCase();
  return (
    String(doc.analysisResult?.detected_document_type || "").toLowerCase() === "script" ||
    Boolean(doc.analysisResult?.script_data) ||
    lowerName.includes("script") ||
    lowerName.includes("readthrough") ||
    lowerName.includes("draft")
  );
}

function hasUsefulScriptData(parsed: any) {
  const scriptData = parsed?.script_data;
  if (!scriptData) return false;
  return Boolean(
    (Array.isArray(scriptData.scenes) && scriptData.scenes.length > 0) ||
    (Array.isArray(scriptData.scene_headings) && scriptData.scene_headings.length > 0) ||
    (Array.isArray(scriptData.scene_analysis) && scriptData.scene_analysis.length > 0) ||
    scriptData.scene_count
  );
}

function hasUsefulShootingScheduleData(parsed: any) {
  const scheduleData = parsed?.shooting_schedule_data;
  if (!scheduleData) return false;
  const shootDays = Array.isArray(scheduleData.shoot_days) ? scheduleData.shoot_days : [];
  return Boolean(
    shootDays.length > 0 ||
    shootDays.some((day: any) => Array.isArray(day?.scene_rows) && day.scene_rows.length > 0) ||
    (Array.isArray(scheduleData.shoot_weeks) && scheduleData.shoot_weeks.length > 0) ||
    (Array.isArray(scheduleData.omitted_scenes) && scheduleData.omitted_scenes.length > 0) ||
    (Array.isArray(scheduleData.unscheduled_scenes) && scheduleData.unscheduled_scenes.length > 0) ||
    (Array.isArray(scheduleData.cast_members) && scheduleData.cast_members.length > 0)
  );
}

function hasUsefulShootingScheduleCastData(parsed: any) {
  return Boolean(
    Array.isArray(parsed?.shooting_schedule_data?.cast_members) &&
      parsed.shooting_schedule_data.cast_members.length > 0,
  );
}

function mergeShootingScheduleCastMembers(existing: any[], recovered: any[]) {
  const merged = new Map<string, any>();
  [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(recovered) ? recovered : [])].forEach((entry) => {
    const castCode = String(entry?.cast_code || "").trim().toUpperCase();
    const characterName = String(entry?.character_name || "").trim().toUpperCase();
    const rawLabel = String(entry?.raw_label || "").trim().toUpperCase();
    const key = [castCode, characterName, rawLabel].filter(Boolean).join("|");
    if (!key) return;
    if (!merged.has(key)) {
      merged.set(key, entry);
      return;
    }
    const current = merged.get(key) || {};
    merged.set(key, {
      ...current,
      ...entry,
      cast_code: current.cast_code || entry.cast_code,
      character_name: current.character_name || entry.character_name,
      raw_label: current.raw_label || entry.raw_label,
      type: current.type || entry.type,
    });
  });
  return Array.from(merged.values());
}

function formatEpisodeBadgeValue(value?: string | null) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  const wordToNumber: Record<string, number> = {
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
    FIVE: 5,
    SIX: 6,
    SEVEN: 7,
    EIGHT: 8,
    NINE: 9,
    TEN: 10,
    ELEVEN: 11,
    TWELVE: 12,
  };
  const uppercase = normalized.toUpperCase();
  const trailingDigits = normalized.match(/(\d+)$/)?.[1];
  if (trailingDigits) {
    return `Ep${String(Number.parseInt(trailingDigits, 10)).padStart(2, "0")}`;
  }
  if (uppercase in wordToNumber) {
    return `Ep${String(wordToNumber[uppercase]).padStart(2, "0")}`;
  }
  return normalized;
}

function isLikelyShootingScheduleDocument(doc: ProjectDocument, parsed?: any) {
  const name = `${doc.name} ${parsed?.filename || ""}`.toLowerCase();
  const detected = String(parsed?.detected_document_type || "").toLowerCase();
  return (
    detected === "shooting_schedule" ||
    name.includes("shooting schedule") ||
    name.includes("one line") ||
    (name.includes("shoot") && name.includes("schedule"))
  );
}

function parseIndexedSceneNumber(entry: unknown) {
  const value = String(entry || "").trim();
  const matched = value.match(/scene\s*([0-9]+[A-Z]?)/i);
  return matched?.[1] || null;
}

function normalizeSceneNumberToken(value: unknown) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  const matched = raw.match(/^SCENE\s*([0-9]+[A-Z]?)$/i);
  return matched?.[1] || raw;
}

function chunkSceneNumbers(sceneNumbers: string[], size: number) {
  const chunks: string[][] = [];
  for (let index = 0; index < sceneNumbers.length; index += size) {
    chunks.push(sceneNumbers.slice(index, index + size));
  }
  return chunks;
}

function parseGeminiJsonResponse(rawText: string) {
  const attempts = new Set<string>();
  const cleaned = String(rawText || "").trim();
  if (!cleaned) {
    throw new Error("Gemini returned an empty response.");
  }

  const queueAttempt = (value: string | null | undefined) => {
    const trimmed = String(value || "").trim();
    if (trimmed) attempts.add(trimmed);
  };

  queueAttempt(cleaned);
  queueAttempt(cleaned.replace(/^```json\s*/i, "").replace(/\s*```$/i, "").trim());

  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    queueAttempt(cleaned.slice(firstBrace, lastBrace + 1));
  }

  for (const attempt of attempts) {
    const normalized = attempt.replace(/,\s*([}\]])/g, "$1");
    try {
      return JSON.parse(normalized);
    } catch {
      // Try the next recovery candidate.
    }
  }

  throw new Error("Invalid JSON response from Gemini API");
}

function mergeScriptScenes(existingScenes: any[], incomingScenes: any[]) {
  const merged = new Map<string, any>();

  for (const scene of existingScenes || []) {
    const key = normalizeSceneNumberToken(scene?.scene_number || scene?.sceneNumber);
    if (key) merged.set(key, scene);
  }

  for (const scene of incomingScenes || []) {
    const key = normalizeSceneNumberToken(scene?.scene_number || scene?.sceneNumber);
    if (!key) continue;
    const previous = merged.get(key) || {};
    merged.set(key, {
      ...previous,
      ...scene,
      heading: scene?.heading || previous?.heading || null,
      script_text: scene?.script_text || previous?.script_text || null,
      description: scene?.description || previous?.description || null,
      characters: Array.isArray(scene?.characters) && scene.characters.length ? scene.characters : (previous?.characters || []),
      script_page_range: scene?.script_page_range || previous?.script_page_range || null,
    });
  }

  return Array.from(merged.values()).sort((a, b) => {
    const aValue = normalizeSceneNumberToken(a?.scene_number || a?.sceneNumber);
    const bValue = normalizeSceneNumberToken(b?.scene_number || b?.sceneNumber);
    return aValue.localeCompare(bValue, undefined, { numeric: true });
  });
}

function getExpectedScriptSceneNumbers(scriptData: any) {
  const targetSceneNumbers = new Set<string>();
  const sceneCount = Number(scriptData?.scene_count || 0);

  if (sceneCount > 0 && sceneCount <= 200) {
    for (let number = 1; number <= sceneCount; number += 1) {
      targetSceneNumbers.add(String(number));
    }
  }

  for (const entry of scriptData?.characters_by_scene || []) {
    const token = normalizeSceneNumberToken(parseIndexedSceneNumber(entry));
    if (token) targetSceneNumbers.add(token);
  }

  for (const scene of scriptData?.scenes || []) {
    const token = normalizeSceneNumberToken(scene?.scene_number || scene?.sceneNumber);
    if (token) targetSceneNumbers.add(token);
  }

  return Array.from(targetSceneNumbers).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

function hydrateScriptScenes(scriptData: any) {
  const existingScenes = Array.isArray(scriptData?.scenes) ? scriptData.scenes : [];
  const merged = new Map<string, any>();

  for (const scene of existingScenes) {
    const key = normalizeSceneNumberToken(scene?.scene_number || scene?.sceneNumber);
    if (!key) continue;
    merged.set(key, { ...scene, scene_number: scene?.scene_number || scene?.sceneNumber || key });
  }

  const expectedSceneNumbers = getExpectedScriptSceneNumbers(scriptData);
  const sceneHeadings = Array.isArray(scriptData?.scene_headings) ? scriptData.scene_headings : [];
  const charactersByScene = Array.isArray(scriptData?.characters_by_scene) ? scriptData.characters_by_scene : [];

  expectedSceneNumbers.forEach((sceneNumber, index) => {
    if (!merged.has(sceneNumber)) {
      merged.set(sceneNumber, {
        scene_number: sceneNumber,
        heading: sceneHeadings[index] || null,
        description: null,
        script_text: null,
        script_page_range: null,
        characters: [],
      });
    }
  });

  for (const entry of charactersByScene) {
    const parsedNumber = normalizeSceneNumberToken(parseIndexedSceneNumber(entry));
    if (!parsedNumber || !merged.has(parsedNumber)) continue;
    const current = merged.get(parsedNumber) || {};
    const [, rawCharacters = ""] = String(entry).split(":");
    const characters = rawCharacters
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!current.characters || current.characters.length === 0) {
      merged.set(parsedNumber, { ...current, characters });
    }
  }

  return Array.from(merged.values()).sort((a, b) => {
    const aValue = normalizeSceneNumberToken(a?.scene_number || a?.sceneNumber);
    const bValue = normalizeSceneNumberToken(b?.scene_number || b?.sceneNumber);
    return aValue.localeCompare(bValue, undefined, { numeric: true });
  });
}

function getMissingScriptSceneNumbers(scriptData: any) {
  const expectedSceneNumbers = getExpectedScriptSceneNumbers(scriptData);
  const indexedSceneNumbers = new Set<string>();

  for (const scene of scriptData?.scenes || []) {
    if (scene?.script_text || scene?.scriptText) {
      const token = normalizeSceneNumberToken(scene?.scene_number || scene?.sceneNumber);
      if (token) indexedSceneNumbers.add(token);
    }
  }

  return expectedSceneNumbers.filter((token) => !indexedSceneNumbers.has(token));
}

function mergeScriptData(previousScriptData: any, nextScriptData: any) {
  if (!previousScriptData && !nextScriptData) return null;

  const mergedBase = {
    ...(previousScriptData || {}),
    ...(nextScriptData || {}),
  };

  return {
    ...mergedBase,
    characters: Array.from(new Set([
      ...((previousScriptData?.characters as string[]) || []),
      ...((nextScriptData?.characters as string[]) || []),
    ].filter(Boolean))),
    locations: Array.from(new Set([
      ...((previousScriptData?.locations as string[]) || []),
      ...((nextScriptData?.locations as string[]) || []),
    ].filter(Boolean))),
    scene_headings: Array.from(new Set([
      ...((previousScriptData?.scene_headings as string[]) || []),
      ...((nextScriptData?.scene_headings as string[]) || []),
    ].filter(Boolean))),
    characters_by_scene: Array.from(new Set([
      ...((previousScriptData?.characters_by_scene as string[]) || []),
      ...((nextScriptData?.characters_by_scene as string[]) || []),
    ].filter(Boolean))),
    scene_analysis: Array.from(new Set([
      ...((previousScriptData?.scene_analysis as string[]) || []),
      ...((nextScriptData?.scene_analysis as string[]) || []),
    ].filter(Boolean))),
    scenes: mergeScriptScenes(
      hydrateScriptScenes(previousScriptData || {}),
      hydrateScriptScenes(nextScriptData || {})
    ),
  };
}

function sanitizeScriptScalar(value: unknown) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return null;
  return raw;
}

function looksLikeRunawayScriptField(value: string) {
  if (value.length > 400) return true;
  const uppercase = value.toUpperCase();
  return (
    uppercase.includes("CUT TO:") ||
    uppercase.includes("INT.") ||
    uppercase.includes("EXT.") ||
    uppercase.includes("DAY ") ||
    uppercase.includes("NIGHT") ||
    uppercase.includes("END OF PDF")
  );
}

function sanitizeScriptData(scriptData: any) {
  if (!scriptData || typeof scriptData !== "object") return scriptData;

  const writer = sanitizeScriptScalar(scriptData.writer);
  const episodeTitle = sanitizeScriptScalar(scriptData.episode_title);
  const scriptDraft = sanitizeScriptScalar(scriptData.script_draft);

  return {
    ...scriptData,
    writer: writer && !looksLikeRunawayScriptField(writer) ? writer : null,
    episode_title: episodeTitle && !looksLikeRunawayScriptField(episodeTitle) ? episodeTitle : null,
    script_draft: scriptDraft && !looksLikeRunawayScriptField(scriptDraft) ? scriptDraft : null,
  };
}

const SHOT_FIELDS = [
  { id: "thumbnail", label: "Thumbnail" },
  { id: "status", label: "Status" },
  { id: "shotCode", label: "Shot Code", required: true },
  { id: "description", label: "Task Description" },
  { id: "sceneDescription", label: "Scene Description" },
  { id: "scopeOfWork", label: "Scope of Work (Client)" },
  { id: "frames", label: "Working Duration" },
  { id: "quantity", label: "Quantity" },
  { id: "legacy_price", label: "Legacy Price (£)" },
  { id: "assumptions", label: "Assumptions" },
  { id: "internalNotes", label: "Bid Notes (Internal)" },
  { id: "task_concept", label: "CPT (Days)" },
  { id: "task_dmp", label: "DMP (Days)" },
  { id: "task_comp", label: "COMP (Days)" },
  { id: "task_roto", label: "ROTO (Days)" },
  { id: "task_prep", label: "PREP (Days)" },
  { id: "task_gfx", label: "GRA (GFX) (Days)" },
  { id: "task_cameraTR", label: "Cam mm (Days)" },
  { id: "task_objectTR", label: "Object MM (Days)" },
  { id: "task_rANIM", label: "RotoAnim (Days)" },
  { id: "task_layout", label: "Layout (Days)" },
  { id: "task_anim", label: "Anim (Days)" },
  { id: "task_light", label: "Light (Days)" },
  { id: "task_fx", label: "FX (Days)" },
  { id: "project_onsetSupervision", label: "ONSET VFX SUPERVISION" },
];

const ASSET_FIELDS = [
  { id: "thumbnail", label: "Thumbnail" },
  { id: "status", label: "Status" },
  { id: "assetCode", label: "Asset Name", required: true },
  { id: "type", label: "Type" },
  { id: "description", label: "Task Description" },
  { id: "scopeOfWork", label: "Scope of Work (Client)" },
  { id: "quantity", label: "Quantity" },
  { id: "legacy_price", label: "Legacy Price (£)" },
  { id: "assumptions", label: "Assumptions" },
  { id: "internalNotes", label: "Bid Notes (Internal)" },
  { id: "task_concept", label: "CPT (Days)" },
  { id: "task_dmp", label: "DMP (Days)" },
  { id: "task_model", label: "Model (Days)" },
  { id: "task_rig", label: "Rig (Days)" },
  { id: "task_retop", label: "Retop (Days)" },
  { id: "task_cloth", label: "Cloth (Days)" },
  { id: "task_fur", label: "Fur (Days)" },
  { id: "task_groom", label: "Groom (Days)" },
  { id: "task_texture", label: "Texture (Days)" },
  { id: "task_anim", label: "Anim (Days)" },
  { id: "task_fx", label: "FX (Days)" },
  { id: "task_lookdev", label: "L/dev (Days)" },
  { id: "task_photoscan", label: "P/scan (Days)" },
  { id: "project_onsetSupervision", label: "ONSET VFX SUPERVISION" },
];

const ONSET_FIELDS = [
  { id: "project_onsetSupervision", label: "Onset Supervision (Days)" },
  { id: "rate_onsetSupervision", label: "Onset Supervision Rate (£)" },
];

const TOP_SHEET_FIELDS = [
  { id: "ts_quoteName", label: "Quote Name" },
  { id: "ts_quoteDate", label: "Quote Date" },
  { id: "ts_version", label: "Version" },
  { id: "ts_cost_shotTotal", label: "Shot Total" },
  { id: "ts_cost_assetTotal", label: "Asset Total" },
  { id: "ts_cost_onsetTotal", label: "Onset Total" },
  { id: "ts_cost_grandTotal", label: "Grand Total" },
  { id: "ts_cost_shotCount", label: "Shot Count" },
  { id: "ts_cost_assetCount", label: "Asset Count" },
  { id: "ts_cost_currency", label: "Currency" },
  { id: "ts_summary", label: "Summary" },
  { id: "ts_approach", label: "Approach" },
  { id: "ts_assumptions", label: "Assumptions" },
  { id: "ts_clarifications", label: "Clarifications" },
  { id: "ts_exclusions", label: "Exclusions" },
  { id: "ts_estimateAssumptions", label: "Estimate Assumptions" },
  { id: "ts_termsAndConditions", label: "Terms and Conditions" },
];

const SHOT_RATE_FIELDS = [
  { id: "shot_rate_supervisor", label: "Shot Rate: Supervisor" },
  { id: "shot_rate_concept", label: "Shot Rate: Concept" },
  { id: "shot_rate_dmp", label: "Shot Rate: DMP" },
  { id: "shot_rate_comp", label: "Shot Rate: Comp" },
  { id: "shot_rate_roto", label: "Shot Rate: Roto" },
  { id: "shot_rate_prep", label: "Shot Rate: Prep" },
  { id: "shot_rate_gfx", label: "Shot Rate: Gra" },
  { id: "shot_rate_cameraTR", label: "Shot Rate: CameraTR" },
  { id: "shot_rate_objectTR", label: "Shot Rate: ObjectTR" },
  { id: "shot_rate_rANIM", label: "Shot Rate: rANIM" },
  { id: "shot_rate_layout", label: "Shot Rate: Layout" },
  { id: "shot_rate_anim", label: "Shot Rate: Anim" },
  { id: "shot_rate_light", label: "Shot Rate: Light" },
  { id: "shot_rate_fx", label: "Shot Rate: FX" },
  { id: "shot_rate_onsetSupervision", label: "Shot Rate: Onset" },
];

const ASSET_RATE_FIELDS = [
  { id: "asset_rate_supervisor", label: "Asset Rate: Supervisor" },
  { id: "asset_rate_concept", label: "Asset Rate: Concept" },
  { id: "asset_rate_dmp", label: "Asset Rate: DMP" },
  { id: "asset_rate_model", label: "Asset Rate: Model" },
  { id: "asset_rate_rig", label: "Asset Rate: Rig" },
  { id: "asset_rate_retop", label: "Asset Rate: Retop" },
  { id: "asset_rate_cloth", label: "Asset Rate: Cloth" },
  { id: "asset_rate_fur", label: "Asset Rate: Fur" },
  { id: "asset_rate_groom", label: "Asset Rate: Groom" },
  { id: "asset_rate_texture", label: "Asset Rate: Texture" },
  { id: "asset_rate_anim", label: "Asset Rate: Anim" },
  { id: "asset_rate_fx", label: "Asset Rate: FX" },
  { id: "asset_rate_lookdev", label: "Asset Rate: Lookdev" },
  { id: "asset_rate_photoscan", label: "Asset Rate: Photoscan" },
];

const KEY_PAGE_FIELDS = [...SHOT_RATE_FIELDS, ...ASSET_RATE_FIELDS];

const cropAndResizeImage = (
  dataUrl: string,
  targetWidth: number,
  targetHeight: number,
  quality: number = 0.85
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Calculate cropping to maintain aspect ratio (cover)
        const imgAspectRatio = img.width / img.height;
        const targetAspectRatio = targetWidth / targetHeight;
        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgAspectRatio > targetAspectRatio) {
          // Image is wider than target
          drawHeight = targetHeight;
          drawWidth = img.width * (targetHeight / img.height);
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          // Image is taller than target
          drawWidth = targetWidth;
          drawHeight = img.height * (targetWidth / img.width);
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        resolve(canvas.toDataURL("image/jpeg", quality));
      } else {
        reject(new Error("Could not get canvas context"));
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
};

const IMPORT_THUMBNAIL_RESIZE_THRESHOLD = 24;
const IMPORT_THUMBNAIL_PROCESSING_MAX_ITEMS = 60;
const IMPORT_DETAILED_REPORT_MAX_ITEMS = 80;
const SINGLE_SHEET_SHOT_IMPORT_BATCH_SIZE = 25;

const prepareImportThumbnail = async (
  dataUrl: string,
  shouldResize: boolean
): Promise<string> => {
  if (!shouldResize) return dataUrl;
  return cropAndResizeImage(dataUrl, 480, 270);
};

const prepareDeferredImportThumbnail = async (dataUrl: string): Promise<string> => {
  return cropAndResizeImage(dataUrl, 320, 180, 0.72);
};

const IMPORT_RESULT_STORAGE_MAX_BYTES = 400_000;
const IMPORT_RESULT_STORAGE_MAX_ROWS = 75;
type ImportDiagnosticsDoc = NonNullable<NonNullable<ImportProgressData["diagnostics"]>["docs"]>[number];

const createPersistableImportResult = (result: ImportProgressData): ImportProgressData => {
  const reduced: ImportProgressData = {
    ...result,
    comparison: result.comparison
      ? {
          ...result.comparison,
          episodes: result.comparison.episodes?.slice(0, IMPORT_RESULT_STORAGE_MAX_ROWS),
          shots: result.comparison.shots?.slice(0, IMPORT_RESULT_STORAGE_MAX_ROWS),
          assets: result.comparison.assets
            ? {
                ...result.comparison.assets,
                rows: result.comparison.assets.rows.slice(0, IMPORT_RESULT_STORAGE_MAX_ROWS),
              }
            : undefined,
        }
      : undefined,
  };

  const reducedJson = JSON.stringify(reduced);
  if (reducedJson.length <= IMPORT_RESULT_STORAGE_MAX_BYTES) return reduced;

  return {
    ...result,
    comparison: undefined,
    duplicateConflicts: undefined,
  };
};

const buildThumbnailOnlyImportedShot = (shotCode: string, thumbnail: string): Shot => ({
  id: uuidv4(),
  active: true,
  shotCode,
  status: "Bidding",
  thumbnail,
  description: "",
  sceneDescription: "",
  scopeOfWork: "",
  frames: 0,
  quantity: 1,
  assumptions: "",
  internalNotes: "",
  days: {
    supervisor: 0,
    concept: 0,
    dmp: 0,
    comp: 0,
    roto: 0,
    prep: 0,
    gfx: 0,
    cameraTR: 0,
    objectTR: 0,
    rANIM: 0,
    layout: 0,
    anim: 0,
    light: 0,
    fx: 0,
  },
});

const buildThumbnailOnlyImportedAsset = (assetCode: string, thumbnail: string): Asset => ({
  id: uuidv4(),
  active: true,
  assetCode,
  type: "",
  status: "Bidding",
  thumbnail,
  description: "",
  scopeOfWork: "",
  quantity: 1,
  assumptions: "",
  internalNotes: "",
  days: {
    supervisor: 0,
    concept: 0,
    dmp: 0,
    model: 0,
    rig: 0,
    retop: 0,
    cloth: 0,
    fur: 0,
    groom: 0,
    texture: 0,
    anim: 0,
    fx: 0,
    lookdev: 0,
    photoscan: 0,
  },
});

const normalizeOnsetDays = (value: number) => Math.round(value * 10) / 10;

// Helper to convert column index to letter (0 -> A, 25 -> Z, 26 -> AA)
function getColumnLetter(index: number): string {
  let letter = '';
  let temp = index;
  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
}

const parseSheetData = async (
  doc: ProjectDocument,
  userUid: string | undefined,
  targetType?: string,
  options?: { includeImages?: boolean }
): Promise<{ rows: any[], allRows: any[][], isTransposed: boolean, images: any[], displayHeaders: string[], mappingHeaders?: string[] } | null> => {
  try {
    let dataToParse = doc.content;
    const shouldIncludeImages = options?.includeImages !== false;
    if (!dataToParse && doc.driveFileId) {
      const url = doc.sheetId !== undefined 
        ? `/api/drive/download/${doc.driveFileId}?gid=${doc.sheetId}&includeImages=${shouldIncludeImages ? "true" : "false"}` 
        : `/api/drive/download/${doc.driveFileId}?includeImages=${shouldIncludeImages ? "true" : "false"}`;
        
      const res = await fetch(url, {
        headers: { "x-user-uid": userUid || "" }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to download file");
      dataToParse = data.content;
    }

    if (!dataToParse) throw new Error("No content available");

    let isJson = false;
    let jsonData: any = null;

    if (typeof dataToParse === 'string') {
      const trimmed = dataToParse.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        isJson = true;
      } else if (dataToParse.startsWith('data:application/json;base64,')) {
        isJson = true;
      }
      
      if (dataToParse.startsWith('data:')) {
        try {
          dataToParse = atob(dataToParse.split(',')[1]);
          const decodedTrimmed = dataToParse.trim();
          if (decodedTrimmed.startsWith('{') || decodedTrimmed.startsWith('[')) {
            isJson = true;
          }
        } catch (e) {
          console.error("Failed to decode data URL", e);
        }
      }
    }

    if (isJson) {
      jsonData = JSON.parse(dataToParse);
    }

    return new Promise<{
      rows: any[];
      allRows: any[][];
      isTransposed: boolean;
      images: any[];
      displayHeaders: string[];
      mappingHeaders?: string[];
    } | null>((resolve, reject) => {
      const processData = (data: any) => {
        if (!data) return resolve(null);
        
        let isTransposed = false;
        let displayHeaders: string[] = [];
        let mappingHeaders: string[] = [];
        let rows: any[] = [];
        
        // Ensure data is an array of arrays
        let activeData: any[][] = [];
        if (Array.isArray(data)) {
          if (data.length > 0 && !Array.isArray(data[0]) && typeof data[0] === 'object') {
            // Handle array of objects
            const headers = Object.keys(data[0]);
            activeData = [headers, ...data.map(obj => headers.map(h => obj[h]))];
          } else {
            activeData = data.filter(row => Array.isArray(row));
          }
        } else if (typeof data === 'object' && data !== null) {
          // Handle single object or object with data property
          if (Array.isArray(data.rows)) {
            // Check if rows are objects
            if (data.rows.length > 0 && !Array.isArray(data.rows[0]) && typeof data.rows[0] === 'object') {
              const headers = Object.keys(data.rows[0]);
              activeData = [headers, ...data.rows.map((obj: any) => headers.map(h => obj[h]))];
            } else {
              return processData(data.rows);
            }
          } else if (Array.isArray(data.data)) {
            return processData(data.data);
          } else {
            const headers = Object.keys(data);
            activeData = [headers, headers.map(h => data[h])];
          }
        }

        if (activeData.length === 0) {
          return resolve(null);
        }

        if (targetType !== 'key' && activeData.length > 0 && activeData[0].length > 20 && activeData.length < 20) {
          isTransposed = true;
          activeData = activeData[0].map((_, colIndex) => activeData.map(row => row[colIndex]));
        }

        if (targetType === 'key') {
          const customHeaders: string[] = [];
          const customRow: any = { __nativeRow: 1, __nativeRowIndex: 0 };
          const seen = new Set<string>();

          const addRates = (type: 'shots' | 'assets', prefix: string) => {
            let targetLabelRowIndex = -1;
            let targetValueRowIndex = -1;

            // Define keywords to identify the correct label row
            const shotKeywords = ['comp', 'roto', 'prep', 'cameratr', 'objecttr'];
            const assetKeywords = ['model', 'rig', 'retop', 'cloth', 'fur', 'groom', 'texture', 'lookdev', 'photoscan'];
            const keywords = type === 'shots' ? shotKeywords : assetKeywords;

            // Search for the label row
            for (let i = 0; i < activeData.length; i++) {
              const row = activeData[i];
              if (!row) continue;
              
              let matchCount = 0;
              for (let j = 0; j < row.length; j++) {
                const cellStr = String(row[j] || '').toLowerCase().replace(/[_\s-]/g, '');
                if (keywords.some(kw => cellStr.includes(kw))) {
                  matchCount++;
                }
              }

              // If we find at least 2 matching keywords, we assume this is the label row
              if (matchCount >= 2) {
                targetLabelRowIndex = i;
                // The value row is usually the next row that has numbers
                for (let k = i + 1; k < Math.min(i + 4, activeData.length); k++) {
                  const nextRow = activeData[k];
                  if (nextRow && nextRow.some(cell => !isNaN(parseFloat(String(cell))))) {
                    targetValueRowIndex = k;
                    break;
                  }
                }
                break;
              }
            }

            if (targetLabelRowIndex === -1 || targetValueRowIndex === -1) return;

            const labelRow = activeData[targetLabelRowIndex];
            const valueRow = activeData[targetValueRowIndex];
            if (!labelRow || !valueRow) return;

            const startCol = 6; // Start at Column G (1 column safety buffer)
            for (let i = startCol; i < labelRow.length; i++) {
              const label = labelRow[i];
              if (label && String(label).trim()) {
                const labelStr = String(label).trim();
                const lowerLabelStr = labelStr.toLowerCase();
                if (lowerLabelStr.includes('task') || lowerLabelStr.includes('rate') || lowerLabelStr.includes('export') || lowerLabelStr.includes('episode')) continue;
                
                let base = `${prefix}${labelStr}`;
                let unique = base;
                let counter = 1;
                while (seen.has(unique)) {
                  unique = `${base} (${counter++})`;
                }
                seen.add(unique);
                customHeaders.push(unique);
                customRow[unique] = valueRow[i];
              }
            }
          };

          addRates('shots', 'Shot Rate: ');
          addRates('assets', 'Asset Rate: ');

          return resolve({
            rows: [customRow],
            allRows: activeData,
            isTransposed: false,
            images: shouldIncludeImages ? (jsonData?.images || doc.images || []) : [],
            displayHeaders: customHeaders,
            mappingHeaders: customHeaders
          });
        }

        let headerRowIndex = activeData.findIndex(row => 
          Array.isArray(row) && row.some(cell => cell && typeof cell === 'string' && 
            (cell.toLowerCase().includes('shot') || 
             cell.toLowerCase().includes('asset') || 
             cell.toLowerCase().includes('task') || 
             cell.toLowerCase().includes('name') ||
             cell.toLowerCase().includes('description') ||
             cell.toLowerCase().includes('code')))
        );

        const actualHeaderIndex = headerRowIndex >= 0 ? headerRowIndex : 0;
        
        let finalHeaderIndex = actualHeaderIndex;
        if (actualHeaderIndex > 0) {
          const prevRow = activeData[actualHeaderIndex - 1];
          if (Array.isArray(prevRow)) {
            const stringCells = prevRow.filter(cell => cell && typeof cell === 'string' && cell.trim().length > 0);
            // If the row above has multiple text cells, it's very likely a super-header (e.g. Task names)
            if (stringCells.length > 1) {
              finalHeaderIndex = actualHeaderIndex - 1;
            }
          }
        }
        
        if (finalHeaderIndex < activeData.length - 1) {
          const mainHeaders = activeData[finalHeaderIndex].map(h => h ? String(h).trim() : '');
          const subHeaders = activeData[finalHeaderIndex + 1]?.map(h => h ? String(h).trim() : '') || [];
          
          const seen = new Set<string>();
          let currentMainHeader = '';
          displayHeaders = mainHeaders.map((h, i) => {
            if (h) currentMainHeader = h;
            const sub = subHeaders[i] || '';
            let base = '';
            
            const subLower = sub.toLowerCase();
            if (subLower === 'bid' && currentMainHeader) {
              // User specifically asked to ignore 'Bid' on second row and use first row
              base = currentMainHeader;
            } else if (sub && subLower !== 'bid') {
              // Use the second row name if it's not 'Bid' (e.g., 'Shot Code', 'Thumbnail')
              base = sub;
            } else {
              // Fallback to first row or generated letter
              base = h || currentMainHeader || `Column ${getColumnLetter(i)}`;
            }
            
            let unique = base;
            let counter = 1;
            while (seen.has(unique)) {
              unique = `${base} (${counter++})`;
            }
            seen.add(unique);
            return unique;
          });
          
          mappingHeaders = [...displayHeaders];
          
          const processedRows = activeData.slice(finalHeaderIndex + 2).map((row, rowIndex) => {
            const rowObj: any = { 
              __nativeRow: finalHeaderIndex + 2 + rowIndex,
              __nativeRowIndex: rowIndex 
            };
            displayHeaders.forEach((h, i) => {
              rowObj[h] = row[i];
            });
            return rowObj;
          }).filter(row => Object.values(row).some(v => v !== undefined && v !== null && v !== '' && typeof v !== 'object'));
          
          rows = processedRows;
        } else {
          const seen = new Set<string>();
          displayHeaders = activeData[0].map((h, i) => {
            const base = h ? String(h).trim() : `Column ${getColumnLetter(i)}`;
            let unique = base;
            let counter = 1;
            while (seen.has(unique)) {
              unique = `${base} (${counter++})`;
            }
            seen.add(unique);
            return unique;
          });
          mappingHeaders = [...displayHeaders];
          
          const processedRows = activeData.slice(1).map((row, rowIndex) => {
            const rowObj: any = { 
              __nativeRow: 1 + rowIndex,
              __nativeRowIndex: rowIndex 
            };
            displayHeaders.forEach((h, i) => {
              rowObj[h] = row[i];
            });
            return rowObj;
          }).filter(row => Object.values(row).some(v => v !== undefined && v !== null && v !== '' && typeof v !== 'object'));
          
          rows = processedRows;
        }

        resolve({
          rows,
          allRows: activeData,
          isTransposed,
          images: shouldIncludeImages ? (jsonData?.images || doc.images || []) : [],
          displayHeaders,
          mappingHeaders
        });
      };

      if (isJson && jsonData) {
        processData(jsonData);
      } else {
        Papa.parse(dataToParse, {
          complete: (results) => processData(results.data as any[][]),
          error: (error) => {
            console.error("CSV Parse Error:", error);
            reject(error);
          }
        });
      }
    });
  } catch (error) {
    console.error("Error parsing sheet data:", error);
    return null;
  }
};

export function Documents({ 
  documents, 
  setDocuments, 
  globalSettings, 
  onImportShots, 
  onImportAssets, 
  onImportOnset,
  onImportTopSheet,
  onImportShootingSchedule,
  existingShots,
  existingAssets,
  existingScenes = [],
  existingSequences = [],
  onUpdateSetup,
  onUpdateRates,
  onUpdateAssetRates,
  rates,
  assetRates,
  setup, 
  projectId, 
  user, 
  handleSetupDrive, 
  handleLinkDrive, 
  handleDisconnectDrive, 
  isSettingUpDrive, 
  driveConnected, 
  isAdmin,
  onSaveDocument,
  onRemoveDocument,
  onPersistScriptStructure,
  onImportStateChange,
  uiVisible = true,
}: Props) {
  const [runningAnalysisIds, setRunningAnalysisIds] = useState<string[]>([]);
  const [analysisQueue, setAnalysisQueue] = useState<ProjectDocument[]>([]);
  const [scheduleImportDraft, setScheduleImportDraft] = useState<ShootingSchedule | null>(null);
  const isStartingQueue = useRef(false);
  const runningAnalysisIdsRef = useRef<string[]>([]);
  const analysisQueueRef = useRef<ProjectDocument[]>([]);
  const backgroundQueueStartedCountRef = useRef(0);
  const backgroundQueueCompletedCountRef = useRef(0);
  const viewMode: "list" = "list";
  const [documentSort, setDocumentSort] = useState<"imported_desc" | "name_asc">("imported_desc");
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const [importProgress, setImportProgress] = useState<ImportProgressData | null>(null);
  const importProgressRef = useRef<ImportProgressData | null>(null);
  const [importResult, setImportResult] = useState<ImportProgressData | null>(null);
  const [importModal, setImportModal] = useState<{
    isOpen: boolean;
    type: 'shots' | 'assets' | 'key' | 'top_sheet' | 'onset';
    headers: string[];
    rawHeaders?: string[];
    rows: any[];
    allRows: string[][];
    docId?: string;
    fileName: string;
    isTransposed?: boolean;
    images?: any[];
    allMappings?: Record<string, Record<string, string>>;
    subDocs?: ProjectDocument[];
    selectedSheets?: Record<string, string>; // type -> doc.id
    isLoadingSheet?: boolean;
    isAiScanning?: boolean;
  } | null>(null);
  const [activeRateTab, setActiveRateTab] = useState<'shots' | 'assets'>('shots');
  const [sheetSelection, setSheetSelection] = useState<{
    fileId: string;
    fileName: string;
    sheets: { id: number; name: string; index: number }[];
  } | null>(null);
  const [selectedSheetIds, setSelectedSheetIds] = useState<number[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [driveFiles, setDriveFiles] = useState<any[]>([]);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(setup.driveFolderId || null);
  const [folderPath, setFolderPath] = useState<{ id: string, name: string, color?: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sharedDrives, setSharedDrives] = useState<{ id: string, name: string }[]>([]);
  const [selectedDriveId, setSelectedDriveId] = useState<string>("");
  const [pullingFileId, setPullingFileId] = useState<string | null>(null);
  const [actionMenuFileId, setActionMenuFileId] = useState<string | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isUploadingToDrive, setIsUploadingToDrive] = useState(false);
  const [isDriveDropActive, setIsDriveDropActive] = useState(false);
  const [isCreatingManagementFolders, setIsCreatingManagementFolders] = useState(false);
  const [driveLoadError, setDriveLoadError] = useState<string | null>(null);
  const visibleDriveFiles = driveFiles.filter((file) => !String(file.name || "").startsWith("project-thumbnail-"));
  const driveUploadRef = useRef<HTMLInputElement>(null);
  const driveDragDepthRef = useRef(0);
  const latestDriveFetchIdRef = useRef(0);
  const importResultStorageKey = `documents-import-result:${projectId || "global"}`;
  const importProgressStorageKey = `documents-import-progress:${projectId || "global"}`;
  const isDocQueuedForAnalysis = (docId: string) => analysisQueue.some((queuedDoc) => queuedDoc.id === docId);
  const isDocRunningAnalysis = (docId: string) => runningAnalysisIds.includes(docId);
  const totalBackgroundAnalysisCount = analysisQueue.length + runningAnalysisIds.length;
  const driveLocationStorageKey = `documents-drive-location:${projectId || setup.driveFolderId || "global"}`;

  useEffect(() => {
    runningAnalysisIdsRef.current = runningAnalysisIds;
  }, [runningAnalysisIds]);

  useEffect(() => {
    analysisQueueRef.current = analysisQueue;
  }, [analysisQueue]);

  const resetImportFeedback = () => {
    importProgressRef.current = null;
    setImportProgress(null);
    setImportResult(null);
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(importResultStorageKey);
      window.sessionStorage.removeItem(importProgressStorageKey);
    }
  };

  const persistImportResult = (nextResult: ImportProgressData | null) => {
    setImportResult(nextResult);
    if (typeof window === "undefined") return;
    if (nextResult) {
      window.sessionStorage.setItem(
        importResultStorageKey,
        JSON.stringify(createPersistableImportResult(nextResult))
      );
    } else {
      window.sessionStorage.removeItem(importResultStorageKey);
    }
  };

  const persistImportProgress = (nextProgress: ImportProgressData | null) => {
    setImportProgress(nextProgress);
    if (typeof window === "undefined") return;
    if (nextProgress && nextProgress.isOpen && !nextProgress.isComplete) {
      window.sessionStorage.setItem(
        importProgressStorageKey,
        JSON.stringify(createPersistableImportResult(nextProgress))
      );
    } else {
      window.sessionStorage.removeItem(importProgressStorageKey);
    }
  };

  const persistDocumentMappings = async (
    finalAllMappings: Record<string, Record<string, string>>,
    targetDocs?: ProjectDocument[],
    docMetadataById?: Record<string, { mappingHeaders?: Record<string, string[]>; mappingIsTransposed?: Record<string, boolean> }>
  ) => {
    const docsToPersist = (targetDocs || []).map((doc) => ({
      ...doc,
      allMappings: finalAllMappings,
      mappingHeaders: {
        ...(doc.mappingHeaders || {}),
        ...(docMetadataById?.[doc.id]?.mappingHeaders || {}),
      },
      mappingIsTransposed: {
        ...(doc.mappingIsTransposed || {}),
        ...(docMetadataById?.[doc.id]?.mappingIsTransposed || {}),
      },
    }));

    if (docsToPersist.length === 0) return;

    setDocuments((prev) =>
      prev.map((doc) => {
        const updated = docsToPersist.find((candidate) => candidate.id === doc.id);
        return updated || doc;
      })
    );

    if (!onSaveDocument) return;

    await Promise.all(
      docsToPersist.map(async (doc) => {
        try {
          await onSaveDocument(doc);
        } catch (error) {
          console.error("Failed to persist document mappings", error);
        }
      })
    );
  };

  const showImportResult = (result: ImportProgressData) => {
    importProgressRef.current = null;
    persistImportProgress(null);
    const persistentResult = { ...result, isOpen: true, isComplete: true };
    persistImportResult(persistentResult);
  };

  const updateImportProgress = (
    next:
      | ImportProgressData
      | null
      | ((prev: ImportProgressData | null) => ImportProgressData | null)
  ) => {
    persistImportProgress(((prev: ImportProgressData | null) => {
      const resolved = typeof next === "function" ? (next as (prev: ImportProgressData | null) => ImportProgressData | null)(prev) : next;
      importProgressRef.current = resolved;
      return resolved;
    })(importProgressRef.current || null));
  };

  useEffect(() => {
    if (driveConnected && isAdmin) {
      fetchSharedDrives();
    }
  }, [driveConnected, isAdmin]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.sessionStorage.getItem(importResultStorageKey);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as ImportProgressData;
      if (parsed?.isOpen && parsed?.isComplete) {
        setImportResult(parsed);
      }
    } catch {
      window.sessionStorage.removeItem(importResultStorageKey);
    }
  }, [importResultStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.sessionStorage.getItem(importProgressStorageKey);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as ImportProgressData;
      if (parsed?.isOpen && !parsed?.isComplete) {
        importProgressRef.current = parsed;
        setImportProgress(parsed);
      }
    } catch {
      window.sessionStorage.removeItem(importProgressStorageKey);
    }
  }, [importProgressStorageKey]);

  const applyDuplicateImportResolutions = (resolutions: {
    shots: Array<{ id: string; newCode: string }>;
    assets: Array<{ id: string; newCode: string }>;
  }) => {
    if (!importResult?.duplicateConflicts) return;

    const shotResolutionMap = new Map(
      resolutions.shots
        .map((resolution) => [resolution.id, resolution.newCode.trim().toLowerCase()] as const)
        .filter(([, code]) => code)
    );
    const assetResolutionMap = new Map(
      resolutions.assets
        .map((resolution) => [resolution.id, resolution.newCode.trim()] as const)
        .filter(([, code]) => code)
    );

    const reservedShotCodes = new Set(existingShots.map((shot) => normalizeCodeKey(shot.shotCode)));
    const reservedAssetCodes = new Set(existingAssets.map((asset) => normalizeCodeKey(asset.assetCode)));

    const resolvedShots: Shot[] = [];
    const resolvedAssets: Asset[] = [];
    const remainingShots: DuplicateImportShotCandidate[] = [];
    const remainingAssets: DuplicateImportAssetCandidate[] = [];

    for (const candidate of importResult.duplicateConflicts.shots) {
      const nextCode = shotResolutionMap.get(candidate.id);
      if (!nextCode) {
        remainingShots.push(candidate);
        continue;
      }

      if (reservedShotCodes.has(normalizeCodeKey(nextCode))) {
        toast.error(`Shot code ${nextCode} is already in use.`);
        return;
      }

      reservedShotCodes.add(normalizeCodeKey(nextCode));
      resolvedShots.push({
        ...candidate.payload,
        id: uuidv4(),
        shotCode: nextCode,
      });
    }

    for (const candidate of importResult.duplicateConflicts.assets) {
      const nextCode = assetResolutionMap.get(candidate.id);
      if (!nextCode) {
        remainingAssets.push(candidate);
        continue;
      }

      if (reservedAssetCodes.has(normalizeCodeKey(nextCode))) {
        toast.error(`Asset code ${nextCode} is already in use.`);
        return;
      }

      reservedAssetCodes.add(normalizeCodeKey(nextCode));
      resolvedAssets.push({
        ...candidate.payload,
        id: uuidv4(),
        assetCode: nextCode,
      });
    }

    if (resolvedShots.length > 0) {
      onImportShots(resolvedShots, true);
    }
    if (resolvedAssets.length > 0) {
      onImportAssets(resolvedAssets, true);
    }

    const nextResult: ImportProgressData = {
      ...importResult,
      duplicateConflicts: {
        shots: remainingShots,
        assets: remainingAssets,
      },
    };

    persistImportResult(nextResult);
    toast.success(
      `Added ${resolvedShots.length} renamed shot${resolvedShots.length === 1 ? "" : "s"} and ${resolvedAssets.length} renamed asset${resolvedAssets.length === 1 ? "" : "s"} from the import report.`
    );
  };

  const fetchSharedDrives = async (retries = 3) => {
    try {
      const res = await fetch("/api/drive/shared-drives", {
        headers: { "x-user-uid": user?.uid || "" }
      });
      const data = await res.json();
      if (data.drives) {
        // Deduplicate shared drives by ID
        const uniqueDrives = Array.from(new Map(data.drives.map((d: any) => [d.id, d])).values()) as { id: string, name: string }[];
        setSharedDrives(uniqueDrives);
        if (uniqueDrives.length > 0 && !selectedDriveId) {
          setSelectedDriveId(uniqueDrives[0].id);
        }
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchSharedDrives(retries - 1), 2000);
      } else {
        console.error("Failed to fetch shared drives", error);
      }
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = window.sessionStorage.getItem(driveLocationStorageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as {
            currentFolderId: string | null;
            folderPath: { id: string; name: string; color?: string }[];
          };
          if (parsed.currentFolderId) {
            setCurrentFolderId(parsed.currentFolderId);
            setFolderPath(Array.isArray(parsed.folderPath) ? parsed.folderPath : []);
            return;
          }
        } catch {
          window.sessionStorage.removeItem(driveLocationStorageKey);
        }
      }
    }

    if (setup.driveFolderId) {
      setCurrentFolderId(setup.driveFolderId);
      setFolderPath([]);
    }
  }, [setup.driveFolderId, driveLocationStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      driveLocationStorageKey,
      JSON.stringify({
        currentFolderId,
        folderPath,
      })
    );
  }, [currentFolderId, folderPath, driveLocationStorageKey]);

  useEffect(() => {
    const handleClickOutside = () => setActionMenuFileId(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (currentFolderId) {
      fetchDriveFiles(currentFolderId);
    }
  }, [currentFolderId]);

  const fetchDriveFiles = async (folderId: string) => {
    if (!user?.uid) {
      setLoadingDrive(false);
      return;
    }

    const requestId = ++latestDriveFetchIdRef.current;
    setLoadingDrive(true);
    setDriveLoadError(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`/api/drive/list?folderId=${folderId}`, {
        headers: { "x-user-uid": user.uid },
        signal: controller.signal
      });
      const data = await res.json();
      if (latestDriveFetchIdRef.current !== requestId) return;
      if (res.ok && data.files) {
        // Deduplicate files by ID to prevent duplicate key warnings
        const uniqueFiles = Array.from(new Map(data.files.map((f: any) => [f.id, f])).values());
        setDriveFiles(uniqueFiles);
        setDriveLoadError(null);
      } else {
        const message = data.error || "Failed to fetch Drive files";
        setDriveFiles([]);
        setDriveLoadError(message);
        toast.error(message);
      }
    } catch (error) {
      if (latestDriveFetchIdRef.current !== requestId) return;
      console.error("Failed to fetch Drive files", error);
      setDriveFiles([]);
      const message = error instanceof DOMException && error.name === "AbortError"
        ? "Google Drive took too long to respond. Please try again."
        : "An error occurred while fetching Drive files";
      setDriveLoadError(message);
      toast.error(message);
    } finally {
      window.clearTimeout(timeout);
      if (latestDriveFetchIdRef.current === requestId) {
        setLoadingDrive(false);
      }
    }
  };

  const handleFolderClick = (folder: any) => {
    setFolderPath(prev => [...prev, { id: folder.id, name: folder.name, color: folder.folderColorRgb }]);
    setCurrentFolderId(folder.id);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setFolderPath([]);
      setCurrentFolderId(setup.driveFolderId || null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolderId(newPath[newPath.length - 1].id);
    }
  };

  const handlePullDriveFile = async (file: any, sheetId?: number, customName?: string, sheetName?: string) => {
    setPullingFileId(file.id);
    try {
      // If it's a Google Sheet and no sheetId is provided, fetch sheets first
      if (file.mimeType === 'application/vnd.google-apps.spreadsheet' && sheetId === undefined) {
        const res = await fetch(`/api/drive/sheets/${file.id}`, {
          headers: { "x-user-uid": user?.uid || "" }
        });
        const data = await res.json();
        if (data.success && data.sheets && data.sheets.length > 1) {
          setSheetSelection({ fileId: file.id, fileName: file.name, sheets: data.sheets });
          setSelectedSheetIds([]); // Reset selection when opening modal
          setPullingFileId(null);
          return;
        }
      }

      const resolvedSheetName =
        sheetName || (sheetId !== undefined ? (customName ? customName.split(' - ').pop() : `Sheet ${sheetId}`) : undefined);
      const normalizedSheetName = (resolvedSheetName || customName || file.name || "").toLowerCase();
      const isCreateShotsSheet =
        file.mimeType === 'application/vnd.google-apps.spreadsheet' &&
        sheetId !== undefined &&
        normalizedSheetName.includes('shot') &&
        projectId &&
        user?.uid;
      const isCreateAssetsSheet =
        file.mimeType === 'application/vnd.google-apps.spreadsheet' &&
        sheetId !== undefined &&
        normalizedSheetName.includes('asset') &&
        projectId &&
        user?.uid;

      if (isCreateShotsSheet) {
        const savedShotMappings = await loadMappingPreference('shots');
        const shotLearnedMappings = {
          ...(learnedMappings['shots'] || {}),
          ...(savedShotMappings || {}),
        };

        const res = await fetch(`/api/projects/${projectId}/pull-import-legacy-shots`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user?.uid || "",
          },
          body: JSON.stringify({
            fileId: file.id,
            sheetId,
            customName,
            sheetName: resolvedSheetName,
            merge: false,
            learnedMappings: shotLearnedMappings,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to pull and import Create SHOTS");

        if (data.document) {
          setDocuments(prev => {
            const existing = prev.filter(doc => doc.id !== data.document.id);
            return [data.document, ...existing];
          });
        }
        if (Array.isArray(data.shots)) {
          onImportShots(data.shots, false);
        }
        if (data.setup && Object.keys(data.setup).length > 0) {
          onUpdateSetup(data.setup);
        }
        if (data.rates && Object.keys(data.rates).length > 0) {
          onUpdateRates(data.rates);
        }

        toast.success(`Successfully pulled and imported ${resolvedSheetName || file.name}`);
        return;
      }

      if (isCreateAssetsSheet) {
        const savedAssetMappings = await loadMappingPreference('assets');
        const assetLearnedMappings = {
          ...(learnedMappings['assets'] || {}),
          ...(savedAssetMappings || {}),
        };

        const res = await fetch(`/api/projects/${projectId}/pull-import-legacy-assets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user?.uid || "",
          },
          body: JSON.stringify({
            fileId: file.id,
            sheetId,
            customName,
            sheetName: resolvedSheetName,
            merge: false,
            learnedMappings: assetLearnedMappings,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to pull and import Create ASSETS");

        if (data.document) {
          setDocuments(prev => {
            const existing = prev.filter(doc => doc.id !== data.document.id);
            return [data.document, ...existing];
          });
        }
        if (Array.isArray(data.assets)) {
          onImportAssets(data.assets, false);
        }
        if (data.setup && Object.keys(data.setup).length > 0) {
          onUpdateSetup(data.setup);
        }
        if (data.assetRates && Object.keys(data.assetRates).length > 0) {
          onUpdateAssetRates(data.assetRates);
        }

        toast.success(`Successfully pulled and imported ${resolvedSheetName || file.name}`);
        return;
      }

      // Create placeholder document immediately so user sees it
      const docId = `drive-${file.id}-${sheetId !== undefined ? sheetId : 'root'}-${Date.now()}`;
      const newDoc: ProjectDocument = {
        id: docId,
        name: customName || (sheetId !== undefined ? `${file.name} (${sheetId})` : file.name),
        type: file.mimeType === 'application/vnd.google-apps.spreadsheet' ? 'application/json' : file.mimeType,
        size: 0, 
        thumbnail: file.thumbnailLink,
        driveFileId: file.id,
        parentDriveFileId: file.mimeType === 'application/vnd.google-apps.spreadsheet' ? file.id : undefined,
        sheetId: sheetId,
        sheetName: resolvedSheetName,
        importedAt: new Date().toISOString()
      };

      if (onSaveDocument) {
        await onSaveDocument(newDoc);
      } else {
        setDocuments(prev => [newDoc, ...prev]);
      }

      const downloadUrl = sheetId !== undefined
        ? `/api/drive/download/${file.id}?gid=${sheetId}`
        : `/api/drive/download/${file.id}`;

      try {
        const res = await fetch(downloadUrl, {
          headers: { "x-user-uid": user?.uid || "" }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to cache file content");

        const cachedDoc: ProjectDocument = {
          ...newDoc,
          content: data.content,
          size: data.content ? data.content.length : newDoc.size
        };

        if (onSaveDocument) {
          await onSaveDocument(cachedDoc);
        } else {
          setDocuments(prev => prev.map(doc => doc.id === cachedDoc.id ? cachedDoc : doc));
        }
      } catch (cacheError) {
        console.warn("Failed to cache pulled Drive file content locally", cacheError);
      }

      toast.success(`Successfully pulled ${file.name} into project`);
      
      // Legacy quote sheets now import through deterministic mappings rather than auto-AI analysis.
      // Keep auto-analysis for unstructured docs, but leave sheet-based quote tabs as manual/opt-in.
      if (newDoc.type === 'text/plain' || 
          newDoc.type === 'application/pdf' || 
          newDoc.type === 'text/csv' || 
          newDoc.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          newDoc.type === 'application/vnd.ms-excel' ||
          newDoc.name.toLowerCase().endsWith('.csv') ||
          newDoc.name.toLowerCase().endsWith('.xlsx') ||
          newDoc.name.toLowerCase().endsWith('.xls')) {
        analyzeDocument(newDoc);
      }
      
    } catch (error) {
      console.error("Failed to pull file from Drive", error);
      toast.error("Failed to pull file from Google Drive");
    } finally {
      setPullingFileId(null);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !currentFolderId) return;

    setIsCreatingFolder(true);
    try {
      const res = await fetch("/api/drive/create-folder", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": user?.uid || ""
        },
        body: JSON.stringify({ name: newFolderName.trim(), parentId: currentFolderId })
      });
      if (!res.ok) throw new Error("Failed to create folder");
      toast.success(`Created folder: ${newFolderName.trim()}`);
      setNewFolderName("");
      setIsNewFolderModalOpen(false);
      fetchDriveFiles(currentFolderId);
    } catch (error) {
      console.error("Failed to create folder", error);
      toast.error("Failed to create folder in Google Drive");
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleCreateManagementFolders = async () => {
    if (!setup.driveFolderId) {
      toast.error("Link a project Google Drive first.");
      return;
    }

    setIsCreatingManagementFolders(true);
    try {
      const res = await fetch("/api/drive/create-management-folders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user?.uid || ""
        },
        body: JSON.stringify({ rootFolderId: setup.driveFolderId })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to create Management folders");
      }

      if (data.folderIds && typeof data.folderIds === "object") {
        onUpdateSetup(prev => ({
          ...prev,
          driveSubfolders: {
            ...(prev.driveSubfolders || {}),
            ...data.folderIds,
          },
        }));
      }

      if (currentFolderId === setup.driveFolderId) {
        await fetchDriveFiles(setup.driveFolderId);
      }

      toast.success("Management folders created.");
    } catch (error: any) {
      console.error("Failed to create management folders", error);
      toast.error(error?.message || "Failed to create Management folders");
    } finally {
      setIsCreatingManagementFolders(false);
    }
  };

  const uploadFileToDrive = async (file?: File | null) => {
    if (!file || !currentFolderId) return;

    setIsUploadingToDrive(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("parentId", currentFolderId);

    try {
      const res = await fetch("/api/drive/upload", {
        method: "POST",
        headers: { "x-user-uid": user?.uid || "" },
        body: formData
      });
      if (!res.ok) throw new Error("Failed to upload file");
      toast.success(`Uploaded ${file.name} to Google Drive`);
      fetchDriveFiles(currentFolderId);
    } catch (error) {
      console.error("Failed to upload file", error);
      toast.error("Failed to upload file to Google Drive");
    } finally {
      setIsUploadingToDrive(false);
      if (driveUploadRef.current) driveUploadRef.current.value = "";
    }
  };

  const handleUploadToDrive = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await uploadFileToDrive(e.target.files?.[0] || null);
  };

  const handleDriveDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!currentFolderId || isUploadingToDrive) return;
    event.preventDefault();
    event.stopPropagation();
    driveDragDepthRef.current += 1;
    setIsDriveDropActive(true);
  };

  const handleDriveDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!currentFolderId || isUploadingToDrive) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDriveDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!currentFolderId || isUploadingToDrive) return;
    event.preventDefault();
    event.stopPropagation();
    driveDragDepthRef.current = Math.max(0, driveDragDepthRef.current - 1);
    if (driveDragDepthRef.current === 0) {
      setIsDriveDropActive(false);
    }
  };

  const handleDriveDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!currentFolderId || isUploadingToDrive) return;
    event.preventDefault();
    event.stopPropagation();
    driveDragDepthRef.current = 0;
    setIsDriveDropActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await uploadFileToDrive(file);
  };

  const learnedMappings = setup.csvMappings || { shots: {}, assets: {}, onset: {}, top_sheet: {}, key: {} };

  const setLearnedMappings = (update: Record<string, Record<string, string>> | ((prev: Record<string, Record<string, string>>) => Record<string, Record<string, string>>)) => {
    const newMappings = typeof update === 'function' ? update(learnedMappings) : update;
    onUpdateSetup({ csvMappings: newMappings });
  };

  const saveMappingPreference = async (type: string, mappings: Record<string, string>) => {
    if (!user) return;
    try {
      const prefRef = doc(db, "mappingPreferences", `${user.uid}_${type}`);
      await setDoc(prefRef, {
        userId: user.uid,
        importType: type,
        mappings,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error("Failed to save mapping preference", error);
    }
  };

  const loadMappingPreference = async (type: string): Promise<Record<string, string> | null> => {
    if (!user) return null;
    try {
      const prefRef = doc(db, "mappingPreferences", `${user.uid}_${type}`);
      const snap = await getDoc(prefRef);
      if (snap.exists()) {
        return snap.data().mappings;
      }
    } catch (error) {
      console.error("Failed to load mapping preference", error);
    }
    return null;
  };

  const normalizeImportedTextEncoding = (value: string) =>
    value
      .replace(/â€¢/g, "•")
      .replace(/â€"/g, "—")
      .replace(/â€“/g, "–")
      .replace(/â€˜/g, "‘")
      .replace(/â€™/g, "’")
      .replace(/â€œ/g, "“")
      .replace(/â€/g, "”")
      .replace(/Â/g, "")
      .replace(/\u00a0/g, " ");

  const sanitizeText = (text: any) => {
    if (text === null || text === undefined) return "";
    return normalizeImportedTextEncoding(
      text.toString().replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    ).trim();
  };

  const parseNumber = (val: any): number => {
    if (val === null || val === undefined) return 0;
    // Remove currency symbols, commas, and other non-numeric chars except decimal point and minus sign
    const s = val.toString().replace(/[^-0-9.]/g, '');
    const num = parseFloat(s);
    return isNaN(num) ? 0 : num;
  };

  const extractVersionCode = (value?: string | null) => {
    const text = sanitizeText(value);
    if (!text) return null;

    const exactMatch = text.match(/^v?(\d+)$/i);
    if (exactMatch) {
      return `v${exactMatch[1].padStart(2, "0")}`;
    }

    const embeddedMatch = text.match(/(?:^|[^a-zA-Z0-9])v(\d+)(?:[^a-zA-Z0-9]|$)/i);
    if (embeddedMatch) {
      return `v${embeddedMatch[1].padStart(2, "0")}`;
    }

    return null;
  };

  const resolveImportedVersion = ({
    explicitVersion,
    quoteName,
    fileName,
  }: {
    explicitVersion?: string | null;
    quoteName?: string | null;
    fileName?: string | null;
  }) => {
    return (
      extractVersionCode(explicitVersion) ||
      extractVersionCode(quoteName) ||
      extractVersionCode(fileName) ||
      null
    );
  };

  const mergeImportedTopSheetReference = (
    currentTopSheet: TopSheet | undefined,
    partial: Partial<NonNullable<TopSheet["importedReference"]>>
  ): NonNullable<TopSheet["importedReference"]> => ({
    ...(currentTopSheet?.importedReference || {}),
    ...partial,
    costs: {
      ...(currentTopSheet?.importedReference?.costs || {}),
      ...(partial.costs || {})
    }
  });

  const normalizeLegacySheetText = (value: unknown) =>
    (value ?? "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[_\s-]/g, "")
      .replace(/[^a-z0-9£$€.,:/]/g, "");

  const isLoremIpsumText = (value: string) => {
    const normalized = value.toLowerCase();
    return normalized.includes("lorem ipsum") || normalized.includes("dolor sit amet");
  };

  const getMergedRowContent = (row: any[] | undefined, startCol: number, endCol: number) => {
    if (!Array.isArray(row)) return "";
    const values = row
      .slice(startCol, endCol + 1)
      .map(cell => sanitizeText(cell))
      .filter(Boolean);

    if (values.length === 0) return "";
    const uniqueValues = values.filter((value, index) => values.indexOf(value) === index);
    return uniqueValues.join("\n").trim();
  };

  const getFirstMergedRowValue = (row: any[] | undefined, startCol: number, endCol: number) => {
    if (!Array.isArray(row)) return "";
    const values = row
      .slice(startCol, endCol + 1)
      .map(cell => sanitizeText(cell))
      .filter(Boolean);

    return values[0] || "";
  };

  const extractCurrencyValue = (value: string) => {
    const match = value.match(/[£$€]\s*([0-9,]+(?:\.[0-9]+)?)/);
    return match ? parseNumber(match[0]) : undefined;
  };

  const parseLegacyCostBlock = (costBlockText: string) => {
    const parsedCosts: NonNullable<NonNullable<TopSheet["importedReference"]>["costs"]> = {};
    const lines = costBlockText
      .split(/\n+/)
      .map(line => line.trim())
      .filter(Boolean);

    for (const line of lines) {
      const normalized = normalizeLegacySheetText(line);
      const amount = extractCurrencyValue(line);
      if (amount === undefined) continue;

      if (normalized.startsWith("assets")) parsedCosts.assetTotal = amount;
      else if (normalized.startsWith("onset")) parsedCosts.onsetTotal = amount;
      else if (normalized.startsWith("total")) parsedCosts.grandTotal = amount;
    }

    return parsedCosts;
  };

  const buildLegacyCostBlock = (allRows: any[][]) => {
    const costRow = allRows[11];
    const totalRow = allRows[12];
    if (!Array.isArray(costRow) || normalizeLegacySheetText(costRow[2]) !== "cost") return "";

    const labels = getFirstMergedRowValue(costRow, 3, 3)
      .split(/\n+/)
      .map(line => line.trim())
      .filter(Boolean);
    const amounts = getFirstMergedRowValue(costRow, 4, 4)
      .split(/\n+/)
      .map(line => line.trim())
      .filter(Boolean);

    const costLines = labels.map((label, index) => {
      const amount = amounts[index];
      return amount ? `${label}: ${amount}` : label;
    });

    const totalLabel = getFirstMergedRowValue(totalRow, 3, 3);
    const totalAmount = getFirstMergedRowValue(totalRow, 4, 4);
    if (totalLabel && totalAmount) {
      costLines.push(`${totalLabel}: ${totalAmount}`);
    }

    return costLines.join("\n").trim();
  };

  const isUsefulTopSheetValue = (value: string, fieldLabel?: string) => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (isLoremIpsumText(trimmed)) return false;
    if (fieldLabel && normalizeLegacySheetText(trimmed) === normalizeLegacySheetText(fieldLabel)) return false;
    return true;
  };

  const extractLegacyTopSheetData = (
    allRows: any[][],
    sourceDocumentName: string,
    sourceSheetName?: string
  ): Partial<TopSheet> => {
    const extracted: Partial<TopSheet> = {};
    const importedReference: Partial<NonNullable<TopSheet["importedReference"]>> = {
      sourceDocumentName,
      ...(sourceSheetName ? { sourceSheetName } : {})
    };

    const labelMap: Record<string, keyof TopSheet> = {
      summary: "summary",
      approach: "approach",
      assumptions: "assumptions",
      clarifications: "clarifications",
      exclusions: "exclusions",
      quoteassumptions: "estimateAssumptions",
      estimateassumptions: "estimateAssumptions",
      termsandconditions: "termsAndConditions",
    };

    const getSheetRow = (sheetRowNumber: number) => allRows[sheetRowNumber - 1];
    const getExactContent = (sheetRowNumber: number) => getFirstMergedRowValue(getSheetRow(sheetRowNumber), 3, 7);
    const getExactLabel = (sheetRowNumber: number) => (getSheetRow(sheetRowNumber)?.[2] ?? "").toString().trim();
    const setNarrativeField = (sheetRowNumber: number, expectedLabel: string, field: keyof TopSheet) => {
      const label = getExactLabel(sheetRowNumber);
      if (normalizeLegacySheetText(label) !== normalizeLegacySheetText(expectedLabel)) return;
      const content = getExactContent(sheetRowNumber);
      if (!isUsefulTopSheetValue(content, expectedLabel)) return;
      extracted[field] = content as any;
      importedReference[field as keyof NonNullable<TopSheet["importedReference"]>] = content as any;
    };

    const topSummaryRows = [
      { row: 3, label: "Quote Name", apply: (content: string) => { importedReference.quoteName = content; } },
      { row: 4, label: "Quote Date", apply: (content: string) => { importedReference.quoteDate = content; } },
      { row: 5, label: "Shot Total", apply: (content: string) => { importedReference.costs = { ...(importedReference.costs || {}), shotTotal: parseNumber(content) }; } },
      { row: 6, label: "Asset Total", apply: (content: string) => { importedReference.costs = { ...(importedReference.costs || {}), assetTotal: parseNumber(content) }; } },
      { row: 7, label: "Shot Count", apply: (content: string) => { importedReference.costs = { ...(importedReference.costs || {}), shotCount: parseNumber(content) }; } },
      { row: 8, label: "Total", apply: (content: string) => { importedReference.costs = { ...(importedReference.costs || {}), grandTotal: parseNumber(content) }; } },
    ] as const;

    for (const item of topSummaryRows) {
      const label = getExactLabel(item.row);
      if (normalizeLegacySheetText(label) !== normalizeLegacySheetText(item.label)) continue;
      const content = getExactContent(item.row);
      if (!isUsefulTopSheetValue(content, item.label)) continue;
      item.apply(content);
    }

    const exactCostBlockText = buildLegacyCostBlock(allRows);
    if (exactCostBlockText) {
      importedReference.costBlockText = exactCostBlockText;
      importedReference.costs = {
        ...(importedReference.costs || {}),
        ...parseLegacyCostBlock(exactCostBlockText)
      };
    } else if (normalizeLegacySheetText(getExactLabel(12)) === "cost") {
      const costBlockText = getMergedRowContent(getSheetRow(12), 3, 4);
      if (costBlockText) {
        importedReference.costBlockText = costBlockText;
        importedReference.costs = {
          ...(importedReference.costs || {}),
          ...parseLegacyCostBlock(costBlockText)
        };
      }
    }

    setNarrativeField(15, "Summary", "summary");
    setNarrativeField(17, "Approach", "approach");
    setNarrativeField(19, "Assumptions", "assumptions");
    setNarrativeField(21, "Clarifications", "clarifications");
    setNarrativeField(23, "Terms and Conditions", "termsAndConditions");
    setNarrativeField(25, "Quote Assumptions", "estimateAssumptions");

    for (let rowIndex = 0; rowIndex < allRows.length; rowIndex++) {
      const row = allRows[rowIndex];
      const label = sanitizeText(row?.[2]);
      if (!label) continue;

      const normalizedLabel = normalizeLegacySheetText(label);
      const content = getFirstMergedRowValue(row, 3, 7);
      if (!content) continue;

      if (normalizedLabel === "cost") {
        if (importedReference.costBlockText) continue;
        importedReference.costBlockText = content;
        importedReference.costs = {
          ...(importedReference.costs || {}),
          ...parseLegacyCostBlock(content)
        };
        continue;
      }

      if (normalizedLabel === "quotename") {
        if (importedReference.quoteName) continue;
        importedReference.quoteName = content;
        continue;
      }
      if (normalizedLabel === "quotedate") {
        if (importedReference.quoteDate) continue;
        importedReference.quoteDate = content;
        continue;
      }
      if (normalizedLabel === "version") {
        if (importedReference.version) continue;
        importedReference.version = content;
        continue;
      }
      if (normalizedLabel === "shottotal") {
        if (importedReference.costs?.shotTotal) continue;
        importedReference.costs = { ...(importedReference.costs || {}), shotTotal: parseNumber(content) };
        continue;
      }
      if (normalizedLabel === "assettotal") {
        if (importedReference.costs?.assetTotal !== undefined) continue;
        importedReference.costs = { ...(importedReference.costs || {}), assetTotal: parseNumber(content) };
        continue;
      }
      if (normalizedLabel === "onsettotal") {
        if (importedReference.costs?.onsetTotal !== undefined) continue;
        importedReference.costs = { ...(importedReference.costs || {}), onsetTotal: parseNumber(content) };
        continue;
      }
      if (normalizedLabel === "shotcount") {
        if (importedReference.costs?.shotCount) continue;
        importedReference.costs = { ...(importedReference.costs || {}), shotCount: parseNumber(content) };
        continue;
      }
      if (normalizedLabel === "assetcount") {
        if (importedReference.costs?.assetCount) continue;
        importedReference.costs = { ...(importedReference.costs || {}), assetCount: parseNumber(content) };
        continue;
      }
      if (normalizedLabel === "total") {
        if (importedReference.costs?.grandTotal) continue;
        importedReference.costs = { ...(importedReference.costs || {}), grandTotal: parseNumber(content) };
        continue;
      }

      const mappedField = labelMap[normalizedLabel];
      if (!mappedField || !isUsefulTopSheetValue(content, label)) continue;
      if (extracted[mappedField]) continue;

      extracted[mappedField] = content as any;
      importedReference[mappedField as keyof NonNullable<TopSheet["importedReference"]>] = content as any;
    }

    return {
      ...extracted,
      importedReference
    };
  };

  const getLegacyTopSheetPreview = (
    allRows: any[][],
    sourceDocumentName: string,
    sourceSheetName?: string
  ) => {
    const extracted = extractLegacyTopSheetData(allRows, sourceDocumentName, sourceSheetName);
    const importedReference = extracted.importedReference || {};
    const getRow = (sheetRowNumber: number) => allRows[sheetRowNumber - 1];
    const getRowStatus = (sheetRowNumber: number, expectedLabel: string) => {
      const row = getRow(sheetRowNumber);
      const actualLabel = (row?.[2] ?? "").toString().trim();
      if (normalizeLegacySheetText(actualLabel) !== normalizeLegacySheetText(expectedLabel)) return "not_found" as const;
      const content = getFirstMergedRowValue(row, 3, 7);
      if (!content.trim()) return "empty" as const;
      if (!isUsefulTopSheetValue(content, expectedLabel)) return "ignored" as const;
      return "found" as const;
    };

    const resolveSectionStatus = (
      value: string,
      sheetRowNumber: number,
      expectedLabel: string
    ) => {
      if (value && value.toString().trim()) {
        return "found" as const;
      }
      return getRowStatus(sheetRowNumber, expectedLabel);
    };

    return {
      extracted,
      importedReference,
      sections: [
        {
          label: "Quote Name",
          value: importedReference.quoteName || "",
          status: resolveSectionStatus(importedReference.quoteName || "", 3, "Quote Name")
        },
        {
          label: "Quote Date",
          value: importedReference.quoteDate || "",
          status: resolveSectionStatus(importedReference.quoteDate || "", 4, "Quote Date")
        },
        {
          label: "Cost Block",
          value: importedReference.costBlockText || "",
          status: resolveSectionStatus(importedReference.costBlockText || "", 12, "Cost")
        },
        {
          label: "Summary",
          value: extracted.summary || "",
          status: resolveSectionStatus(extracted.summary || "", 15, "Summary")
        },
        {
          label: "Approach",
          value: extracted.approach || "",
          status: resolveSectionStatus(extracted.approach || "", 17, "Approach")
        },
        {
          label: "Assumptions",
          value: extracted.assumptions || "",
          status: resolveSectionStatus(extracted.assumptions || "", 19, "Assumptions")
        },
        {
          label: "Clarifications",
          value: extracted.clarifications || "",
          status: resolveSectionStatus(extracted.clarifications || "", 21, "Clarifications")
        },
        {
          label: "Terms and Conditions",
          value: extracted.termsAndConditions || "",
          status: resolveSectionStatus(extracted.termsAndConditions || "", 23, "Terms and Conditions")
        },
        {
          label: "Quote Assumptions",
          value: extracted.estimateAssumptions || "",
          status: resolveSectionStatus(extracted.estimateAssumptions || "", 25, "Quote Assumptions")
        },
      ]
    };
  };

  const extractLegacyOnsetSupervisionDays = (
    rows: any[],
    allRows: any[][],
    typeMappings: Record<string, string>
  ) => {
    const onsetTerms = new Set(["onsetsupervision", "onsetvfxsupervision", "onset"]);
    const matchesOnsetTerm = (value: any) => {
      const normalized = normalizeLegacySheetText(value);
      if (!normalized) return false;
      return onsetTerms.has(normalized);
    };
    const shotCodeHeader = Object.keys(typeMappings).find(h => typeMappings[h] === 'shotCode');
    const onsetDaysHeader = Object.keys(typeMappings).find(h => typeMappings[h] === 'project_onsetSupervision');
    const qtyHeader = Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity');
    const framesHeader = Object.keys(typeMappings).find(h => typeMappings[h] === 'frames');
    const isExplicitZeroLike = (value: any) => {
      const raw = String(value ?? '').trim();
      if (!raw) return false;
      const normalized = raw.replace(/,/g, '');
      const parsed = Number(normalized);
      return Number.isFinite(parsed) && parsed === 0;
    };
    const hasExplicitMappedZero = (row: Record<string, any>) => {
      return [onsetDaysHeader, qtyHeader, framesHeader].some((header) => {
        if (!header) return false;
        return isExplicitZeroLike(row[header]);
      });
    };
    const getExplicitOnsetRowValue = (row: Record<string, any>) => {
      const mappedOnsetValue = onsetDaysHeader ? parseNumber(row[onsetDaysHeader]) : 0;
      const quantityValue = qtyHeader ? parseNumber(row[qtyHeader]) : 0;
      const framesValue = framesHeader ? parseNumber(row[framesHeader]) : 0;

      if (mappedOnsetValue > 0) return mappedOnsetValue;
      if (quantityValue > 0) return quantityValue;
      if (framesValue > 0) return framesValue;
      return 0;
    };
    const findFirstNumericAfterMatch = (rowValues: any[], matchIndex: number) => {
      for (let col = Math.max(matchIndex + 1, 0); col < rowValues.length; col++) {
        const numericValue = parseNumber(rowValues[col]);
        if (numericValue > 0) return numericValue;
      }
      return 0;
    };

    if (shotCodeHeader) {
      const orderedActiveRows = rows.filter(row => {
        const code = (row[shotCodeHeader] ?? "").toString().trim();
        return code.length > 0;
      });

      const firstActiveRow = orderedActiveRows[0];
      if (firstActiveRow && matchesOnsetTerm(firstActiveRow[shotCodeHeader])) {
        return getExplicitOnsetRowValue(firstActiveRow);
      }

      const onsetRow = rows.find(row => {
        return matchesOnsetTerm(row[shotCodeHeader]);
      });
      if (onsetRow) {
        const explicitValue = getExplicitOnsetRowValue(onsetRow);
        if (explicitValue > 0) return explicitValue;

        // Only lock to zero when the mapped onset cells explicitly say zero.
        // If the mapped cells are blank, keep looking across the row and raw sheet
        // because some legacy quotes store the onset number in a neighboring column.
        if (hasExplicitMappedZero(onsetRow)) return 0;

        const rowEntries = Object.entries(onsetRow || {});
        const matchIndex = rowEntries.findIndex(([, value]) => matchesOnsetTerm(value));
        if (matchIndex !== -1) {
          const numericFromRow = findFirstNumericAfterMatch(rowEntries.map(([, value]) => value), matchIndex);
          if (numericFromRow > 0) return numericFromRow;
        }
      }
    }

    for (const rawRow of allRows) {
      const limitedCells = rawRow.slice(0, 6);
      const matchIndex = limitedCells.findIndex(cell => matchesOnsetTerm(cell));
      if (matchIndex === -1) continue;

      const adjacentQuantity = parseNumber(rawRow?.[matchIndex + 1]);
      if (adjacentQuantity > 0) return adjacentQuantity;

      const numericFromRow = findFirstNumericAfterMatch(rawRow, matchIndex);
      if (numericFromRow > 0) return numericFromRow;
    }

    return 0;
  };

  const shouldReplaceTopSheetField = (currentValue: string | undefined, extractedValue: string | undefined, fieldLabel: string) => {
    if (!extractedValue) return false;
    if (!currentValue) return true;
    const normalizedCurrent = normalizeLegacySheetText(currentValue);
    return normalizedCurrent === normalizeLegacySheetText(fieldLabel);
  };

  const sanitizeTopSheetMappedValue = (fieldId: string, value: string) => {
    const trimmed = sanitizeText(value);
    if (!trimmed) return "";

    const fieldLabels: Record<string, string> = {
      ts_quoteName: "Quote Name",
      ts_quoteDate: "Quote Date",
      ts_version: "Version",
      ts_summary: "Summary",
      ts_approach: "Approach",
      ts_assumptions: "Assumptions",
      ts_clarifications: "Clarifications",
      ts_exclusions: "Exclusions",
      ts_estimateAssumptions: "Quote Assumptions",
      ts_termsAndConditions: "Terms and Conditions",
    };

    const label = fieldLabels[fieldId];
    if (label && !isUsefulTopSheetValue(trimmed, label)) return "";
    if (fieldId.startsWith("ts_cost_") && parseNumber(trimmed) === 0 && !/[0-9]/.test(trimmed)) return "";
    return trimmed;
  };

  const calculateShotImportCost = (shot: Shot, rateCard: RateCard, currentSetup: ProjectSetup) => {
    const baseTaskCost =
      (shot.days.concept || 0) * (rateCard.concept || 0) +
      (shot.days.dmp || 0) * (rateCard.dmp || 0) +
      (shot.days.comp || 0) * (rateCard.comp || 0) +
      (shot.days.roto || 0) * (rateCard.roto || 0) +
      (shot.days.prep || 0) * (rateCard.prep || 0) +
      (shot.days.gfx || 0) * (rateCard.gfx || 0) +
      (shot.days.cameraTR || 0) * (rateCard.cameraTR || 0) +
      (shot.days.objectTR || 0) * (rateCard.objectTR || 0) +
      (shot.days.rANIM || 0) * (rateCard.rANIM || 0) +
      (shot.days.layout || 0) * (rateCard.layout || 0) +
      (shot.days.anim || 0) * (rateCard.anim || 0) +
      (shot.days.light || 0) * (rateCard.light || 0) +
      (shot.days.fx || 0) * (rateCard.fx || 0);

    const taskDays =
      (shot.days.concept || 0) +
      (shot.days.dmp || 0) +
      (shot.days.comp || 0) +
      (shot.days.roto || 0) +
      (shot.days.prep || 0) +
      (shot.days.gfx || 0) +
      (shot.days.cameraTR || 0) +
      (shot.days.objectTR || 0) +
      (shot.days.rANIM || 0) +
      (shot.days.layout || 0) +
      (shot.days.anim || 0) +
      (shot.days.light || 0) +
      (shot.days.fx || 0);

    const supDays = taskDays * ((currentSetup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (rateCard.supervisor || 0);
    return (baseTaskCost + supCost) * (shot.quantity || 1);
  };

  const calculateAssetImportCost = (asset: Asset, rateCard: AssetRateCard, currentSetup: ProjectSetup) => {
    const baseTaskCost =
      (asset.days.concept || 0) * (rateCard.concept || 0) +
      (asset.days.dmp || 0) * (rateCard.dmp || 0) +
      (asset.days.model || 0) * (rateCard.model || 0) +
      (asset.days.rig || 0) * (rateCard.rig || 0) +
      (asset.days.retop || 0) * (rateCard.retop || 0) +
      (asset.days.cloth || 0) * (rateCard.cloth || 0) +
      (asset.days.fur || 0) * (rateCard.fur || 0) +
      (asset.days.groom || 0) * (rateCard.groom || 0) +
      (asset.days.texture || 0) * (rateCard.texture || 0) +
      (asset.days.anim || 0) * (rateCard.anim || 0) +
      (asset.days.fx || 0) * (rateCard.fx || 0) +
      (asset.days.lookdev || 0) * (rateCard.lookdev || 0) +
      (asset.days.photoscan || 0) * (rateCard.photoscan || 0);

    const taskDays =
      (asset.days.concept || 0) +
      (asset.days.dmp || 0) +
      (asset.days.model || 0) +
      (asset.days.rig || 0) +
      (asset.days.retop || 0) +
      (asset.days.cloth || 0) +
      (asset.days.fur || 0) +
      (asset.days.groom || 0) +
      (asset.days.texture || 0) +
      (asset.days.anim || 0) +
      (asset.days.fx || 0) +
      (asset.days.lookdev || 0) +
      (asset.days.photoscan || 0);

    const supDays = taskDays * ((currentSetup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (rateCard.supervisor || 0);
    return (baseTaskCost + supCost) * (asset.quantity || 1);
  };

  const calculateAccuracyMetric = (imported: number | null, calculated: number | null) => {
    if (imported === null || imported === undefined || imported <= 0 || calculated === null || calculated === undefined) {
      return { accuracy: null, imported, calculated };
    }
    const variance = Math.abs(calculated - imported);
    const accuracy = Math.max(0, Math.round((1 - variance / imported) * 1000) / 10);
    return { accuracy, imported, calculated };
  };

  const normalizeCodeKey = (value: string) => value.trim().toLowerCase();

  const suggestDuplicateShotCode = (originalCode: string, reservedCodes: Set<string>) => {
    const parts = originalCode.split("_");
    const lastSegment = parts[parts.length - 1];
    if (!/^\d+$/.test(lastSegment)) {
      let suffix = 2;
      let candidate = `${originalCode}_${suffix.toString().padStart(2, "0")}`;
      while (reservedCodes.has(normalizeCodeKey(candidate))) {
        suffix += 1;
        candidate = `${originalCode}_${suffix.toString().padStart(2, "0")}`;
      }
      return candidate;
    }

    const width = lastSegment.length;
    const baseNumber = parseInt(lastSegment, 10);
    const preferredOffsets: number[] = [];
    for (let step = 1; step <= 20; step += 1) {
      preferredOffsets.push(step * 5, -step * 5);
    }

    for (const offset of preferredOffsets) {
      const candidateNumber = baseNumber + offset;
      if (candidateNumber <= 0) continue;
      const candidate = [...parts.slice(0, -1), candidateNumber.toString().padStart(width, "0")].join("_");
      if (!reservedCodes.has(normalizeCodeKey(candidate))) {
        return candidate;
      }
    }

    let fallbackNumber = baseNumber + 1;
    while (true) {
      const candidate = [...parts.slice(0, -1), fallbackNumber.toString().padStart(width, "0")].join("_");
      if (!reservedCodes.has(normalizeCodeKey(candidate))) {
        return candidate;
      }
      fallbackNumber += 1;
    }
  };

  const suggestDuplicateAssetCode = (originalCode: string, reservedCodes: Set<string>) => {
    let suffix = 2;
    let candidate = `${originalCode}-${suffix}`;
    while (reservedCodes.has(normalizeCodeKey(candidate))) {
      suffix += 1;
      candidate = `${originalCode}-${suffix}`;
    }
    return candidate;
  };

  const getEpisodeKeyFromShot = (shot: Shot, currentSetup?: ProjectSetup) => {
    const parserConfig = (currentSetup as ProjectSetup & { shotCodeParserConfig?: unknown } | undefined)
      ?.shotCodeParserConfig as Record<string, unknown> | null | undefined;
    const hierarchy = deriveHierarchyFromShotCode(
      shot.shotCode || "",
      parserConfig || null
    );

    if (hierarchy.seasonEpisodeCode) {
      return hierarchy.seasonEpisodeCode;
    }

    if (typeof hierarchy.episodeNumber === "number" && Number.isFinite(hierarchy.episodeNumber)) {
      return `ep ${String(hierarchy.episodeNumber).padStart(2, "0")}`;
    }

    return "unassigned";
  };

  const buildImportComparison = (
    shotsToImport: Shot[],
    assetsToImport: Asset[],
    effectiveRates: RateCard,
    effectiveAssetRates: AssetRateCard,
    effectiveSetup: ProjectSetup,
    importedOnsetTotal: number | null,
    calculatedOnsetTotal: number | null,
    importedOnsetDays: number | null,
    calculatedOnsetDays: number | null,
    includeDetailedRows = true
  ) => {
    const episodeMap = new Map<string, { imported: number; calculated: number; shotCount: number }>();
    for (const shot of shotsToImport) {
      const episode = getEpisodeKeyFromShot(shot, effectiveSetup);
      const current = episodeMap.get(episode) || { imported: 0, calculated: 0, shotCount: 0 };
      current.imported += shot.legacyPrice || 0;
      current.calculated += calculateShotImportCost(shot, effectiveRates, effectiveSetup);
      current.shotCount += 1;
      episodeMap.set(episode, current);
    }

    const episodes = Array.from(episodeMap.entries())
      .map(([episode, values]) => ({
        episode,
        imported: values.imported || null,
        calculated: values.calculated || null,
        variance: values.calculated - values.imported,
        shotCount: values.shotCount,
      }))
      .sort((a, b) => a.episode.localeCompare(b.episode));

    const hasEpisodeMismatch = episodes.some((episode) => Math.abs(episode.variance || 0) > 0.01);

    const shots = includeDetailedRows && hasEpisodeMismatch
      ? shotsToImport
          .map((shot) => {
            const imported = shot.legacyPrice || null;
            const calculated = calculateShotImportCost(shot, effectiveRates, effectiveSetup);
            const variance = imported === null ? null : calculated - imported;
            return {
              shotCode: shot.shotCode,
              episode: getEpisodeKeyFromShot(shot, effectiveSetup),
              imported,
              calculated,
              variance,
            };
          })
          .filter((shot) => Math.abs(shot.variance || 0) > 0.01)
          .sort((a, b) => Math.abs(b.variance || 0) - Math.abs(a.variance || 0))
      : [];

    const assetRows = includeDetailedRows
      ? assetsToImport
          .map((asset) => {
            const imported = asset.legacyPrice || null;
            const calculated = calculateAssetImportCost(asset, effectiveAssetRates, effectiveSetup);
            const variance = imported === null ? null : calculated - imported;
            return {
              code: asset.assetCode,
              imported,
              calculated,
              variance,
            };
          })
          .filter((asset) => Math.abs(asset.variance || 0) > 0.01)
          .sort((a, b) => Math.abs(b.variance || 0) - Math.abs(a.variance || 0))
      : [];

    return {
      onset: {
        importedDays: importedOnsetDays,
        calculatedDays: calculatedOnsetDays,
        importedTotal: importedOnsetTotal,
        calculatedTotal: calculatedOnsetTotal,
      },
      episodes,
      assets: {
        importedTotal: assetsToImport.reduce((acc, asset) => acc + (asset.legacyPrice || 0), 0) || null,
        calculatedTotal: assetsToImport.reduce((acc, asset) => acc + calculateAssetImportCost(asset, effectiveAssetRates, effectiveSetup), 0) || null,
        rows: assetRows,
      },
      shots,
    };
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(value);

  const extractLegacyLinePrice = (row: Record<string, any>, typeMappings: Record<string, string>) => {
    const mappedHeader = Object.keys(typeMappings).find(h => typeMappings[h] === 'legacy_price');
    const mappedValue = mappedHeader ? parseNumber(row[mappedHeader]) : 0;
    if (mappedValue > 0) return mappedValue;

    const ignoredMappedFields = new Set([
      'thumbnail',
      'status',
      'shotCode',
      'assetCode',
      'description',
      'sceneDescription',
      'scopeOfWork',
      'frames',
      'quantity',
      'assumptions',
      'internalNotes',
      'project_onsetSupervision',
      'type',
    ]);

    const strongSignals = ['price', 'cost', 'total', 'amount', 'bid', 'value', 'estimate'];
    let bestStrongCandidate: number | null = null;
    let bestFallbackCandidate: number | null = null;

    for (const [header, rawValue] of Object.entries(row)) {
      const mappedField = typeMappings[header];
      if (mappedField) {
        if (ignoredMappedFields.has(mappedField)) continue;
        if (mappedField.startsWith('task_')) continue;
        if (mappedField.startsWith('shot_rate_')) continue;
        if (mappedField.startsWith('asset_rate_')) continue;
      }

      const value = parseNumber(rawValue);
      if (!Number.isFinite(value) || value <= 50) continue;

      const normalizedHeader = header.toLowerCase().replace(/[_\s\n-]/g, '');
      const hasStrongSignal = strongSignals.some(signal => normalizedHeader.includes(signal));

      if (hasStrongSignal) {
        bestStrongCandidate = Math.max(bestStrongCandidate ?? 0, value);
      } else {
        bestFallbackCandidate = Math.max(bestFallbackCandidate ?? 0, value);
      }
    }

    return bestStrongCandidate ?? bestFallbackCandidate ?? 0;
  };

  // Helper to auto-map a header
  const autoMapHeader = (header: any, type: 'shots' | 'assets' | 'key' | 'top_sheet' | 'onset', index?: number) => {
    if (header === null || header === undefined) return null;
    const headerStr = header.toString();
    const h = headerStr.trim().toLowerCase().replace(/[_\s-]/g, '');
    let fields: { id: string, label: string }[] = [];
    if (type === 'shots') fields = SHOT_FIELDS;
    else if (type === 'assets') fields = ASSET_FIELDS;
    else if (type === 'onset') fields = ONSET_FIELDS;
    else if (type === 'top_sheet') fields = TOP_SHEET_FIELDS;
    else if (type === 'key') fields = KEY_PAGE_FIELDS;

    // Position-based hints
    if (type === 'shots' && index === 3) {
      // Column D is often Shot Code
      if (h === 'shot' || h === 'code' || h === 'id' || h === 'shotcode' || !h) return 'shotCode';
    }
    if (type === 'assets' && index === 0) {
      // Column A is often Asset Name
      if (h === 'asset' || h === 'name' || h === 'assetname' || !h) return 'assetCode';
    }

    const getMappedField = (fieldId: string) => {
      if (fields.some(f => f.id === fieldId)) return fieldId;
      const taskFieldId = `task_${fieldId}`;
      if (fields.some(f => f.id === taskFieldId)) return taskFieldId;
      const isAssetHeader = h.includes('asset');
      const isShotHeader = h.includes('shot');
      const shotRateFieldId = `shot_rate_${fieldId}`;
      const assetRateFieldId = `asset_rate_${fieldId}`;
      if (isAssetHeader && fields.some(f => f.id === assetRateFieldId)) return assetRateFieldId;
      if (isShotHeader && fields.some(f => f.id === shotRateFieldId)) return shotRateFieldId;
      if (fields.some(f => f.id === shotRateFieldId)) return shotRateFieldId;
      if (fields.some(f => f.id === assetRateFieldId)) return assetRateFieldId;
      const rateFieldId = `rate_${fieldId}`;
      if (fields.some(f => f.id === rateFieldId)) return rateFieldId;
      return null;
    };

    // Pass 1: Exact matches for aliases (guarantees obvious columns map perfectly)
    const cleanH = h.replace('shotrate', '').replace('assetrate', '').replace(/rate$/, '').replace(/£$/, '');
    for (const [fieldId, aliases] of Object.entries(TASK_ALIASES)) {
      if (aliases.some(alias => {
        const normalizedAlias = alias.toLowerCase().replace(/[_\s-]/g, '');
        return h === normalizedAlias || cleanH === normalizedAlias;
      })) {
        const mapped = getMappedField(fieldId);
        if (mapped) return mapped;
      }
    }

    // Pass 2: Safe partial matches (alias must be > 3 chars to prevent "id" matching "width")
    // Find the longest matching alias to prevent "ranim" from matching "anim"
    let bestMatch: { fieldId: string, length: number } | null = null;
    for (const [fieldId, aliases] of Object.entries(TASK_ALIASES)) {
      for (const alias of aliases) {
        const normalizedAlias = alias.toLowerCase().replace(/[_\s-]/g, '');
        if (normalizedAlias.length > 3 && (h.includes(normalizedAlias) || cleanH.includes(normalizedAlias))) {
          if (!bestMatch || normalizedAlias.length > bestMatch.length) {
            bestMatch = { fieldId, length: normalizedAlias.length };
          }
        }
      }
    }
    
    if (bestMatch) {
      const mapped = getMappedField(bestMatch.fieldId);
      if (mapped) return mapped;
    }

    // Pass 3: Direct label/id matching
    const match = fields.find(f => {
      const normalizedLabel = f.label.toLowerCase().replace(/[_\s-]/g, '').replace('(days)', '').replace('(£)', '').trim();
      const normalizedId = f.id.toLowerCase().replace(/[_\s-]/g, '').replace('task', '').replace('rate', '').replace('ts', '').replace('project', '').trim();
      
      // Exact match
      if (h === normalizedLabel || h === normalizedId) return true;
      
      // Safe partial match (only if the matching part is > 3 characters)
      return (normalizedLabel.length > 3 && h.includes(normalizedLabel)) || 
             (normalizedId.length > 3 && h.includes(normalizedId)) ||
             (h.length > 3 && normalizedLabel.includes(h)) || 
             (h.length > 3 && normalizedId.includes(h));
    });

    return match ? match.id : undefined;
  };

  const handlePreview = async (doc: ProjectDocument) => {
    let content = doc.content;
    
    if (!content && doc.driveFileId) {
      setPullingFileId(doc.driveFileId);
      try {
        const res = await fetch(`/api/drive/download/${doc.driveFileId}`, {
          headers: { "x-user-uid": user?.uid || "" }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to download file for preview");
        content = data.content;
      } catch (error) {
        console.error("Failed to fetch content for preview", error);
        toast.error("Failed to fetch content from Google Drive");
        return;
      } finally {
        setPullingFileId(null);
      }
    }

    if (!content) {
      toast.error("Document content is not available for preview");
      return;
    }

    try {
      // Handle data URL
      if (content.startsWith('data:')) {
        const parts = content.split(',');
        const mime = parts[0].match(/:(.*?);/)?.[1] || doc.type;
        const b64 = parts[1];
        const binary = atob(b64);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          array[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([array], { type: mime });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        // Assume it's a plain string
        const blob = new Blob([content], { type: doc.type || 'text/plain' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (e) {
      console.error("Failed to open preview", e);
      toast.error("Failed to open preview");
    }
  };

  const getDocTypeInfo = (doc: ProjectDocument) => {
    const sheetName = (doc.sheetName || "").toLowerCase();
    if (sheetName.includes('key')) return { label: "Key Page Mappings", type: 'key' };
    if (sheetName.includes('top sheet')) return { label: "Review Top Sheet", type: 'top_sheet' };
    if (sheetName.includes('shot')) return { label: "Shot Bidding Mappings", type: 'shots' };
    if (sheetName.includes('asset')) return { label: "Asset Bidding Mappings", type: 'assets' };
    return { label: "CSV Mappings", type: 'shots' };
  };

  const supportsAiAnalysis = (doc: ProjectDocument) => getDocTypeInfo(doc).type !== 'top_sheet';

  const queueDocumentsForAnalysis = (docsToQueue: ProjectDocument[], source: 'single' | 'bulk' = 'single') => {
    const seen = new Set<string>();
    const queueable = docsToQueue.filter((doc) => {
      if (!supportsAiAnalysis(doc)) return false;
      if (!doc?.id || seen.has(doc.id)) return false;
      seen.add(doc.id);
      return !isDocRunningAnalysis(doc.id) && !isDocQueuedForAnalysis(doc.id);
    });

    if (queueable.length === 0) {
      if (source === 'bulk') {
        toast.info("No additional documents need scanning right now.");
      }
      return;
    }

    setAnalysisQueue((prev) => [...prev, ...queueable]);
    backgroundQueueStartedCountRef.current += queueable.length;

    if (source === 'bulk') {
      toast.success(
        `${queueable.length} document${queueable.length === 1 ? '' : 's'} queued for background analysis.`
      );
    }
  };

  const getDetectedDocBadge = (doc: ProjectDocument, options?: { isSubDoc?: boolean; isGroupedParent?: boolean }) => {
    if (options?.isGroupedParent) {
      return {
        label: "QUOTE",
        Icon: FileSpreadsheet,
        pillClass: "bg-emerald-50 text-emerald-700 border border-emerald-100",
        iconClass: "text-emerald-500",
        tileClass: "bg-emerald-50 border border-emerald-100",
      };
    }

    if (options?.isSubDoc) {
      return {
        label: "SHEET",
        Icon: Table2,
        pillClass: "bg-amber-50 text-amber-700 border border-amber-100",
        iconClass: "text-amber-500",
        tileClass: "bg-white border border-amber-100",
      };
    }

    const detectedType = String(doc.analysisResult?.detected_document_type || "").toLowerCase();
    const lowerName = doc.name.toLowerCase();

    if (isStoredScriptDocument(doc)) {
      return {
        label: "SCRIPT",
        Icon: FileText,
        pillClass: "bg-violet-50 text-violet-700 border border-violet-100",
        iconClass: "text-violet-500",
        tileClass: "bg-violet-50 border border-violet-100",
      };
    }

    if (detectedType === "post_schedule" || lowerName.includes("schedule")) {
      return {
        label: "SCHED",
        Icon: Calendar,
        pillClass: "bg-sky-50 text-sky-700 border border-sky-100",
        iconClass: "text-sky-500",
        tileClass: "bg-sky-50 border border-sky-100",
      };
    }

    if (detectedType === "quote" || detectedType === "legacy_bid_csv" || doc.type === "text/csv" || (doc.type === "application/json" && !!doc.driveFileId)) {
      return {
        label: "QUOTE",
        Icon: FileSpreadsheet,
        pillClass: "bg-emerald-50 text-emerald-700 border border-emerald-100",
        iconClass: "text-emerald-500",
        tileClass: "bg-emerald-50 border border-emerald-100",
      };
    }

    return {
      label: "FILE",
      Icon: FileText,
      pillClass: "bg-slate-100 text-slate-600 border border-slate-200",
      iconClass: "text-slate-400",
      tileClass: "bg-slate-100 border border-slate-200",
    };
  };

  const getDocumentSortLabel = () => {
    return documentSort === "imported_desc" ? "Sort: Latest" : "Sort: Name";
  };

  const getDocumentSortValue = (doc: ProjectDocument) => {
    return new Date(doc.importedAt || 0).getTime();
  };

  const sortDocuments = (items: ProjectDocument[]) => {
    const sorted = [...items];
    if (documentSort === "name_asc") {
      sorted.sort((a, b) => (a.sheetName || a.name).localeCompare(b.sheetName || b.name));
    } else {
      sorted.sort((a, b) => getDocumentSortValue(b) - getDocumentSortValue(a));
    }
    return sorted;
  };

  const documentInsights = React.useMemo(() => {
    const fileDocs = documents.filter((doc) => !String(doc.type || "").includes("folder"));
    const scriptDocs = fileDocs.filter((doc) => isStoredScriptDocument(doc));
    const quoteDocs = fileDocs.filter((doc) => {
      const detectedType = String(doc.analysisResult?.detected_document_type || "").toLowerCase();
      const lowerName = doc.name.toLowerCase();
      return detectedType === "quote" || detectedType === "legacy_bid_csv" || lowerName.includes("estimate") || lowerName.includes("quote");
    });
    const scheduleDocs = fileDocs.filter((doc) => {
      const detectedType = String(doc.analysisResult?.detected_document_type || "").toLowerCase();
      const lowerName = doc.name.toLowerCase();
      return detectedType === "post_schedule" || lowerName.includes("schedule");
    });
    const sceneTotal = scriptDocs.reduce((sum, doc) => sum + (doc.sceneCount || 0), 0);
    const episodeSet = new Set(scriptDocs.map((doc) => String(doc.episode || "").trim()).filter(Boolean));
    const uniqueCharacters = new Set(
      scriptDocs.flatMap((doc) =>
        Array.isArray(doc.analysisResult?.script_data?.characters)
          ? doc.analysisResult.script_data.characters.map((character: string) => character.trim()).filter(Boolean)
          : []
      )
    );
    const uniqueLocations = new Set(
      scriptDocs.flatMap((doc) =>
        Array.isArray(doc.analysisResult?.script_data?.locations)
          ? doc.analysisResult.script_data.locations.map((location: string) => location.trim()).filter(Boolean)
          : []
      )
    );
    const inferredCities = new Set(
      Array.from(uniqueLocations)
        .map((location) => location.split(",").map((part) => part.trim()).filter(Boolean))
        .filter((parts) => parts.length >= 2)
        .map((parts) => parts[parts.length - 2])
        .filter(Boolean)
    );
    const inferredCountries = new Set(
      Array.from(uniqueLocations)
        .map((location) => location.split(",").map((part) => part.trim()).filter(Boolean))
        .filter((parts) => parts.length >= 2)
        .map((parts) => parts[parts.length - 1])
        .filter(Boolean)
    );
    const analyzedCount = fileDocs.filter((doc) => !!doc.analysisResult).length;
    const quotesAnalysed = quoteDocs.filter((doc) => !!doc.analysisResult).length;
    const quoteGrandTotal = quoteDocs.reduce((sum, doc) => {
      const grandTotal = Number(doc.analysisResult?.top_sheet_data?.cost_summary?.grand_total ?? doc.analysisResult?.quote_estimate_data?.grand_total ?? 0);
      return sum + (Number.isFinite(grandTotal) ? grandTotal : 0);
    }, 0);
    const quoteShotCount = quoteDocs.reduce((sum, doc) => {
      const shotCount = Number(doc.analysisResult?.top_sheet_data?.cost_summary?.shot_count ?? doc.analysisResult?.quote_estimate_data?.shot_count ?? 0);
      return sum + (Number.isFinite(shotCount) ? shotCount : 0);
    }, 0);
    const quoteAssetCount = quoteDocs.reduce((sum, doc) => {
      const assetCount = Number(doc.analysisResult?.top_sheet_data?.cost_summary?.asset_count ?? doc.analysisResult?.quote_estimate_data?.asset_count ?? 0);
      return sum + (Number.isFinite(assetCount) ? assetCount : 0);
    }, 0);

    const chips = [
      scriptDocs.length ? { label: `${scriptDocs.length} Script${scriptDocs.length === 1 ? "" : "s"}`, icon: FileText, className: "bg-violet-50 text-violet-700 border-violet-100" } : null,
      quoteDocs.length ? { label: `${quoteDocs.length} Quote${quoteDocs.length === 1 ? "" : "s"}`, icon: FileSpreadsheet, className: "bg-amber-50 text-amber-700 border-amber-100" } : null,
      scheduleDocs.length ? { label: `${scheduleDocs.length} Schedule${scheduleDocs.length === 1 ? "" : "s"}`, icon: Calendar, className: "bg-sky-50 text-sky-700 border-sky-100" } : null,
    ].filter(Boolean) as { label: string; icon: React.ComponentType<any>; className: string }[];

    const summaryRows: { label: string; value: string }[] = [];
    summaryRows.push({ label: "Documents Imported", value: `${fileDocs.length}` });
    if (episodeSet.size) summaryRows.push({ label: "Episodes", value: `${episodeSet.size} recognised` });
    if (sceneTotal) summaryRows.push({ label: "Scenes", value: `${sceneTotal} extracted` });
    if (uniqueCharacters.size) summaryRows.push({ label: "Characters", value: `${uniqueCharacters.size} identified` });
    if (uniqueLocations.size) summaryRows.push({ label: "Locations", value: `${uniqueLocations.size} identified` });
    if (inferredCities.size) summaryRows.push({ label: "Cities", value: `${inferredCities.size} inferred` });
    if (inferredCountries.size) summaryRows.push({ label: "Countries", value: `${inferredCountries.size} inferred` });
    if (quoteDocs.length) summaryRows.push({ label: "Quotes Recognised", value: `${quoteDocs.length}` });
    if (quoteDocs.length) summaryRows.push({ label: "Quotes Analysed", value: `${quotesAnalysed} of ${quoteDocs.length}` });
    if (quoteShotCount || quoteAssetCount) {
      summaryRows.push({
        label: "Quote Scope",
        value: `${quoteShotCount || 0} shots${quoteAssetCount ? ` • ${quoteAssetCount} assets` : " • 0 assets"}`,
      });
    }
    if (quoteGrandTotal) summaryRows.push({ label: "Quote Total", value: `£${quoteGrandTotal.toLocaleString()}` });
    return { chips, summaryRows };
  }, [documents]);

  const docsMissingAnalysis = React.useMemo(
    () => documents.filter((doc) => supportsAiAnalysis(doc) && !doc.analysisResult),
    [documents]
  );

  const getMappingStatus = (doc: ProjectDocument) => {
    const type = getDocTypeInfo(doc).type;
    if (type === 'top_sheet') {
      return { percentage: 1, color: "text-emerald-500", title: "Top Sheet uses legacy extraction preview" };
    }
    let fields: any[] = [];
    if (type === 'shots') fields = SHOT_FIELDS;
    else if (type === 'assets') fields = ASSET_FIELDS;
    else if (type === 'onset') fields = ONSET_FIELDS;
    else if (type === 'top_sheet') fields = TOP_SHEET_FIELDS;
    else if (type === 'key') fields = KEY_PAGE_FIELDS;

    const mappings = doc.allMappings?.[type] || {};
    const mappedFieldIds = new Set(Object.values(mappings).filter(id => fields.some(f => f.id === id)));
    const mappedFieldsCount = mappedFieldIds.size;
    const totalFields = fields.length;
    
    if (totalFields === 0) return { color: "text-slate-300", title: "No fields to map" };

    const percentage = mappedFieldsCount / totalFields;

    if (percentage === 1) {
      return { percentage, color: "text-emerald-500", title: "Mappings Complete (100%)" };
    } else if (percentage >= 0.9) {
      return { percentage, color: "text-amber-500", title: `Mappings Almost Complete (${Math.round(percentage * 100)}%). Please review before import.` };
    } else {
      return { percentage, color: "text-rose-500", title: `Mappings Incomplete (${Math.round(percentage * 100)}%). Not enough mappings completed to do a successful import.` };
    }
  };

  const loadSheetForMapping = async (
    doc: ProjectDocument,
    targetType: 'shots' | 'assets' | 'key' | 'top_sheet' | 'onset',
    currentModalState: any,
    showLoadingState = true
  ) => {
    if (showLoadingState) {
      setImportModal(prev => prev ? { ...prev, isLoadingSheet: true, type: targetType } : null);
    }
    try {
      const parsedData = await parseSheetData(doc, user?.uid, targetType);
      if (!parsedData) {
        if (showLoadingState) {
          setImportModal(prev => prev ? { ...prev, isLoadingSheet: false } : null);
        }
        return;
      }

      const { rows, allRows, isTransposed, images, displayHeaders, mappingHeaders } = parsedData;

      const learnedStr = localStorage.getItem('learnedMappings');
      const learned = learnedStr ? JSON.parse(learnedStr) : {};

      const initialMappings: Record<string, string> = {};
      const existingMappings = currentModalState.allMappings?.[targetType] || {};

      if (targetType === 'top_sheet') {
        Object.keys(initialMappings).forEach(key => delete initialMappings[key]);
      } else if (Object.keys(existingMappings).length > 0) {
        Object.assign(initialMappings, existingMappings);
      } else {
        displayHeaders.forEach((header, index) => {
          if (!header) return;
          let mappedId = autoMapHeader(header, targetType, index);
          if (!mappedId && learned[header.trim().toLowerCase()]) {
            mappedId = learned[header.trim().toLowerCase()];
          }
          if (mappedId && !Object.values(initialMappings).includes(mappedId)) {
            initialMappings[header] = mappedId;
          }
        });
      }

      setMappings(initialMappings);
      
      const updatedAllMappings = {
        ...(currentModalState.allMappings || {}),
        [targetType]: initialMappings
      };

      if (onSaveDocument) {
        const nextHeaders = (mappingHeaders && mappingHeaders.length > 0 ? mappingHeaders : displayHeaders).filter(Boolean);
        const existingHeaders = doc.mappingHeaders?.[targetType] || [];
        const existingIsTransposed = doc.mappingIsTransposed?.[targetType];
        const headersChanged = JSON.stringify(existingHeaders) !== JSON.stringify(nextHeaders);
        const transposeChanged = existingIsTransposed !== isTransposed;

        if (nextHeaders.length > 0 && (headersChanged || transposeChanged)) {
          void onSaveDocument({
            ...doc,
            mappingHeaders: {
              ...(doc.mappingHeaders || {}),
              [targetType]: nextHeaders,
            },
            mappingIsTransposed: {
              ...(doc.mappingIsTransposed || {}),
              [targetType]: isTransposed,
            },
          }).catch((error) => {
            console.error("Failed to persist document mapping metadata", error);
          });
        }
      }

      setImportModal({
        ...currentModalState,
        isOpen: true,
        type: targetType,
        headers: displayHeaders,
        rawHeaders: displayHeaders,
        rows: rows,
        allRows: allRows,
        fileName: doc.name,
        isTransposed: isTransposed,
        images: images,
        allMappings: updatedAllMappings,
        isLoadingSheet: false,
        selectedSheets: {
          ...(currentModalState.selectedSheets || {}),
          [targetType]: doc.id
        }
      });
    } catch (error) {
      console.error("Error loading sheet:", error);
      toast.error("Failed to load sheet data");
      if (showLoadingState) {
        setImportModal(prev => prev ? { ...prev, isLoadingSheet: false } : null);
      }
    }
  };


  const runAiMappingScan = async (type: 'shots' | 'assets' | 'key' | 'top_sheet' | 'onset', allRows: any[][], headers: string[]) => {
    // AI scanning has been disabled for quotes.
    return;
  };

  const handleCSVImport = async (doc: ProjectDocument, forcedType?: 'shots' | 'assets' | 'key' | 'top_sheet' | 'onset') => {
    const type = forcedType || getDocTypeInfo(doc).type as any;
    resetImportFeedback();

    const cachedShotMappings = (doc.allMappings?.shots || {}) as Record<string, string>;
    const canUseDirectShotImport =
      type === 'shots' &&
      doc.type === 'application/json' &&
      Boolean(projectId && user?.uid && doc.id) &&
      Object.keys(cachedShotMappings).length > 0;

    if (canUseDirectShotImport) {
      onImportStateChange?.(true);
      try {
        updateImportProgress({
          isOpen: true,
          isComplete: false,
          phaseLabel: "Importing Create SHOTS from cached document",
          totalShots: 0,
          processedShots: 0,
          totalAssets: 0,
          processedAssets: 0,
          totalThumbnails: 0,
          processedThumbnails: 0,
          onsetDays: 0,
          bidDate: null,
          version: null,
        });

        const response = await fetch(`/api/projects/${projectId}/import-legacy-shots`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user?.uid || "",
          },
          body: JSON.stringify({
            docId: doc.id,
            typeMappings: cachedShotMappings,
            fileName: doc.name,
            merge: false,
          }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Failed to import Create SHOTS from the cached document.");
        }

        if (Array.isArray(result.shots)) {
          onImportShots(result.shots, false);
        }
        if (result.setupUpdates && Object.keys(result.setupUpdates).length > 0) {
          onUpdateSetup(result.setupUpdates);
        }
        if (result.importedRates && Object.keys(result.importedRates).length > 0) {
          onUpdateRates(prev => ({ ...prev, ...result.importedRates }));
        }

        let thumbnailCount = 0;
        const hasThumbnailMapping = Object.values(cachedShotMappings).includes('thumbnail');
        if (hasThumbnailMapping) {
          updateImportProgress(prev => prev ? {
            ...prev,
            phaseLabel: "Applying shot thumbnails",
          } : null);

          const thumbnailResponse = await fetch(`/api/projects/${projectId}/import-legacy-shot-thumbnails`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-user-uid": user?.uid || "",
            },
            body: JSON.stringify({
              docId: doc.id,
              typeMappings: cachedShotMappings,
            }),
          });
          const thumbnailResult = await thumbnailResponse.json();
          if (!thumbnailResponse.ok) {
            throw new Error(thumbnailResult.error || "Failed to import Create SHOTS thumbnails from the cached document.");
          }

          if (Array.isArray(thumbnailResult.updates) && thumbnailResult.updates.length > 0) {
            await onImportShots(
              thumbnailResult.updates.map((update: { shotCode: string; thumbnail: string }) =>
                buildThumbnailOnlyImportedShot(update.shotCode, update.thumbnail)
              ),
              true
            );
            thumbnailCount = thumbnailResult.updates.length;
          }
        }

        showImportResult({
          isOpen: true,
          isComplete: true,
          phaseLabel: "Create SHOTS import complete",
          totalShots: Array.isArray(result.shots) ? result.shots.length : 0,
          processedShots: Array.isArray(result.shots) ? result.shots.length : 0,
          totalAssets: 0,
          processedAssets: 0,
          totalThumbnails: thumbnailCount,
          processedThumbnails: thumbnailCount,
          onsetDays: result.setupUpdates?.onsetSupervision ?? 0,
          bidDate: result.setupUpdates?.bidDate || null,
          version: result.setupUpdates?.version || null,
        });

        toast.success(`Successfully imported ${doc.sheetName || doc.name}`);
        return;
      } catch (error) {
        console.error("Failed to import cached Create SHOTS document:", error);
        toast.error(error instanceof Error ? error.message : "Failed to import Create SHOTS");
        return;
      } finally {
        onImportStateChange?.(false);
      }
    }

    const initialModalState = {
      isOpen: true,
      type,
      headers: [],
      rows: [],
      allRows: [],
      fileName: doc.name,
      allMappings: doc.allMappings || {},
      isLoadingSheet: true
    };
    setImportModal(initialModalState);
    loadSheetForMapping(doc, type, initialModalState);
  };

  const openDocumentMappings = (doc: ProjectDocument, forcedType?: 'shots' | 'assets' | 'key' | 'top_sheet' | 'onset') => {
    const type = forcedType || getDocTypeInfo(doc).type as any;
    resetImportFeedback();
    const existingMappings = (doc.allMappings?.[type] || {}) as Record<string, string>;
    const persistedHeaders = (doc.mappingHeaders?.[type] || []).filter(Boolean);
    const savedHeaders = (persistedHeaders.length > 0 ? persistedHeaders : Object.keys(existingMappings)).filter(Boolean);
    const hasCachedContent = typeof doc.content === 'string' && doc.content.trim().length > 0;
    const canFastOpen = doc.type === 'application/json' && savedHeaders.length > 0 && (persistedHeaders.length > 0 || hasCachedContent);

    if (canFastOpen) {
      setMappings(existingMappings);
      const initialModalState = {
        isOpen: true,
        type,
        headers: savedHeaders,
        rawHeaders: savedHeaders,
        rows: [],
        allRows: [],
        fileName: doc.name,
        allMappings: {
          ...(doc.allMappings || {}),
          [type]: existingMappings,
        },
        isLoadingSheet: false,
        isTransposed: doc.mappingIsTransposed?.[type] ?? false,
        images: [],
        selectedSheets: {
          [type]: doc.id,
        },
      };
      setImportModal(initialModalState);
      if (persistedHeaders.length === 0 && hasCachedContent) {
        void loadSheetForMapping(doc, type, initialModalState, false);
      }
      return;
    }

    const initialModalState = {
      isOpen: true,
      type,
      headers: [],
      rows: [],
      allRows: [],
      fileName: doc.name,
      allMappings: doc.allMappings || {},
      isLoadingSheet: true
    };
    setImportModal(initialModalState);
    loadSheetForMapping(doc, type, initialModalState);
  };

  const extractRatesFromKeyTab = (allRows: any[][], importedRates: Record<string, number>, importedAssetRates: Record<string, number>, typeMappings: Record<string, string>) => {
    if (!allRows || allRows.length === 0) return;

    const findAndExtractRates = (
      type: 'shots' | 'assets',
      resultMap: Record<string, number>,
      mappingPrefix: string
    ) => {
      let targetLabelRowIndex = -1;
      let targetValueRowIndex = -1;

      // Define keywords to identify the correct label row
      const shotKeywords = ['comp', 'roto', 'prep', 'cameratr', 'objecttr'];
      const assetKeywords = ['model', 'rig', 'retop', 'cloth', 'fur', 'groom', 'texture', 'lookdev', 'photoscan'];
      const keywords = type === 'shots' ? shotKeywords : assetKeywords;

      // Search for the label row
      for (let i = 0; i < allRows.length; i++) {
        const row = allRows[i];
        if (!row) continue;
        
        let matchCount = 0;
        for (let j = 0; j < row.length; j++) {
          const cellStr = String(row[j] || '').toLowerCase().replace(/[_\s-]/g, '');
          if (keywords.some(kw => cellStr.includes(kw))) {
            matchCount++;
          }
        }

        // If we find at least 2 matching keywords, we assume this is the label row
        if (matchCount >= 2) {
          targetLabelRowIndex = i;
          // The value row is usually the next row that has numbers
          for (let k = i + 1; k < Math.min(i + 4, allRows.length); k++) {
            const nextRow = allRows[k];
            if (nextRow && nextRow.some(cell => !isNaN(parseFloat(String(cell))))) {
              targetValueRowIndex = k;
              break;
            }
          }
          break;
        }
      }

      if (targetLabelRowIndex === -1 || targetValueRowIndex === -1) return;

      const labelRow = allRows[targetLabelRowIndex];
      const valueRow = allRows[targetValueRowIndex];
      if (!labelRow || !valueRow) return;

      const startCol = 1; // Start at Column B to catch all rates
      for (let c = startCol; c < labelRow.length; c++) {
        const cellValue = labelRow[c];
        if (!cellValue) continue;
        const cellStr = String(cellValue).trim();
        const lowerCellStr = cellStr.toLowerCase();
        if (lowerCellStr.includes('task') || lowerCellStr.includes('rate') || lowerCellStr.includes('export') || lowerCellStr.includes('episode')) continue;
        
        const normalizedCellStr = lowerCellStr.replace(/[_\s-]/g, '');
        
        // First check manual mappings
        let matchedTaskKey = null;
        const mappedId = typeMappings[cellValue] || typeMappings[cellStr];
        if (mappedId) {
          if (mappedId.startsWith('shot_rate_')) {
            matchedTaskKey = mappedId.replace('shot_rate_', '');
          } else if (mappedId.startsWith('asset_rate_')) {
            matchedTaskKey = mappedId.replace('asset_rate_', '');
          }
        }

        if (!matchedTaskKey) {
          const cleanH = normalizedCellStr.replace('shotrate', '').replace('assetrate', '');
          
          // Pass 1: Exact matches
          for (const [taskKey, aliases] of Object.entries(TASK_ALIASES)) {
            if (['shotCode', 'assetCode', 'description', 'sceneDescription', 'frames', 'quantity', 'assumptions', 'internalNotes', 'project_onsetSupervision'].includes(taskKey)) continue;
            if (taskKey.startsWith('ts_') || taskKey.startsWith('project_')) continue;

            const isMatch = aliases.some(alias => {
              const normalizedAlias = alias.toLowerCase().replace(/[_\s-]/g, '');
              return cleanH === normalizedAlias || normalizedCellStr === normalizedAlias;
            });

            if (isMatch) {
              matchedTaskKey = taskKey;
              break;
            }
          }
          
          // Pass 2: Partial matches
          if (!matchedTaskKey) {
            for (const [taskKey, aliases] of Object.entries(TASK_ALIASES)) {
              if (['shotCode', 'assetCode', 'description', 'sceneDescription', 'frames', 'quantity', 'assumptions', 'internalNotes', 'project_onsetSupervision'].includes(taskKey)) continue;
              if (taskKey.startsWith('ts_') || taskKey.startsWith('project_')) continue;

              const isMatch = aliases.some(alias => {
                const normalizedAlias = alias.toLowerCase().replace(/[_\s-]/g, '');
                return normalizedAlias.length > 3 && (cleanH.includes(normalizedAlias) || normalizedCellStr.includes(normalizedAlias));
              });

              if (isMatch) {
                matchedTaskKey = taskKey;
                break;
              }
            }
          }
        }

        if (matchedTaskKey) {
          const val = parseNumber(valueRow[c]);
          const finalKey = matchedTaskKey.replace('task_', '').replace('shot_rate_', '').replace('asset_rate_', '');
          if (val > 0 && !resultMap[finalKey]) {
            resultMap[finalKey] = val;
          }
        }
      }
    };

    findAndExtractRates('shots', importedRates, 'Shot Rate: ');
    findAndExtractRates('assets', importedAssetRates, 'Asset Rate: ');
  };

  const handleGroupImportAction = async (subDocs: ProjectDocument[], merge: boolean) => {
    onImportStateChange?.(true);
    let serverImportApplied = false;
    try {
      resetImportFeedback();

      const structuredDocs = subDocs.filter(doc => {
        const typeInfo = getDocTypeInfo(doc);
        return typeInfo.type !== 'top_sheet';
      });
      const mappedStructuredDocs = structuredDocs.filter(doc => {
        const typeInfo = getDocTypeInfo(doc);
        const typeMappings = doc.allMappings?.[typeInfo.type];
        return Boolean(typeMappings && Object.keys(typeMappings).length > 0);
      });

      if (structuredDocs.length > 0 && mappedStructuredDocs.length === 0) {
        toast.error("Review mappings for Create SHOTS, Create ASSETS, or Key before importing this quote.");
        return;
      }

      if (projectId && user?.uid) {
        const docIdsForServer = subDocs.map((doc) => doc.id).filter(Boolean);

        updateImportProgress({
          isOpen: true,
          isComplete: false,
          isPreparing: false,
          phaseLabel: "Sending quote group to server",
          totalShots: 0,
          processedShots: 0,
          totalAssets: 0,
          processedAssets: 0,
          totalThumbnails: 0,
          processedThumbnails: 0,
          onsetDays: 0,
          bidDate: null,
          version: null,
        });

        const response = await fetch(`/api/projects/${projectId}/import-legacy-quote-group`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid,
          },
          body: JSON.stringify({
            merge,
            docIds: docIdsForServer,
          }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Failed to import legacy quote group on the server.");
        }
        const serverImportedShots = Array.isArray(result.shots) ? result.shots.length : 0;
        const serverImportedAssets = Array.isArray(result.assets) ? result.assets.length : 0;
        const shouldFallbackToClientGroupedImport =
          mappedStructuredDocs.length > 0 &&
          serverImportedShots === 0 &&
          serverImportedAssets === 0;

        if (shouldFallbackToClientGroupedImport) {
          updateImportProgress(prev => prev ? {
            ...prev,
            phaseLabel: "Server returned no structured rows, retrying locally",
          } : prev);
        } else {
          serverImportApplied = true;

          updateImportProgress(prev => prev ? {
            ...prev,
            phaseLabel: "Applying server-imported quote",
            totalShots: serverImportedShots,
            processedShots: serverImportedShots,
            totalAssets: serverImportedAssets,
            processedAssets: serverImportedAssets,
            onsetDays: result.setup?.onsetSupervision ?? 0,
            bidDate: result.setup?.bidDate || null,
            version: result.setup?.version || null,
          } : null);

          if (Array.isArray(result.shots)) {
            onImportShots(result.shots, false);
          }
          if (Array.isArray(result.assets)) {
            onImportAssets(result.assets, false);
          }
          if (result.setup && Object.keys(result.setup).length > 0) {
            onUpdateSetup(result.setup);
          }
          if (result.rates && Object.keys(result.rates).length > 0) {
            onUpdateRates(result.rates);
          }
          if (result.assetRates && Object.keys(result.assetRates).length > 0) {
            onUpdateAssetRates(result.assetRates);
          }

          const shotDocsWithThumbnails = subDocs
            .map((doc) => {
              const typeInfo = getDocTypeInfo(doc);
              const typeMappings = (doc.allMappings?.[typeInfo.type] || {}) as Record<string, string>;
              return {
                docId: doc.id,
                type: typeInfo.type,
                fileName: doc.name,
                sheetName: doc.sheetName,
                typeMappings,
              };
            })
            .filter((entry) => entry.type === "shots");

          const serverAppliedShotThumbnails = Array.isArray(result.shots)
            ? result.shots.filter((shot: any) => typeof shot?.thumbnail === "string" && shot.thumbnail.trim().length > 0).length
            : 0;

          let appliedShotThumbnails = serverAppliedShotThumbnails;
          if (appliedShotThumbnails > 0) {
            updateImportProgress(prev => prev ? {
              ...prev,
              totalThumbnails: appliedShotThumbnails,
              processedThumbnails: appliedShotThumbnails,
            } : null);
          } else {
            for (const shotDoc of shotDocsWithThumbnails) {
              updateImportProgress(prev => prev ? {
                ...prev,
                phaseLabel: `Applying shot thumbnails from ${shotDoc.sheetName || shotDoc.fileName}`,
              } : null);

              const thumbnailResponse = await fetch(`/api/projects/${projectId}/import-legacy-shot-thumbnails`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "x-user-uid": user.uid,
                },
                body: JSON.stringify({
                  docId: shotDoc.docId,
                  typeMappings: shotDoc.typeMappings,
                }),
              });
              const thumbnailResult = await thumbnailResponse.json();
              if (!thumbnailResponse.ok) {
                throw new Error(thumbnailResult.error || "Failed to import grouped shot thumbnails on the server.");
              }

              if (Array.isArray(thumbnailResult.updates) && thumbnailResult.updates.length > 0) {
                await onImportShots(
                  thumbnailResult.updates.map((update: { shotCode: string; thumbnail: string }) =>
                    buildThumbnailOnlyImportedShot(update.shotCode, update.thumbnail)
                  ),
                  true
                );
                appliedShotThumbnails += thumbnailResult.updates.length;
                updateImportProgress(prev => prev ? {
                  ...prev,
                  totalThumbnails: appliedShotThumbnails,
                  processedThumbnails: appliedShotThumbnails,
                } : null);
              }
            }
          }

          const currentProgress = importProgressRef.current || {
            isOpen: true,
            isComplete: false,
            totalShots: serverImportedShots,
            processedShots: serverImportedShots,
            totalAssets: serverImportedAssets,
            processedAssets: serverImportedAssets,
            totalThumbnails: appliedShotThumbnails,
            processedThumbnails: appliedShotThumbnails,
            onsetDays: result.setup?.onsetSupervision ?? 0,
            bidDate: result.setup?.bidDate || null,
            version: result.setup?.version || null,
          };

          const sourceDocs = subDocs.map((doc) => doc.sheetName || doc.name);
          const usedDocTypes = new Set(
            subDocs.map((doc) => getDocTypeInfo(doc).type)
          );
          const hasKeyDoc = usedDocTypes.has('key');
          const hasTopSheetDoc = subDocs.some((doc) => getDocTypeInfo(doc).type === 'top_sheet');
          const thumbnailSourceCount = shotDocsWithThumbnails.length;
          const thumbnailStepStatus =
            thumbnailSourceCount === 0
              ? { status: 'info' as const, detail: 'No thumbnail-mapped shot sheet was included in this run.' }
              : appliedShotThumbnails > 0
                ? {
                    status: 'done' as const,
                    detail:
                      serverAppliedShotThumbnails > 0
                        ? `${appliedShotThumbnails} shot thumbnails were included in the grouped import.`
                        : `${appliedShotThumbnails} shot thumbnails were applied.`,
                  }
                : { status: 'warning' as const, detail: 'No shot thumbnails were applied from the Create SHOTS document.' };

          const outcomeSummary = {
            mode: merge ? 'merge' as const : 'import' as const,
            sourceDocs,
            steps: [
              {
                label: 'Quote tabs used',
                detail: sourceDocs.length > 0 ? sourceDocs.join(' • ') : 'No quote tabs were selected.',
                status: 'info' as const,
              },
              {
                label: merge ? 'Merge result' : 'Import result',
                detail: `${serverImportedShots} shots and ${serverImportedAssets} assets are now in the project snapshot.`,
                status: 'done' as const,
              },
              {
                label: 'Setup values',
                detail: `Bid date ${result.setup?.bidDate || 'not detected'} • Version ${result.setup?.version || 'not detected'} • Onset ${result.setup?.onsetSupervision ?? 0} days.`,
                status: 'done' as const,
              },
              {
                label: 'Rates and top sheet',
                detail: `${hasKeyDoc ? 'Key tab was included.' : 'No Key tab was included.'} ${hasTopSheetDoc ? 'Top Sheet was included.' : 'No Top Sheet was included.'}`,
                status: hasKeyDoc || hasTopSheetDoc ? 'done' as const : 'info' as const,
              },
              {
                label: 'Thumbnails',
                detail: thumbnailStepStatus.detail,
                status: thumbnailStepStatus.status,
              },
            ],
          };

          showImportResult({
            ...currentProgress,
            isComplete: true,
            totalShots: serverImportedShots,
            processedShots: serverImportedShots,
            totalAssets: serverImportedAssets,
            processedAssets: serverImportedAssets,
            totalThumbnails: appliedShotThumbnails,
            processedThumbnails: appliedShotThumbnails,
            onsetDays: result.setup?.onsetSupervision ?? currentProgress.onsetDays ?? 0,
            bidDate: result.setup?.bidDate || currentProgress.bidDate || null,
            version: result.setup?.version || currentProgress.version || null,
            outcomeSummary,
          });

          const persistedMappings = subDocs.reduce<Record<string, Record<string, string>>>((acc, doc) => {
            const typeInfo = getDocTypeInfo(doc);
            const typeMappings = (doc.allMappings?.[typeInfo.type] || {}) as Record<string, string>;
            if (Object.keys(typeMappings || {}).length > 0) {
              acc[typeInfo.type] = typeMappings;
            }
            return acc;
          }, {});
          if (Object.keys(persistedMappings).length > 0) {
            await persistDocumentMappings(persistedMappings, subDocs);
          }
          return;
        }
      }

      updateImportProgress({
        isOpen: true,
        isComplete: false,
        isPreparing: true,
        phaseLabel: "Preparing quote import",
        totalShots: 0,
        processedShots: 0,
        totalAssets: 0,
        processedAssets: 0,
        totalThumbnails: 0,
        processedThumbnails: 0,
        onsetDays: 0,
        bidDate: null,
        version: null
      });

      let totalOnsetSupervision = 0;
      let importedOnsetLegacyTotal = 0;
      const importedRates: Record<string, number> = {};
      const importedAssetRates: Record<string, number> = {};
      const shotsToImport: Shot[] = [];
      const assetsToImport: Asset[] = [];
      const newTopSheet: TopSheet = { ...(setup.topSheet || {}) };
      let hasTopSheetUpdates = false;

      // Pre-parse and calculate totals
      const parsedDocsData: {
        doc: ProjectDocument;
        type: string;
        typeMappings: Record<string, string>;
        parsedData: any;
      }[] = [];
      let totalShots = 0;
      let totalAssets = 0;
      let totalThumbnails = 0;
      let totalParsedShotRows = 0;
      let totalValidShotRows = 0;
      let totalParsedAssetRows = 0;
      let totalValidAssetRows = 0;
      let latestImportedReference = setup.topSheet?.importedReference;
      const importDiagnosticsByDoc = new Map<string, ImportDiagnosticsDoc>();

      const ensureImportDiagnostic = (
        doc: ProjectDocument,
        type: ImportDiagnosticsDoc["type"]
      ): ImportDiagnosticsDoc => {
        const key = `${type}:${doc.id}`;
        const existing = importDiagnosticsByDoc.get(key);
        if (existing) return existing;
        const created: ImportDiagnosticsDoc = {
          docId: doc.id,
          docName: doc.sheetName || doc.name,
          type,
          parsedRows: 0,
          validRows: 0,
          importedRows: 0,
          duplicateRows: 0,
          thumbnailMatches: 0,
        };
        importDiagnosticsByDoc.set(key, created);
        return created;
      };

      for (const doc of subDocs) {
        const typeInfo = getDocTypeInfo(doc);
        const type = typeInfo.type;
        const typeMappings = (doc.allMappings?.[type] || {}) as Record<string, string>;

        if (type !== 'top_sheet' && (!typeMappings || Object.keys(typeMappings).length === 0)) continue;

        const parsedData = await parseSheetData(doc, user?.uid, type, {
          includeImages: type !== 'shots' && type !== 'assets',
        });
        if (!parsedData) continue;
        
        parsedDocsData.push({ doc, type, typeMappings, parsedData });

        if (type === 'shots') {
          const diagnostic = ensureImportDiagnostic(doc, 'shots');
          const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'shotCode');
          const validRows = parsedData.rows.filter(r => {
             const code = (codeCol ? (r[codeCol] ?? "") : "").toString().trim();
             const normalized = code.toLowerCase().replace(/[^a-z0-9]/g, '');
             return code && normalized !== 'onsetsupervision' && normalized !== 'onsetvfxsupervision' && normalized !== 'onset';
          });
          diagnostic.parsedRows = parsedData.rows.length;
          diagnostic.validRows = validRows.length;
          totalParsedShotRows += parsedData.rows.length;
          totalValidShotRows += validRows.length;
          totalShots += validRows.length;
          const thumbCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'thumbnail');
          if (thumbCol && parsedData.images) {
            validRows.forEach(row => {
               const headerIndex = parsedData.displayHeaders?.indexOf(thumbCol) ?? -1;
               const targetRow = parsedData.isTransposed ? headerIndex : row.__nativeRow;
               const targetCol = parsedData.isTransposed ? row.__nativeRowIndex : headerIndex;
               const img = parsedData.images.find(img => img.row === targetRow && img.col === targetCol);
               if (img) {
                 totalThumbnails++;
                 diagnostic.thumbnailMatches = (diagnostic.thumbnailMatches || 0) + 1;
               }
            });
          }
        } else if (type === 'assets') {
          const diagnostic = ensureImportDiagnostic(doc, 'assets');
          const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'assetCode');
          const validRows = parsedData.rows.filter(r => {
             const code = (codeCol ? (r[codeCol] ?? "") : "").toString().trim();
             const normalized = code.toLowerCase().replace(/[^a-z0-9]/g, '');
             return code && normalized !== 'onsetsupervision' && normalized !== 'onsetvfxsupervision' && normalized !== 'onset';
          });
          diagnostic.parsedRows = parsedData.rows.length;
          diagnostic.validRows = validRows.length;
          totalParsedAssetRows += parsedData.rows.length;
          totalValidAssetRows += validRows.length;
          totalAssets += validRows.length;
          const thumbCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'thumbnail');
          if (thumbCol && parsedData.images) {
            validRows.forEach(row => {
               const headerIndex = parsedData.displayHeaders?.indexOf(thumbCol) ?? -1;
               const targetRow = parsedData.isTransposed ? headerIndex : row.__nativeRow;
               const targetCol = parsedData.isTransposed ? row.__nativeRowIndex : headerIndex;
               const img = parsedData.images.find(img => img.row === targetRow && img.col === targetCol);
               if (img) {
                 totalThumbnails++;
                 diagnostic.thumbnailMatches = (diagnostic.thumbnailMatches || 0) + 1;
               }
            });
          }
        }
      }

      const shouldDeferImportThumbnails = totalShots + totalAssets > IMPORT_THUMBNAIL_PROCESSING_MAX_ITEMS;
      const shouldProcessImportThumbnails = totalThumbnails > 0;
      const duplicateShotCodes = new Set<string>();
      const duplicateAssetCodes = new Set<string>();
      const duplicateShotsToResolve: DuplicateImportShotCandidate[] = [];
      const duplicateAssetsToResolve: DuplicateImportAssetCandidate[] = [];
      const buildImportDiagnosticsSummary = (): NonNullable<ImportProgressData["diagnostics"]> => ({
        totalParsedShotRows,
        totalValidShotRows,
        totalImportedShots: shotsToImport.length,
        totalDuplicateShots: duplicateShotsToResolve.length,
        totalParsedAssetRows,
        totalValidAssetRows,
        totalImportedAssets: assetsToImport.length,
        totalDuplicateAssets: duplicateAssetsToResolve.length,
        docs: Array.from(importDiagnosticsByDoc.values()).sort((left, right) => {
          if (left.type === right.type) return left.docName.localeCompare(right.docName);
          return left.type.localeCompare(right.type);
        }),
      });

      let bidDate = null;
      let version = null;
      if (subDocs.length > 0 && subDocs[0].name) {
        const dateMatch = subDocs[0].name.match(/(?:^|[^0-9])(\d{4})[-]?(\d{2})[-]?(\d{2})(?:[^0-9]|$)/);
        if (dateMatch) bidDate = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
        const versionMatch = subDocs[0].name.match(/(?:^|[^a-zA-Z0-9])v(\d+)(?:[^a-zA-Z0-9]|$)/i);
        if (versionMatch) version = 'v' + versionMatch[1].padStart(2, '0');
      }

      updateImportProgress({
        isOpen: true,
        isComplete: false,
        isPreparing: false,
        phaseLabel: "Starting grouped import",
        totalShots,
        processedShots: 0,
        totalAssets,
        processedAssets: 0,
        totalThumbnails,
        processedThumbnails: 0,
        onsetDays: 0,
        bidDate,
        version,
        diagnostics: buildImportDiagnosticsSummary(),
      });

      let processedShotsCount = 0;
      let processedAssetsCount = 0;
      let processedThumbnailsCount = 0;
      const shouldResizeImportThumbnails =
        shouldProcessImportThumbnails &&
        !shouldDeferImportThumbnails &&
        totalThumbnails > 0 &&
        totalThumbnails <= IMPORT_THUMBNAIL_RESIZE_THRESHOLD;
      const shouldBuildDetailedImportReport = totalShots + totalAssets <= IMPORT_DETAILED_REPORT_MAX_ITEMS;
      const deferredShotThumbnails: Array<{ shotCode: string; dataUrl: string }> = [];
      const deferredAssetThumbnails: Array<{ assetCode: string; dataUrl: string }> = [];

      const shotsMap = new Map<string, Shot>();
      const assetsMap = new Map<string, Asset>();

      for (const { doc, type, typeMappings, parsedData } of parsedDocsData) {
        const { rows, images, isTransposed, displayHeaders } = parsedData;
        const rawHeaders = displayHeaders;
        const importDiagnostic =
          type === 'shots' || type === 'assets'
            ? ensureImportDiagnostic(doc, type)
            : null;

        if (type === 'shots') {
          updateImportProgress(prev => prev ? { ...prev, phaseLabel: `Importing shots from ${doc.sheetName || doc.name}` } : null);
          const shotCodeHeader = Object.keys(typeMappings).find(h => typeMappings[h] === 'shotCode');
          
          const detectedOnsetDays = extractLegacyOnsetSupervisionDays(rows, parsedData.allRows, typeMappings);
          if (detectedOnsetDays > 0) {
            totalOnsetSupervision = normalizeOnsetDays(totalOnsetSupervision + detectedOnsetDays);
            updateImportProgress(prev => prev ? { ...prev, onsetDays: totalOnsetSupervision } : null);
          }

          for (const row of rows) {
            const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'shotCode');
            const rawCode = codeCol ? row[codeCol] : "";
            const code = (rawCode ?? "").toString().trim().toLowerCase();
            const normalized = code.replace(/[^a-z0-9]/g, '');
            
            if (!code) continue;

            const isOnsetRow = normalized === 'onsetsupervision' || normalized === 'onsetvfxsupervision' || normalized === 'onset';
            if (isOnsetRow) {
              const legacyOnsetPrice = extractLegacyLinePrice(row, typeMappings);
              if (legacyOnsetPrice > 0) importedOnsetLegacyTotal += legacyOnsetPrice;
              continue;
            }

            if (shotsMap.has(code)) {
              if (importDiagnostic) {
                importDiagnostic.duplicateRows += 1;
              }
              duplicateShotCodes.add(code);
              const duplicateShot: Shot = {
                id: uuidv4(),
                active: true,
                shotCode: code,
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                sceneDescription: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'sceneDescription') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                frames: Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'frames') || ""])),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, comp: 0, roto: 0, prep: 0, gfx: 0,
                  cameraTR: 0, objectTR: 0, rANIM: 0, layout: 0, anim: 0, light: 0, fx: 0
                }
              };
              for (const [header, field] of Object.entries(typeMappings)) {
                const fieldStr = field as string;
                if (fieldStr === 'status') {
                  const status = normalizeImportedBidStatus(row[header]);
                  if (status) {
                    duplicateShot.status = status as ShotStatus;
                    duplicateShot.active = status !== "Lost" && status !== "Off";
                  }
                } else if (fieldStr.startsWith('task_')) {
                  const taskKey = fieldStr.replace('task_', '') as keyof Shot['days'];
                  const val = parseNumber(row[header]);
                  if (val > 0) duplicateShot.days[taskKey] = val;
                }
              }
              const reservedShotCodes = new Set<string>([
                ...existingShots.map(shot => normalizeCodeKey(shot.shotCode)),
                ...Array.from(shotsMap.keys()).map(normalizeCodeKey),
                ...duplicateShotsToResolve.map(candidate => normalizeCodeKey(candidate.suggestedCode)),
              ]);
              const suggestedCode = suggestDuplicateShotCode(code, reservedShotCodes);
              duplicateShotsToResolve.push({
                id: duplicateShot.id,
                originalCode: code,
                suggestedCode,
                occurrence: duplicateShotsToResolve.filter(candidate => candidate.originalCode === code).length + 2,
                imported: duplicateShot.legacyPrice ?? null,
                calculated: calculateShotImportCost(duplicateShot, { ...DEFAULT_RATES, ...rates, ...importedRates }, setup),
                description: duplicateShot.description || duplicateShot.sceneDescription || duplicateShot.scopeOfWork,
                payload: duplicateShot,
              });
              continue;
            }

            let shot = shotsMap.get(code);
            let isNewShot = false;
            if (!shot) {
              isNewShot = true;
              shot = {
                id: uuidv4(),
                active: true,
                shotCode: code,
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                sceneDescription: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'sceneDescription') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                frames: Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'frames') || ""])),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, comp: 0, roto: 0, prep: 0, gfx: 0,
                  cameraTR: 0, objectTR: 0, rANIM: 0, layout: 0, anim: 0, light: 0, fx: 0
                }
              };
              shotsMap.set(code, shot);
            }

            for (const [header, field] of Object.entries(typeMappings)) {
              const fieldStr = field as string;
              if (fieldStr === 'thumbnail' && images && isNewShot && shouldProcessImportThumbnails) {
                const headerIndex = rawHeaders?.indexOf(header) ?? -1;
                const targetRow = isTransposed ? headerIndex : row.__nativeRow;
                const targetCol = isTransposed ? row.__nativeRowIndex : headerIndex;
                const img = images.find(img => img.row === targetRow && img.col === targetCol);
                if (img) {
                  if (shouldDeferImportThumbnails) {
                    deferredShotThumbnails.push({ shotCode: code, dataUrl: img.dataUrl });
                  } else {
                    try {
                      shot.thumbnail = await prepareImportThumbnail(img.dataUrl, shouldResizeImportThumbnails);
                      processedThumbnailsCount++;
                      updateImportProgress(prev => prev ? { ...prev, processedThumbnails: processedThumbnailsCount } : null);
                    } catch (e) {
                      console.error("Failed to crop thumbnail", e);
                      shot.thumbnail = img.dataUrl;
                    }
                  }
                }
              } else if (fieldStr === 'status') {
                const status = normalizeImportedBidStatus(row[header]);
                if (status) {
                  shot.status = status as ShotStatus;
                  shot.active = status !== "Lost" && status !== "Off";
                }
              } else if (fieldStr.startsWith('task_')) {
                const taskKey = fieldStr.replace('task_', '') as keyof Shot['days'];
                const val = parseNumber(row[header]);
                if (val > 0) shot.days[taskKey] = val;
              } else if (fieldStr.startsWith('shot_rate_')) {
                const rateKey = fieldStr.replace('shot_rate_', '');
                const val = parseNumber(row[header]);
                if (val > 0 && !importedRates[rateKey]) {
                  importedRates[rateKey] = val;
                }
              }
            }

            if (isNewShot) {
              shotsToImport.push(shot);
              if (importDiagnostic) {
                importDiagnostic.importedRows += 1;
              }
              processedShotsCount++;
              if (processedShotsCount % 5 === 0) {
                updateImportProgress(prev => prev ? { ...prev, processedShots: processedShotsCount, diagnostics: buildImportDiagnosticsSummary() } : null);
                await new Promise(r => setTimeout(r, 0));
              }
            }
          }
          // Final update for shots
          updateImportProgress(prev => prev ? { ...prev, processedShots: processedShotsCount, diagnostics: buildImportDiagnosticsSummary() } : null);
          await new Promise(r => setTimeout(r, 0));
        } else if (type === 'assets') {
          updateImportProgress(prev => prev ? { ...prev, phaseLabel: `Importing assets from ${doc.sheetName || doc.name}` } : null);
          for (const row of rows) {
            const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'assetCode');
            const rawCode = codeCol ? row[codeCol] : "";
            const code = (rawCode ?? "").toString().trim();

            if (!code) continue;

            const isOnsetRow = code.toLowerCase() === 'onset supervision' || code.toLowerCase() === 'onset';
            if (isOnsetRow) {
              const legacyOnsetPrice = extractLegacyLinePrice(row, typeMappings);
              if (legacyOnsetPrice > 0) importedOnsetLegacyTotal += legacyOnsetPrice;
              // Legacy onset days are sourced from Create SHOTS only.
              // Asset imports may still carry a legacy onset price reference,
              // but they must never mutate the project onset-day total.
              continue;
            }

            if (assetsMap.has(code)) {
              if (importDiagnostic) {
                importDiagnostic.duplicateRows += 1;
              }
              duplicateAssetCodes.add(code.toLowerCase());
              const duplicateAsset: Asset = {
                id: uuidv4(),
                active: true,
                assetCode: code,
                type: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'type') || ""]),
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, model: 0, rig: 0, retop: 0,
                  cloth: 0, fur: 0, groom: 0, texture: 0, anim: 0, fx: 0, lookdev: 0, photoscan: 0
                }
              };
              for (const [header, field] of Object.entries(typeMappings)) {
                const fieldStr = field as string;
                if (fieldStr === 'status') {
                  const status = normalizeImportedBidStatus(row[header]);
                  if (status) {
                    duplicateAsset.status = status as ShotStatus;
                    duplicateAsset.active = status !== "Lost" && status !== "Off";
                  }
                } else if (fieldStr === 'type') {
                  duplicateAsset.type = sanitizeText(row[header]);
                } else if (fieldStr.startsWith('task_')) {
                  const taskKey = fieldStr.replace('task_', '') as keyof Asset['days'];
                  const val = parseNumber(row[header]);
                  if (val > 0) duplicateAsset.days[taskKey] = val;
                }
              }
              const reservedAssetCodes = new Set<string>([
                ...existingAssets.map(asset => normalizeCodeKey(asset.assetCode)),
                ...Array.from(assetsMap.keys()).map(normalizeCodeKey),
                ...duplicateAssetsToResolve.map(candidate => normalizeCodeKey(candidate.suggestedCode)),
              ]);
              const suggestedCode = suggestDuplicateAssetCode(code, reservedAssetCodes);
              duplicateAssetsToResolve.push({
                id: duplicateAsset.id,
                originalCode: code,
                suggestedCode,
                occurrence: duplicateAssetsToResolve.filter(candidate => candidate.originalCode === code).length + 2,
                imported: duplicateAsset.legacyPrice ?? null,
                calculated: calculateAssetImportCost(duplicateAsset, { ...DEFAULT_ASSET_RATES, ...assetRates, ...importedAssetRates }, setup),
                description: duplicateAsset.description || duplicateAsset.scopeOfWork,
                payload: duplicateAsset,
              });
              continue;
            }

            let asset = assetsMap.get(code);
            let isNewAsset = false;
            if (!asset) {
              isNewAsset = true;
              asset = {
                id: uuidv4(),
                active: true,
                assetCode: code,
                type: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'type') || ""]),
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, model: 0, rig: 0, retop: 0,
                  cloth: 0, fur: 0, groom: 0, texture: 0, anim: 0, fx: 0, lookdev: 0, photoscan: 0
                }
              };
              assetsMap.set(code, asset);
            }

            for (const [header, field] of Object.entries(typeMappings)) {
              const fieldStr = field as string;
              if (fieldStr === 'thumbnail' && images && isNewAsset && shouldProcessImportThumbnails) {
                const headerIndex = rawHeaders?.indexOf(header) ?? -1;
                const targetRow = isTransposed ? headerIndex : row.__nativeRow;
                const targetCol = isTransposed ? row.__nativeRowIndex : headerIndex;
                const img = images.find(img => img.row === targetRow && img.col === targetCol);
                if (img) {
                  if (shouldDeferImportThumbnails) {
                    deferredAssetThumbnails.push({ assetCode: code, dataUrl: img.dataUrl });
                  } else {
                    try {
                      asset.thumbnail = await prepareImportThumbnail(img.dataUrl, shouldResizeImportThumbnails);
                      processedThumbnailsCount++;
                      updateImportProgress(prev => prev ? { ...prev, processedThumbnails: processedThumbnailsCount } : null);
                    } catch (e) {
                      console.error("Failed to crop thumbnail", e);
                      asset.thumbnail = img.dataUrl;
                    }
                  }
                }
              } else if (fieldStr === 'status') {
                const status = normalizeImportedBidStatus(row[header]);
                if (status) {
                  asset.status = status as ShotStatus;
                  asset.active = status !== "Lost" && status !== "Off";
                }
              } else if (fieldStr === 'type') {
                asset.type = sanitizeText(row[header]);
              } else if (fieldStr.startsWith('task_')) {
                const taskKey = fieldStr.replace('task_', '') as keyof Asset['days'];
                const val = parseNumber(row[header]);
                if (val > 0) asset.days[taskKey] = val; // Overwrite if > 0, or add? Let's overwrite with the non-zero value
              } else if (fieldStr.startsWith('asset_rate_')) {
                const rateKey = fieldStr.replace('asset_rate_', '');
                const val = parseNumber(row[header]);
                if (val > 0 && !importedAssetRates[rateKey]) {
                  importedAssetRates[rateKey] = val;
                }
              }
            }

            if (isNewAsset) {
              assetsToImport.push(asset);
              if (importDiagnostic) {
                importDiagnostic.importedRows += 1;
              }
              processedAssetsCount++;
              if (processedAssetsCount % 5 === 0) {
                updateImportProgress(prev => prev ? { ...prev, processedAssets: processedAssetsCount, diagnostics: buildImportDiagnosticsSummary() } : null);
                await new Promise(r => setTimeout(r, 0));
              }
            }
          }
          // Final update for assets
          updateImportProgress(prev => prev ? { ...prev, processedAssets: processedAssetsCount, diagnostics: buildImportDiagnosticsSummary() } : null);
          await new Promise(r => setTimeout(r, 0));
        } else if (type === 'key') {
          updateImportProgress(prev => prev ? { ...prev, phaseLabel: `Applying key rates from ${doc.sheetName || doc.name}` } : null);
          extractRatesFromKeyTab(parsedData.allRows, importedRates, importedAssetRates, typeMappings);
        } else if (type === 'top_sheet') {
          updateImportProgress(prev => prev ? { ...prev, phaseLabel: `Importing top sheet from ${doc.sheetName || doc.name}` } : null);
          const extractedTopSheet = extractLegacyTopSheetData(parsedData.allRows, doc.name, doc.sheetName);
          for (const row of rows) {
            for (const [header, fieldId] of Object.entries(typeMappings)) {
              const val = sanitizeTopSheetMappedValue(fieldId, (row[header] ?? "").toString());
              if (!val) continue;

              if (fieldId === 'ts_quoteName') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { quoteName: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              }
              else if (fieldId === 'ts_quoteDate') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { quoteDate: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              }
              else if (fieldId === 'ts_version') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { version: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              }
              else if (fieldId === 'ts_cost_shotTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { shotTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_assetTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { assetTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_onsetTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { onsetTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_grandTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { grandTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_shotCount') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { shotCount: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_assetCount') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { assetCount: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_currency') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName: doc.name, sourceSheetName: doc.sheetName, costs: { currency: val } });
              }
              else if (fieldId === 'ts_summary') {
                newTopSheet.summary = val;
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { summary: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              }
              else if (fieldId === 'ts_approach') newTopSheet.approach = val;
              else if (fieldId === 'ts_assumptions') newTopSheet.assumptions = val;
              else if (fieldId === 'ts_clarifications') newTopSheet.clarifications = val;
              else if (fieldId === 'ts_exclusions') newTopSheet.exclusions = val;
              else if (fieldId === 'ts_estimateAssumptions') newTopSheet.estimateAssumptions = val;
              else if (fieldId === 'ts_termsAndConditions') newTopSheet.termsAndConditions = val;

              if (fieldId === 'ts_approach') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { approach: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              else if (fieldId === 'ts_assumptions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { assumptions: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              else if (fieldId === 'ts_clarifications') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { clarifications: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              else if (fieldId === 'ts_exclusions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { exclusions: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              else if (fieldId === 'ts_estimateAssumptions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { estimateAssumptions: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              else if (fieldId === 'ts_termsAndConditions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { termsAndConditions: val, sourceDocumentName: doc.name, sourceSheetName: doc.sheetName });
              
              hasTopSheetUpdates = true;
            }
          }

          if (extractedTopSheet.importedReference) {
            newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, extractedTopSheet.importedReference);
            hasTopSheetUpdates = true;
          }
          if (shouldReplaceTopSheetField(newTopSheet.summary, extractedTopSheet.summary, "Summary")) newTopSheet.summary = extractedTopSheet.summary;
          if (shouldReplaceTopSheetField(newTopSheet.approach, extractedTopSheet.approach, "Approach")) newTopSheet.approach = extractedTopSheet.approach;
          if (shouldReplaceTopSheetField(newTopSheet.assumptions, extractedTopSheet.assumptions, "Assumptions")) newTopSheet.assumptions = extractedTopSheet.assumptions;
          if (shouldReplaceTopSheetField(newTopSheet.clarifications, extractedTopSheet.clarifications, "Clarifications")) newTopSheet.clarifications = extractedTopSheet.clarifications;
          if (shouldReplaceTopSheetField(newTopSheet.exclusions, extractedTopSheet.exclusions, "Exclusions")) newTopSheet.exclusions = extractedTopSheet.exclusions;
          if (shouldReplaceTopSheetField(newTopSheet.estimateAssumptions, extractedTopSheet.estimateAssumptions, "Quote Assumptions")) newTopSheet.estimateAssumptions = extractedTopSheet.estimateAssumptions;
          if (shouldReplaceTopSheetField(newTopSheet.termsAndConditions, extractedTopSheet.termsAndConditions, "Terms and Conditions")) newTopSheet.termsAndConditions = extractedTopSheet.termsAndConditions;
        }
      }

      if (shotsToImport.length > 0) {
        updateImportProgress(prev => prev ? { ...prev, phaseLabel: "Committing imported shots" } : null);
        onImportShots(shotsToImport, merge);
      }
      if (assetsToImport.length > 0) {
        updateImportProgress(prev => prev ? { ...prev, phaseLabel: "Committing imported assets" } : null);
        onImportAssets(assetsToImport, merge);
      }
      if (hasTopSheetUpdates) {
        updateImportProgress(prev => prev ? { ...prev, phaseLabel: "Applying top sheet updates" } : null);
        if (onImportTopSheet) {
          onImportTopSheet(newTopSheet);
        } else {
          onUpdateSetup({ topSheet: newTopSheet });
        }
      }

      if (Object.keys(importedRates).length > 0) {
        updateImportProgress(prev => prev ? { ...prev, phaseLabel: "Applying imported rates" } : null);
        onUpdateRates(prev => ({ ...prev, ...importedRates }));
      }
      if (Object.keys(importedAssetRates).length > 0) {
        onUpdateAssetRates(prev => ({ ...prev, ...importedAssetRates }));
      }

      if (shouldDeferImportThumbnails && (deferredShotThumbnails.length > 0 || deferredAssetThumbnails.length > 0)) {
        updateImportProgress(prev => prev ? { ...prev, phaseLabel: "Applying imported thumbnails" } : null);

        const THUMBNAIL_BATCH_SIZE = 8;

        for (let index = 0; index < deferredShotThumbnails.length; index += THUMBNAIL_BATCH_SIZE) {
          const batch = await Promise.all(
            deferredShotThumbnails
              .slice(index, index + THUMBNAIL_BATCH_SIZE)
              .map(async ({ shotCode, dataUrl }) => {
                try {
                  return buildThumbnailOnlyImportedShot(shotCode, await prepareDeferredImportThumbnail(dataUrl));
                } catch (error) {
                  console.error("Failed to prepare deferred shot thumbnail", error);
                  return buildThumbnailOnlyImportedShot(shotCode, dataUrl);
                }
              })
          );
          onImportShots(batch, true);
          processedThumbnailsCount += batch.length;
          updateImportProgress(prev => prev ? { ...prev, processedThumbnails: processedThumbnailsCount } : null);
          await new Promise(r => setTimeout(r, 0));
        }

        for (let index = 0; index < deferredAssetThumbnails.length; index += THUMBNAIL_BATCH_SIZE) {
          const batch = await Promise.all(
            deferredAssetThumbnails
              .slice(index, index + THUMBNAIL_BATCH_SIZE)
              .map(async ({ assetCode, dataUrl }) => {
                try {
                  return buildThumbnailOnlyImportedAsset(assetCode, await prepareDeferredImportThumbnail(dataUrl));
                } catch (error) {
                  console.error("Failed to prepare deferred asset thumbnail", error);
                  return buildThumbnailOnlyImportedAsset(assetCode, dataUrl);
                }
              })
          );
          onImportAssets(batch, true);
          processedThumbnailsCount += batch.length;
          updateImportProgress(prev => prev ? { ...prev, processedThumbnails: processedThumbnailsCount } : null);
          await new Promise(r => setTimeout(r, 0));
        }
      }

      const setupUpdates: any = {};
      
      if (totalOnsetSupervision > 0) {
        setupUpdates.onsetSupervision = totalOnsetSupervision;
      } else if (!merge) {
        setupUpdates.onsetSupervision = 0;
      }

      const importedReference = newTopSheet.importedReference || setup.topSheet?.importedReference;

      if (subDocs.length > 0 && subDocs[0].name) {
        const dateMatch = subDocs[0].name.match(/(?:^|[^0-9])(\d{4})[-]?(\d{2})[-]?(\d{2})(?:[^0-9]|$)/);
        if (dateMatch) {
          setupUpdates.bidDate = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
        }
      }

      const resolvedVersion = resolveImportedVersion({
        explicitVersion: importedReference?.version,
        quoteName: importedReference?.quoteName,
        fileName: subDocs[0]?.name,
      });
      if (resolvedVersion) {
        setupUpdates.version = resolvedVersion;
      }

      if (Object.keys(setupUpdates).length > 0) {
        onUpdateSetup(prev => ({ ...prev, ...setupUpdates }));
      }

      const effectiveRates = { ...DEFAULT_RATES, ...rates, ...importedRates };
      const effectiveAssetRates = { ...DEFAULT_ASSET_RATES, ...assetRates, ...importedAssetRates };
      const effectiveSetup = { ...setup, ...setupUpdates };
      const importedReferenceCosts = newTopSheet.importedReference?.costs || setup.topSheet?.importedReference?.costs || {};
      const importedShotTotalFromLines = shotsToImport.reduce((acc, shot) => acc + (shot.legacyPrice || 0), 0);
      const importedAssetTotalFromLines = assetsToImport.reduce((acc, asset) => acc + (asset.legacyPrice || 0), 0);
      const calculatedShotTotal = shotsToImport.reduce((acc, shot) => acc + calculateShotImportCost(shot, effectiveRates, effectiveSetup), 0);
      const calculatedAssetTotal = assetsToImport.reduce((acc, asset) => acc + calculateAssetImportCost(asset, effectiveAssetRates, effectiveSetup), 0);
      const calculatedOnsetTotal = totalOnsetSupervision > 0 ? totalOnsetSupervision * (effectiveRates.onsetSupervision || 0) : 0;
      const importedShotTotal = importedShotTotalFromLines > 0 ? importedShotTotalFromLines : (importedReferenceCosts.shotTotal ?? null);
      const importedAssetTotal = importedAssetTotalFromLines > 0 ? importedAssetTotalFromLines : (importedReferenceCosts.assetTotal ?? null);
      const importedOnsetTotal = importedOnsetLegacyTotal > 0 ? importedOnsetLegacyTotal : (importedReferenceCosts.onsetTotal ?? null);
      const importedOverallTotal =
        importedReferenceCosts.grandTotal ??
        ((importedShotTotal ?? 0) + (importedAssetTotal ?? 0) + (importedOnsetTotal ?? 0) || null);
      const calculatedOverallTotal = calculatedShotTotal + calculatedAssetTotal + calculatedOnsetTotal;
      const overallAccuracy = calculateAccuracyMetric(importedOverallTotal, calculatedOverallTotal);
      const diagnostics = buildImportDiagnosticsSummary();
      const comparison = buildImportComparison(
        shotsToImport,
        assetsToImport,
        effectiveRates,
        effectiveAssetRates,
        effectiveSetup,
        importedOnsetTotal,
        calculatedOnsetTotal,
        importedOnsetTotal !== null && importedOnsetTotal !== undefined && effectiveRates.onsetSupervision > 0
          ? importedOnsetTotal / effectiveRates.onsetSupervision
          : totalOnsetSupervision || null,
        totalOnsetSupervision || null,
        shouldBuildDetailedImportReport
      );

      const currentProgress = importProgressRef.current || {
        isOpen: true,
        isComplete: false,
        isPreparing: false,
        phaseLabel: "Finalizing import",
        totalShots,
        processedShots: processedShotsCount,
        totalAssets,
        processedAssets: processedAssetsCount,
        totalThumbnails,
        processedThumbnails: processedThumbnailsCount,
        onsetDays: totalOnsetSupervision,
        bidDate,
        version,
      };
      if (currentProgress) {
        updateImportProgress(prev => prev ? { ...prev, phaseLabel: "Preparing import summary" } : null);
        showImportResult({
          ...currentProgress,
          isComplete: true,
          priceAccuracy: {
            overall: overallAccuracy,
            shots: calculateAccuracyMetric(importedShotTotal, calculatedShotTotal),
            assets: calculateAccuracyMetric(importedAssetTotal, calculatedAssetTotal),
            onset: calculateAccuracyMetric(importedOnsetTotal, calculatedOnsetTotal),
          },
          diagnostics,
          comparison,
          duplicateConflicts: shouldBuildDetailedImportReport
            ? {
                shots: duplicateShotsToResolve,
                assets: duplicateAssetsToResolve,
              }
            : undefined,
        });
      }
      const persistedMappings = parsedDocsData.reduce<Record<string, Record<string, string>>>((acc, { type, typeMappings }) => {
        if (Object.keys(typeMappings || {}).length > 0) {
          acc[type] = typeMappings;
        }
        return acc;
      }, {});
      if (Object.keys(persistedMappings).length > 0) {
        await persistDocumentMappings(persistedMappings, subDocs);
      }
    } catch (error) {
      console.error("Import failed:", error);
      const message = error instanceof Error ? error.message : "Failed to import quote group.";
      if (serverImportApplied) {
        toast.error(`Quote data imported, but a follow-up step failed: ${message}`);
      } else {
        toast.error(message);
      }
      resetImportFeedback();
    } finally {
      onImportStateChange?.(false);
    }
  };

  const handleImport = async (merge: boolean) => {
    if (!importModal) return;

    onImportStateChange?.(true);
    try {
      const { allMappings, rows, allRows, isTransposed, images } = importModal;
      
      // Update learned mappings for all types
      const newLearned = { ...learnedMappings };
      Object.entries(allMappings || {}).forEach(([type, typeMappings]) => {
        if (!newLearned[type]) newLearned[type] = {};
        Object.entries(typeMappings).forEach(([header, field]) => {
          if (header && field) {
            newLearned[type][header.trim().toLowerCase()] = field;
          }
        });
        // Persist to Firestore
        saveMappingPreference(type, newLearned[type]);
      });
      setLearnedMappings(newLearned);

      let totalOnsetSupervision = 0;
      const importedRates: Record<string, number> = {};
      const importedAssetRates: Record<string, number> = {};
      let importedOnsetLegacyTotal = 0;

      // Process only the current type
      const type = importModal.type;
      const typeMappings = allMappings?.[type] || {};

      if (type === 'shots' && projectId && user?.uid && Object.keys(typeMappings).length > 0) {
        const shotImportDocId = importModal.docId;
        const shotImportFileName = importModal.fileName;
        const shotImportSubDocs = importModal.subDocs;
        const finalAllMappings = {
          ...(importModal.allMappings || {}),
          [importModal.type]: mappings,
        };

        setImportModal(prev => prev ? {
          ...prev,
          rows: [],
          allRows: [],
          images: [],
        } : prev);

        updateImportProgress({
          isOpen: true,
          isComplete: false,
          phaseLabel: "Sending Create SHOTS to server",
          totalShots: 0,
          processedShots: 0,
          totalAssets: 0,
          processedAssets: 0,
          totalThumbnails: 0,
          processedThumbnails: 0,
          onsetDays: 0,
          bidDate: null,
          version: null
        });

        const response = await fetch(`/api/projects/${projectId}/import-legacy-shots`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid,
          },
          body: JSON.stringify({
            docId: shotImportDocId,
            typeMappings,
            fileName: shotImportFileName,
            merge,
          }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Failed to import Create SHOTS on the server.");
        }

        const importedShotCount = typeof result.importedCount === 'number'
          ? result.importedCount
          : (Array.isArray(result.shots) ? result.shots.length : 0);

        updateImportProgress(prev => prev ? {
          ...prev,
          phaseLabel: "Applying server-imported shots",
          totalShots: importedShotCount,
          processedShots: importedShotCount,
        } : null);

        if (Array.isArray(result.shots)) {
          onImportShots(result.shots, false);
        }
        if (result.setupUpdates && Object.keys(result.setupUpdates).length > 0) {
          onUpdateSetup(result.setupUpdates);
        }
        if (result.importedRates && Object.keys(result.importedRates).length > 0) {
          onUpdateRates(prev => ({ ...prev, ...result.importedRates }));
        }

        const currentProgress = importProgressRef.current || {
          isOpen: true,
          isComplete: false,
          totalShots: importedShotCount,
          processedShots: importedShotCount,
          totalAssets: 0,
          processedAssets: 0,
          totalThumbnails: 0,
          processedThumbnails: 0,
          onsetDays: result.setupUpdates?.onsetSupervision ?? 0,
          bidDate: result.setupUpdates?.bidDate || null,
          version: result.setupUpdates?.version || null,
        };
        showImportResult({
          ...currentProgress,
          isComplete: true,
          totalShots: importedShotCount,
          processedShots: importedShotCount,
          onsetDays: result.setupUpdates?.onsetSupervision ?? currentProgress.onsetDays ?? 0,
          bidDate: result.setupUpdates?.bidDate || currentProgress.bidDate || null,
          version: result.setupUpdates?.version || currentProgress.version || null,
        });

        const thumbnailHeader = Object.keys(typeMappings).find((header) => typeMappings[header] === 'thumbnail');
        if (thumbnailHeader && shotImportDocId) {
          updateImportProgress(prev => prev ? {
            ...prev,
            phaseLabel: "Applying shot thumbnails",
          } : null);

          const thumbnailResponse = await fetch(`/api/projects/${projectId}/import-legacy-shot-thumbnails`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-user-uid": user.uid,
            },
            body: JSON.stringify({
              docId: shotImportDocId,
              typeMappings,
            }),
          });
          const thumbnailResult = await thumbnailResponse.json();
          if (!thumbnailResponse.ok) {
            throw new Error(thumbnailResult.error || "Failed to import Create SHOTS thumbnails on the server.");
          }

          if (Array.isArray(thumbnailResult.updates) && thumbnailResult.updates.length > 0) {
            await onImportShots(
              thumbnailResult.updates.map((update: { shotCode: string; thumbnail: string }) =>
                buildThumbnailOnlyImportedShot(update.shotCode, update.thumbnail)
              ),
              true
            );
          }

          updateImportProgress(prev => prev ? {
            ...prev,
            totalThumbnails: typeof thumbnailResult.updatedCount === 'number' ? thumbnailResult.updatedCount : 0,
            processedThumbnails: typeof thumbnailResult.updatedCount === 'number' ? thumbnailResult.updatedCount : 0,
          } : null);
        }

        const docsToPersist = shotImportSubDocs
          ? shotImportSubDocs
          : documents.filter((d) => d.name === shotImportFileName);
        if (Object.keys(finalAllMappings).length > 0 && docsToPersist.length > 0) {
          await persistDocumentMappings(finalAllMappings, docsToPersist);
        }

        setImportModal(null);
        setMappings({});
        return;
      }

      // Edge case: Onset Supervision (days) from the row where Shot Code is "Onset Supervision"
      const shotsMappings = allMappings?.['shots'] || {};
      const shotCodeHeader = Object.keys(shotsMappings).find(h => shotsMappings[h] === 'shotCode');
      
      if (shotCodeHeader || allRows.length > 0) {
        totalOnsetSupervision = normalizeOnsetDays(extractLegacyOnsetSupervisionDays(rows, allRows, shotsMappings));
      }

      let totalShots = 0;
      let totalAssets = 0;
      let totalThumbnails = 0;

      if (type === 'shots') {
        const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'shotCode');
        const validRows = rows.filter(r => {
           const code = (codeCol ? (r[codeCol] ?? "") : "").toString().trim().toLowerCase();
           const normalized = code.replace(/[^a-z0-9]/g, '');
           return code && normalized !== 'onsetsupervision' && normalized !== 'onsetvfxsupervision' && normalized !== 'onset';
        });
        totalShots = validRows.length;
        const thumbCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'thumbnail');
        if (thumbCol && images) {
          validRows.forEach(row => {
             const headerIndex = importModal.rawHeaders?.indexOf(thumbCol) ?? -1;
             const targetRow = isTransposed ? headerIndex : row.__nativeRow;
             const targetCol = isTransposed ? row.__nativeRowIndex : headerIndex;
             const img = images.find(img => img.row === targetRow && img.col === targetCol);
             if (img) totalThumbnails++;
          });
        }
      } else if (type === 'assets') {
        const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'assetCode');
        const validRows = rows.filter(r => {
           const code = (codeCol ? (r[codeCol] ?? "") : "").toString().trim().toLowerCase();
           const normalized = code.replace(/[^a-z0-9]/g, '');
           return code && normalized !== 'onsetsupervision' && normalized !== 'onsetvfxsupervision' && normalized !== 'onset';
        });
        totalAssets = validRows.length;
        const thumbCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'thumbnail');
        if (thumbCol && images) {
          validRows.forEach(row => {
             const headerIndex = importModal.rawHeaders?.indexOf(thumbCol) ?? -1;
             const targetRow = isTransposed ? headerIndex : row.__nativeRow;
             const targetCol = isTransposed ? row.__nativeRowIndex : headerIndex;
             const img = images.find(img => img.row === targetRow && img.col === targetCol);
             if (img) totalThumbnails++;
          });
        }
      }

      const shouldProcessImportThumbnails = totalShots + totalAssets <= IMPORT_THUMBNAIL_PROCESSING_MAX_ITEMS;
      if (!shouldProcessImportThumbnails) {
        totalThumbnails = 0;
      }

      let latestImportedReference = setup.topSheet?.importedReference;

      updateImportProgress({
        isOpen: true,
        isComplete: false,
        phaseLabel: `Preparing ${type === 'shots' ? 'shot' : type === 'assets' ? 'asset' : type} import`,
        totalShots,
        processedShots: 0,
        totalAssets,
        processedAssets: 0,
        totalThumbnails,
        processedThumbnails: 0,
        onsetDays: totalOnsetSupervision,
        bidDate: null,
        version: null
      });

      let processedShotsCount = 0;
      let processedAssetsCount = 0;
      let processedThumbnailsCount = 0;
      const shouldResizeImportThumbnails = shouldProcessImportThumbnails && totalThumbnails > 0 && totalThumbnails <= IMPORT_THUMBNAIL_RESIZE_THRESHOLD;
      const shouldBuildDetailedImportReport = totalShots + totalAssets <= IMPORT_DETAILED_REPORT_MAX_ITEMS;
      const duplicateShotCodes = new Set<string>();
      const duplicateAssetCodes = new Set<string>();
      const duplicateShotsToResolve: DuplicateImportShotCandidate[] = [];
      const duplicateAssetsToResolve: DuplicateImportAssetCandidate[] = [];

      if (Object.keys(typeMappings).length > 0 || type === 'top_sheet') {
        if (type === 'shots') {
          updateImportProgress(prev => prev ? {
            ...prev,
            phaseLabel: "Building shot payload",
          } : null);
          const shotsToImport: Shot[] = [];
          const shotsMap = new Map<string, Shot>();
          for (const row of rows) {
            const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'shotCode');
            const rawCode = codeCol ? row[codeCol] : "";
            const code = (rawCode ?? "").toString().trim().toLowerCase();
            
            if (!code) continue;

            const isOnsetRow = code === 'onset supervision' || code === 'onset vfx supervision';
            if (isOnsetRow) {
              const legacyOnsetPrice = extractLegacyLinePrice(row, typeMappings);
              if (legacyOnsetPrice > 0) importedOnsetLegacyTotal += legacyOnsetPrice;
              // We already handled the project-level onset supervision above.
              // We skip adding this as a regular shot to avoid double counting.
              continue;
            }

            if (shotsMap.has(code)) {
              duplicateShotCodes.add(code);
              const duplicateShot: Shot = {
                id: uuidv4(),
                active: true,
                shotCode: code,
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                sceneDescription: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'sceneDescription') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                frames: Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'frames') || ""])),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, comp: 0, roto: 0, prep: 0, gfx: 0,
                  cameraTR: 0, objectTR: 0, rANIM: 0, layout: 0, anim: 0, light: 0, fx: 0
                }
              };
              for (const [header, field] of Object.entries(typeMappings)) {
                if (field === 'status') {
                  const status = normalizeImportedBidStatus(row[header]);
                  if (status) {
                    duplicateShot.status = status as ShotStatus;
                    duplicateShot.active = status !== "Lost" && status !== "Off";
                  }
                } else if (field.startsWith('task_')) {
                  const taskKey = field.replace('task_', '') as keyof Shot['days'];
                  const val = parseNumber(row[header]);
                  if (val > 0) duplicateShot.days[taskKey] = val;
                }
              }
              const reservedShotCodes = new Set<string>([
                ...existingShots.map(shot => normalizeCodeKey(shot.shotCode)),
                ...Array.from(shotsMap.keys()).map(normalizeCodeKey),
                ...duplicateShotsToResolve.map(candidate => normalizeCodeKey(candidate.suggestedCode)),
              ]);
              const suggestedCode = suggestDuplicateShotCode(code, reservedShotCodes);
              duplicateShotsToResolve.push({
                id: duplicateShot.id,
                originalCode: code,
                suggestedCode,
                occurrence: duplicateShotsToResolve.filter(candidate => candidate.originalCode === code).length + 2,
                imported: duplicateShot.legacyPrice ?? null,
                calculated: calculateShotImportCost(duplicateShot, { ...DEFAULT_RATES, ...rates, ...importedRates }, setup),
                description: duplicateShot.description || duplicateShot.sceneDescription || duplicateShot.scopeOfWork,
                payload: duplicateShot,
              });
              continue;
            }

            let shot = shotsMap.get(code);
            let isNewShot = false;
            if (!shot) {
              isNewShot = true;
              shot = {
                id: uuidv4(),
                active: true,
                shotCode: code,
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                sceneDescription: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'sceneDescription') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                frames: Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'frames') || ""])),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, comp: 0, roto: 0, prep: 0, gfx: 0,
                  cameraTR: 0, objectTR: 0, rANIM: 0, layout: 0, anim: 0, light: 0, fx: 0
                }
              };
              shotsMap.set(code, shot);
            }

            for (const [header, field] of Object.entries(typeMappings)) {
              if (field === 'thumbnail' && images && isNewShot && shouldProcessImportThumbnails) {
                const headerIndex = importModal.rawHeaders?.indexOf(header) ?? -1;
                const targetRow = isTransposed ? headerIndex : row.__nativeRow;
                const targetCol = isTransposed ? row.__nativeRowIndex : headerIndex;
                const img = images.find(img => img.row === targetRow && img.col === targetCol);
                if (img) {
                  try {
                    shot.thumbnail = await prepareImportThumbnail(img.dataUrl, shouldResizeImportThumbnails);
                    processedThumbnailsCount++;
                    updateImportProgress(prev => prev ? { ...prev, processedThumbnails: processedThumbnailsCount } : null);
                  } catch (e) {
                    console.error("Failed to crop thumbnail", e);
                    shot.thumbnail = img.dataUrl;
                  }
                }
              } else if (field === 'status') {
                const status = normalizeImportedBidStatus(row[header]);
                if (status) {
                  shot.status = status as ShotStatus;
                  shot.active = status !== "Lost" && status !== "Off";
                }
              } else if (field.startsWith('task_')) {
                const taskKey = field.replace('task_', '') as keyof Shot['days'];
                const val = parseNumber(row[header]);
                if (val > 0) shot.days[taskKey] = val;
              } else if (field.startsWith('shot_rate_')) {
                const rateKey = field.replace('shot_rate_', '');
                const val = parseNumber(row[header]);
                if (val > 0 && !importedRates[rateKey]) {
                  importedRates[rateKey] = val;
                }
              }
            }

            if (isNewShot) {
              shotsToImport.push(shot);
              processedShotsCount++;
              if (processedShotsCount % 5 === 0) {
                updateImportProgress(prev => prev ? {
                  ...prev,
                  phaseLabel: `Building shot payload (${processedShotsCount}/${totalShots || rows.length})`,
                  processedShots: processedShotsCount,
                } : null);
                await new Promise(r => setTimeout(r, 0));
              }
            }
          }
          updateImportProgress(prev => prev ? {
            ...prev,
            phaseLabel: `Shot payload built (${shotsToImport.length} shots)`,
            processedShots: processedShotsCount,
          } : null);
          await new Promise(r => setTimeout(r, 0));
          if (shotsToImport.length > 0) {
            const totalBatches = Math.max(1, Math.ceil(shotsToImport.length / SINGLE_SHEET_SHOT_IMPORT_BATCH_SIZE));
            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
              const batchStart = batchIndex * SINGLE_SHEET_SHOT_IMPORT_BATCH_SIZE;
              const batch = shotsToImport.slice(batchStart, batchStart + SINGLE_SHEET_SHOT_IMPORT_BATCH_SIZE);
              updateImportProgress(prev => prev ? {
                ...prev,
                phaseLabel: `Committing shot batch ${batchIndex + 1}/${totalBatches}`,
                processedShots: Math.min(shotsToImport.length, batchStart + batch.length),
              } : null);
              onImportShots(batch, batchIndex === 0 ? merge : true);
              await new Promise(r => setTimeout(r, 0));
            }
            updateImportProgress(prev => prev ? {
              ...prev,
              phaseLabel: "Shot batches committed",
              processedShots: shotsToImport.length,
            } : null);
          } else {
            updateImportProgress(prev => prev ? {
              ...prev,
              phaseLabel: "No shots to commit",
            } : null);
          }
        } else if (type === 'assets') {
          const assetsToImport: Asset[] = [];
          const assetsMap = new Map<string, Asset>();
          for (const row of rows) {
            const codeCol = Object.keys(typeMappings).find(h => typeMappings[h] === 'assetCode');
            const rawCode = codeCol ? row[codeCol] : "";
            const code = (rawCode ?? "").toString().trim().toLowerCase();
            const normalized = code.replace(/[^a-z0-9]/g, '');

            if (!code) continue;

            const isOnsetRow = normalized === 'onsetsupervision' || normalized === 'onsetvfxsupervision' || normalized === 'onset';
            if (isOnsetRow) {
              const legacyOnsetPrice = extractLegacyLinePrice(row, typeMappings);
              if (legacyOnsetPrice > 0) importedOnsetLegacyTotal += legacyOnsetPrice;
              // Legacy onset days are sourced from Create SHOTS only.
              // Asset imports may still carry a legacy onset price reference,
              // but they must never mutate the project onset-day total.
              continue;
            }

            if (assetsMap.has(code)) {
              duplicateAssetCodes.add(code);
              const duplicateAsset: Asset = {
                id: uuidv4(),
                active: true,
                assetCode: code,
                type: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'type') || ""]),
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, model: 0, rig: 0, retop: 0,
                  cloth: 0, fur: 0, groom: 0, texture: 0, anim: 0, fx: 0, lookdev: 0, photoscan: 0
                }
              };
              for (const [header, field] of Object.entries(typeMappings)) {
                if (field === 'status') {
                  const status = normalizeImportedBidStatus(row[header]);
                  if (status) {
                    duplicateAsset.status = status as ShotStatus;
                    duplicateAsset.active = status !== "Lost" && status !== "Off";
                  }
                } else if (field === 'type') {
                  duplicateAsset.type = sanitizeText(row[header]);
                } else if (field.startsWith('task_')) {
                  const taskKey = field.replace('task_', '') as keyof Asset['days'];
                  const val = parseNumber(row[header]);
                  if (val > 0) duplicateAsset.days[taskKey] = val;
                }
              }
              const reservedAssetCodes = new Set<string>([
                ...existingAssets.map(asset => normalizeCodeKey(asset.assetCode)),
                ...Array.from(assetsMap.keys()).map(normalizeCodeKey),
                ...duplicateAssetsToResolve.map(candidate => normalizeCodeKey(candidate.suggestedCode)),
              ]);
              const suggestedCode = suggestDuplicateAssetCode(code, reservedAssetCodes);
              duplicateAssetsToResolve.push({
                id: duplicateAsset.id,
                originalCode: code,
                suggestedCode,
                occurrence: duplicateAssetsToResolve.filter(candidate => candidate.originalCode === code).length + 2,
                imported: duplicateAsset.legacyPrice ?? null,
                calculated: calculateAssetImportCost(duplicateAsset, { ...DEFAULT_ASSET_RATES, ...assetRates, ...importedAssetRates }, setup),
                description: duplicateAsset.description || duplicateAsset.scopeOfWork,
                payload: duplicateAsset,
              });
              continue;
            }

            let asset = assetsMap.get(code);
            let isNewAsset = false;
            if (!asset) {
              isNewAsset = true;
              asset = {
                id: uuidv4(),
                active: true,
                assetCode: code,
                type: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'type') || ""]),
                status: "Bidding",
                thumbnail: "",
                description: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'description') || ""]),
                scopeOfWork: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'scopeOfWork') || ""]),
                quantity: Math.max(1, Math.round(parseNumber(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'quantity') || ""]))),
                legacyPrice: extractLegacyLinePrice(row, typeMappings) || undefined,
                assumptions: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'assumptions') || ""]),
                internalNotes: sanitizeText(row[Object.keys(typeMappings).find(h => typeMappings[h] === 'internalNotes') || ""]),
                days: {
                  supervisor: 0, concept: 0, dmp: 0, model: 0, rig: 0, retop: 0,
                  cloth: 0, fur: 0, groom: 0, texture: 0, anim: 0, fx: 0, lookdev: 0, photoscan: 0
                }
              };
              assetsMap.set(code, asset);
            }

            for (const [header, field] of Object.entries(typeMappings)) {
              if (field === 'thumbnail' && images && isNewAsset && shouldProcessImportThumbnails) {
                const headerIndex = importModal.rawHeaders?.indexOf(header) ?? -1;
                const targetRow = isTransposed ? headerIndex : row.__nativeRow;
                const targetCol = isTransposed ? row.__nativeRowIndex : headerIndex;
                const img = images.find(img => img.row === targetRow && img.col === targetCol);
                if (img) {
                  try {
                    asset.thumbnail = await prepareImportThumbnail(img.dataUrl, shouldResizeImportThumbnails);
                    processedThumbnailsCount++;
                    updateImportProgress(prev => prev ? { ...prev, processedThumbnails: processedThumbnailsCount } : null);
                  } catch (e) {
                    console.error("Failed to crop thumbnail", e);
                    asset.thumbnail = img.dataUrl;
                  }
                }
              } else if (field === 'status') {
                const status = normalizeImportedBidStatus(row[header]);
                if (status) {
                  asset.status = status as ShotStatus;
                  asset.active = status !== "Lost" && status !== "Off";
                }
              } else if (field === 'type') {
                asset.type = sanitizeText(row[header]);
              } else if (field.startsWith('task_')) {
                const taskKey = field.replace('task_', '') as keyof Asset['days'];
                const val = parseNumber(row[header]);
                if (val > 0) asset.days[taskKey] = val;
              } else if (field.startsWith('asset_rate_')) {
                const rateKey = field.replace('asset_rate_', '');
                const val = parseNumber(row[header]);
                if (val > 0 && !importedAssetRates[rateKey]) {
                  importedAssetRates[rateKey] = val;
                }
              }
            }

            if (isNewAsset) {
              assetsToImport.push(asset);
              processedAssetsCount++;
              if (processedAssetsCount % 5 === 0) {
                updateImportProgress(prev => prev ? { ...prev, processedAssets: processedAssetsCount } : null);
                await new Promise(r => setTimeout(r, 0));
              }
            }
          }
          updateImportProgress(prev => prev ? { ...prev, processedAssets: processedAssetsCount } : null);
          await new Promise(r => setTimeout(r, 0));
          onImportAssets(assetsToImport, merge);
        } else if (type === 'key') {
          extractRatesFromKeyTab(allRows, importedRates, importedAssetRates, typeMappings);
        } else if (type === 'top_sheet') {
          const newTopSheet: TopSheet = { ...(setup.topSheet || {}) };
          const sourceDocumentName = importModal.fileName;
          for (const row of rows) {
            for (const [header, fieldId] of Object.entries(typeMappings)) {
              const val = sanitizeTopSheetMappedValue(fieldId, (row[header] ?? "").toString());
              if (!val) continue;

              if (fieldId === 'ts_quoteName') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { quoteName: val, sourceDocumentName });
              }
              else if (fieldId === 'ts_quoteDate') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { quoteDate: val, sourceDocumentName });
              }
              else if (fieldId === 'ts_version') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { version: val, sourceDocumentName });
              }
              else if (fieldId === 'ts_cost_shotTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { shotTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_assetTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { assetTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_onsetTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { onsetTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_grandTotal') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { grandTotal: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_shotCount') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { shotCount: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_assetCount') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { assetCount: parseNumber(val) } });
              }
              else if (fieldId === 'ts_cost_currency') {
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { sourceDocumentName, costs: { currency: val } });
              }
              else if (fieldId === 'ts_summary') {
                newTopSheet.summary = val;
                newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { summary: val, sourceDocumentName });
              }
              else if (fieldId === 'ts_approach') newTopSheet.approach = val;
              else if (fieldId === 'ts_assumptions') newTopSheet.assumptions = val;
              else if (fieldId === 'ts_clarifications') newTopSheet.clarifications = val;
              else if (fieldId === 'ts_exclusions') newTopSheet.exclusions = val;
              else if (fieldId === 'ts_estimateAssumptions') newTopSheet.estimateAssumptions = val;
              else if (fieldId === 'ts_termsAndConditions') newTopSheet.termsAndConditions = val;

              if (fieldId === 'ts_approach') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { approach: val, sourceDocumentName });
              else if (fieldId === 'ts_assumptions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { assumptions: val, sourceDocumentName });
              else if (fieldId === 'ts_clarifications') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { clarifications: val, sourceDocumentName });
              else if (fieldId === 'ts_exclusions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { exclusions: val, sourceDocumentName });
              else if (fieldId === 'ts_estimateAssumptions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { estimateAssumptions: val, sourceDocumentName });
              else if (fieldId === 'ts_termsAndConditions') newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, { termsAndConditions: val, sourceDocumentName });
            }
          }

          const extractedTopSheet = extractLegacyTopSheetData(allRows, sourceDocumentName);
          if (extractedTopSheet.importedReference) {
            newTopSheet.importedReference = mergeImportedTopSheetReference(newTopSheet, extractedTopSheet.importedReference);
          }
          if (shouldReplaceTopSheetField(newTopSheet.summary, extractedTopSheet.summary, "Summary")) newTopSheet.summary = extractedTopSheet.summary;
          if (shouldReplaceTopSheetField(newTopSheet.approach, extractedTopSheet.approach, "Approach")) newTopSheet.approach = extractedTopSheet.approach;
          if (shouldReplaceTopSheetField(newTopSheet.assumptions, extractedTopSheet.assumptions, "Assumptions")) newTopSheet.assumptions = extractedTopSheet.assumptions;
          if (shouldReplaceTopSheetField(newTopSheet.clarifications, extractedTopSheet.clarifications, "Clarifications")) newTopSheet.clarifications = extractedTopSheet.clarifications;
          if (shouldReplaceTopSheetField(newTopSheet.exclusions, extractedTopSheet.exclusions, "Exclusions")) newTopSheet.exclusions = extractedTopSheet.exclusions;
          if (shouldReplaceTopSheetField(newTopSheet.estimateAssumptions, extractedTopSheet.estimateAssumptions, "Quote Assumptions")) newTopSheet.estimateAssumptions = extractedTopSheet.estimateAssumptions;
          if (shouldReplaceTopSheetField(newTopSheet.termsAndConditions, extractedTopSheet.termsAndConditions, "Terms and Conditions")) newTopSheet.termsAndConditions = extractedTopSheet.termsAndConditions;

          if (onImportTopSheet) {
            onImportTopSheet(newTopSheet);
          } else {
            onUpdateSetup({
              topSheet: newTopSheet
            });
          }
          latestImportedReference = newTopSheet.importedReference;
        }
      }

      // Apply any imported rates
      if (Object.keys(importedRates).length > 0) {
        onUpdateRates(prev => ({ ...prev, ...importedRates }));
      }
      if (Object.keys(importedAssetRates).length > 0) {
        onUpdateAssetRates(prev => ({ ...prev, ...importedAssetRates }));
      }
      const setupUpdates: any = {};
      
      if (totalOnsetSupervision > 0) {
        setupUpdates.onsetSupervision = totalOnsetSupervision;
      } else if (!merge) {
        setupUpdates.onsetSupervision = 0;
      }

      const importedReference = latestImportedReference;

      if (importModal.fileName) {
        const dateMatch = importModal.fileName.match(/(?:^|[^0-9])(\d{4})[-]?(\d{2})[-]?(\d{2})(?:[^0-9]|$)/);
        if (dateMatch) {
          const formattedDate = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
          setupUpdates.bidDate = formattedDate;
          updateImportProgress(prev => prev ? { ...prev, bidDate: formattedDate } : null);
        }
      }

      const resolvedVersion = resolveImportedVersion({
        explicitVersion: importedReference?.version,
        quoteName: importedReference?.quoteName,
        fileName: importModal.fileName,
      });
      if (resolvedVersion) {
        setupUpdates.version = resolvedVersion;
        updateImportProgress(prev => prev ? { ...prev, version: resolvedVersion } : null);
      }

      if (Object.keys(setupUpdates).length > 0) {
        onUpdateSetup(prev => ({ ...prev, ...setupUpdates }));
      }

      const currentProgress = importProgressRef.current || {
        isOpen: true,
        isComplete: false,
        totalShots,
        processedShots: processedShotsCount,
        totalAssets,
        processedAssets: processedAssetsCount,
        totalThumbnails,
        processedThumbnails: processedThumbnailsCount,
        onsetDays: totalOnsetSupervision,
        bidDate: setupUpdates.bidDate || null,
        version: setupUpdates.version || null,
      };
      if (currentProgress) {
        showImportResult({
          ...currentProgress,
          isComplete: true,
          duplicateConflicts: shouldBuildDetailedImportReport
            ? {
                shots: duplicateShotsToResolve,
                assets: duplicateAssetsToResolve,
              }
            : undefined,
        });
      }
      const finalAllMappings = {
        ...(importModal.allMappings || {}),
        [importModal.type]: mappings,
      };
      const docsToPersist = documents.filter((d) => d.name === importModal.fileName);
      if (Object.keys(finalAllMappings).length > 0 && docsToPersist.length > 0) {
        await persistDocumentMappings(finalAllMappings, docsToPersist);
      }
      setImportModal(null);
      setMappings({});
    } catch (error) {
      console.error("Import failed:", error);
      toast.error("Failed to import data. Please check your CSV format and mappings.");
      resetImportFeedback();
    } finally {
      onImportStateChange?.(false);
    }
  };

  const closeImportModal = () => {
    if (importModal) {
      const finalAllMappings = {
        ...(importModal.allMappings || {}),
        [importModal.type]: mappings
      };
      const docsToPersist = importModal.subDocs
        ? importModal.subDocs
        : documents.filter((d) => d.name === importModal.fileName);

      void persistDocumentMappings(finalAllMappings, docsToPersist);
    }
    setImportModal(null);
  };

  const removeDocument = async (id: string) => {
    if (onRemoveDocument) {
      await onRemoveDocument(id);
    } else {
      setDocuments((prev) => {
        const doc = prev.find(d => d.id === id);
        if (doc && doc.url && doc.url.startsWith('blob:')) URL.revokeObjectURL(doc.url);
        return prev.filter(d => d.id !== id);
      });
    }
  };

  const removeGroup = async (parentId: string) => {
    const docsToRemove = documents.filter(d => d.parentDriveFileId === parentId);
    for (const doc of docsToRemove) {
      await removeDocument(doc.id);
    }
    toast.success(`Removed all sheets from the group`);
  };

  const toggleDocExpanded = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDocs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleUseExtractedData = async (file: any, action: 'shots' | 'assets' | 'top_sheet' | 'onset' | 'shooting_schedule') => {
    const doc = documents.find(d => d.driveFileId === file.id);
    if (!doc || !doc.analysisResult) {
      toast.error("No analysis data found for this document.");
      return;
    }

    const result = doc.analysisResult;

    if (action === 'shooting_schedule') {
      if (!projectId) {
        toast.error("Open a project before importing a shooting schedule.");
        return;
      }
      if (!doc.analysisResult?.shooting_schedule_data) {
        toast.error("No shooting schedule data found in this analysis yet.");
        return;
      }
      const draft = buildShootingScheduleImportDraft({
        projectId,
        documentId: doc.id,
        fileName: doc.name,
        fileUrl: doc.url,
        importedByUserId: user?.uid || null,
        analysisResult: doc.analysisResult,
        scenes: existingScenes,
        sequences: existingSequences,
      });
      setScheduleImportDraft(draft);
      return;
    }

    if (action === 'shots' || action === 'assets' || action === 'onset') {
      const lineItems = result.legacy_bid_csv_data?.line_items || [];
      if (lineItems.length === 0) {
        toast.error("No line items found in extracted data.");
        return;
      }

      // Convert line items to rows for the import modal
      const headers = Object.keys(lineItems[0]);
      const rows = lineItems;
      const allRows = [headers, ...lineItems.map((item: any) => headers.map((h: string) => String(item[h] || "")))];

      resetImportFeedback();
      setImportModal({
        isOpen: true,
        type: action,
        headers,
        rows,
        allRows,
        fileName: file.name
      });

      // Try to pre-fill mappings
      const newMappings: Record<string, string> = {};
      const saved = await loadMappingPreference(action);
      const learned = { ...(learnedMappings[action] || {}), ...(saved || {}) };

      headers.forEach(header => {
        const normalizedHeader = header.trim().toLowerCase();
        if (learned[normalizedHeader]) {
          newMappings[header] = learned[normalizedHeader];
        } else {
          const mappedId = autoMapHeader(header, action);
          if (mappedId) {
            if (!Object.values(newMappings).includes(mappedId)) {
              newMappings[header] = mappedId;
            }
          }
        }
      });
      setMappings(newMappings);
    } else if (action === 'top_sheet') {
      const ts = result.top_sheet_data;
      if (!ts) {
        toast.error("No top sheet data found in extracted data.");
        return;
      }
      
      const newTopSheet: TopSheet = {
        ...(setup.topSheet || {}),
        ...(ts.summary ? { summary: ts.summary } : {}),
        ...(ts.approach ? { approach: ts.approach } : {}),
        ...(ts.assumptions ? { assumptions: ts.assumptions } : {}),
        ...(ts.clarifications ? { clarifications: ts.clarifications } : {}),
        ...(ts.exclusions ? { exclusions: ts.exclusions } : {}),
        ...(ts.estimate_assumptions ? { estimateAssumptions: ts.estimate_assumptions } : {}),
        ...(ts.terms_and_conditions ? { termsAndConditions: ts.terms_and_conditions } : {}),
        importedReference: mergeImportedTopSheetReference(setup.topSheet, {
          sourceDocumentName: doc.name,
          quoteName: ts.quote_name || result.legacy_bid_csv_data?.estimate_name || result.global_fields?.project_title || "",
          quoteDate: ts.quote_date || result.legacy_bid_csv_data?.estimate_date || "",
          version: ts.version || result.legacy_bid_csv_data?.bid_version || "",
          summary: ts.summary || "",
          approach: ts.approach || "",
          assumptions: ts.assumptions || "",
          clarifications: ts.clarifications || "",
          exclusions: ts.exclusions || "",
          estimateAssumptions: ts.estimate_assumptions || "",
          termsAndConditions: ts.terms_and_conditions || "",
          costs: {
            shotTotal: ts.costs?.shot_total ?? result.legacy_bid_csv_data?.totals?.shot_total,
            assetTotal: ts.costs?.asset_total ?? result.legacy_bid_csv_data?.totals?.asset_total,
            onsetTotal: ts.costs?.onset_total,
            grandTotal: ts.costs?.grand_total ?? result.legacy_bid_csv_data?.totals?.grand_total,
            shotCount: ts.costs?.shot_count,
            assetCount: ts.costs?.asset_count,
            currency: ts.costs?.currency ?? result.legacy_bid_csv_data?.currency
          }
        })
      };

      if (onImportTopSheet) {
        onImportTopSheet(newTopSheet);
      } else {
        onUpdateSetup({
          ...setup,
          topSheet: newTopSheet
        });
      }
      toast.success("Top sheet details imported successfully.");
    }
  };

  useEffect(() => {
    if (isStartingQueue.current || !analysisQueue || analysisQueue.length === 0) return;

    const availableSlots = MAX_CONCURRENT_ANALYSES - runningAnalysisIds.length;
    if (availableSlots <= 0) return;

    const docsToStart = analysisQueue.slice(0, availableSlots);
    if (docsToStart.length === 0) return;

    isStartingQueue.current = true;
    setAnalysisQueue((prev) => prev.slice(docsToStart.length));
    setRunningAnalysisIds((prev) => [...prev, ...docsToStart.map((doc) => doc.id)]);
    isStartingQueue.current = false;

    docsToStart.forEach((queuedDoc, index) => {
      const startWithDelay = async () => {
        if (index > 0) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        try {
          await runAnalysis(queuedDoc);
        } catch (err) {
          console.error("Queue analysis failed:", err);
        } finally {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          setRunningAnalysisIds((prev) => {
            const nextRunning = prev.filter((id) => id !== queuedDoc.id);
            backgroundQueueCompletedCountRef.current += 1;

            if (
              nextRunning.length === 0 &&
              analysisQueueRef.current.length === 0 &&
              backgroundQueueStartedCountRef.current > 0 &&
              backgroundQueueCompletedCountRef.current >= backgroundQueueStartedCountRef.current
            ) {
              const completedCount = backgroundQueueStartedCountRef.current;
              backgroundQueueStartedCountRef.current = 0;
              backgroundQueueCompletedCountRef.current = 0;
              window.setTimeout(() => {
                toast.success(
                  `Background analysis finished for ${completedCount} document${completedCount === 1 ? "" : "s"}.`
                );
              }, 0);
            }

            return nextRunning;
          });
        }
      };

      void startWithDelay();
    });
  }, [analysisQueue, runningAnalysisIds]);

  const analyzeDocument = (doc: ProjectDocument) => {
    queueDocumentsForAnalysis([doc], 'single');
  };

  const runAnalysis = async (doc: ProjectDocument) => {
    if (!process.env.GEMINI_API_KEY) {
      toast.error("Gemini API Key is missing.");
      return;
    }

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });

      const encodeTextToBase64 = (input: string) => {
        const bytes = new TextEncoder().encode(input);
        let binary = "";
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
      };

      const buildWorksheetAnalysisText = (worksheet: any) => {
        const lines: string[] = [];
        lines.push(`Sheet: ${worksheet.name}`);
        worksheet.eachRow((row: any, rowNumber: number) => {
          const values = Array.isArray(row.values) ? row.values.slice(1) : [];
          const normalized = values.map((value: any) => {
            if (value === null || value === undefined) return "";
            if (typeof value === "object") {
              if (Array.isArray(value.richText)) return value.richText.map((part: any) => part.text).join("");
              if ("result" in value && value.result !== undefined) return String(value.result);
              if ("text" in value && value.text) return String(value.text);
            }
            return String(value);
          });
          if (normalized.some((cell) => cell.trim() !== "")) {
            lines.push(`Row ${rowNumber}: ${normalized.join(" | ")}`);
          }
        });
        return lines.join("\n");
      };

      const spreadsheetToAnalysisPayload = async () => {
        let arrayBuffer: ArrayBuffer | null = null;
        if (doc.file) {
          arrayBuffer = await doc.file.arrayBuffer();
        } else if (doc.content?.startsWith("data:")) {
          const response = await fetch(doc.content);
          arrayBuffer = await response.arrayBuffer();
        } else if (doc.driveFileId) {
          const url = doc.sheetId !== undefined
            ? `/api/drive/download/${doc.driveFileId}?gid=${doc.sheetId}`
            : `/api/drive/download/${doc.driveFileId}`;
          const res = await fetch(url, {
            headers: { "x-user-uid": user?.uid || "" }
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed to download spreadsheet for analysis");
          if (String(data.mimeType || "").toLowerCase() === "application/json") {
            const decoded = atob(String(data.content || "").split(",")[1] || "");
            const parsed = JSON.parse(decoded);
            const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
            const text = rows
              .map((row: any[], index: number) => `Row ${index + 1}: ${(Array.isArray(row) ? row : []).map((cell) => String(cell ?? "")).join(" | ")}`)
              .join("\n");
            return {
              mimeType: "text/plain",
              base64: encodeTextToBase64(text),
            };
          }
          const contentUrl = String(data.content || "");
          if (!contentUrl.startsWith("data:")) {
            throw new Error("Spreadsheet content is not available for analysis");
          }
          const response = await fetch(contentUrl);
          arrayBuffer = await response.arrayBuffer();
        } else if (doc.url && doc.url.startsWith("https://")) {
          const response = await fetch(doc.url);
          arrayBuffer = await response.arrayBuffer();
        }

        if (!arrayBuffer) {
          throw new Error("Spreadsheet content is not available for analysis");
        }

        const ExcelJSModule = await import("exceljs");
        const ExcelJS = ExcelJSModule.default || ExcelJSModule;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheetText = workbook.worksheets
          .map((worksheet: any) => buildWorksheetAnalysisText(worksheet))
          .filter(Boolean)
          .join("\n\n");

        return {
          mimeType: "text/plain",
          base64: encodeTextToBase64(worksheetText),
        };
      };

      let mimeType = doc.type;
      if (doc.name.toLowerCase().endsWith('.pdf')) {
        mimeType = 'application/pdf';
      } else if (doc.name.toLowerCase().endsWith('.txt')) {
        mimeType = 'text/plain';
      }

      let base64 = "";
      const isSpreadsheetBinary =
        doc.name.toLowerCase().endsWith('.xlsx') ||
        doc.name.toLowerCase().endsWith('.xls') ||
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        mimeType === 'application/vnd.ms-excel';

      if (isSpreadsheetBinary) {
        const spreadsheetPayload = await spreadsheetToAnalysisPayload();
        mimeType = spreadsheetPayload.mimeType;
        base64 = spreadsheetPayload.base64;
      } else if (doc.file) {
        base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(doc.file!);
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = error => reject(error);
        });
      } else if (doc.content) {
        base64 = doc.content.split(',')[1];
      } else if (doc.driveFileId) {
        // Fetch content on-the-fly from Drive API
        const url = doc.sheetId !== undefined 
          ? `/api/drive/download/${doc.driveFileId}?gid=${doc.sheetId}` 
          : `/api/drive/download/${doc.driveFileId}`;
        const res = await fetch(url, {
          headers: { "x-user-uid": user?.uid || "" }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to download file for analysis");
        base64 = data.content.split(',')[1];
      } else if (doc.url && doc.url.startsWith('https://')) {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = error => reject(error);
        });
      } else {
        throw new Error("Document content is not available for analysis.");
      }

      // Token limit protection: Gemini 3 Flash has a 1M token limit.
      // 1 token is ~4 chars for text. Base64 is 33% larger.
      // 3.5M chars of base64 is a safe upper bound (~2.6M binary chars -> ~650k tokens).
      const MAX_BASE64_SIZE = 3500000; 
      if (base64.length > MAX_BASE64_SIZE) {
        if (mimeType === 'text/plain' || mimeType === 'text/csv' || mimeType === 'application/json') {
          // For text files, we can safely truncate
          const decoded = atob(base64);
          const truncated = decoded.substring(0, Math.floor(MAX_BASE64_SIZE * 0.75));
          base64 = btoa(truncated);
          console.warn(`Document ${doc.name} truncated for analysis due to size.`);
        } else {
          // For binary files like PDF, we can't easily truncate.
          // We'll try to send it but if it fails, we'll catch it.
          // However, if it's obviously too big (e.g. > 10MB), we should probably stop early.
          if (base64.length > 10000000) {
            throw new Error("Document is too large for AI analysis. Please try a smaller file.");
          }
        }
      }
      
      const likelyScriptDocument = isLikelyScriptDocument(doc, {
        detected_document_type: doc.analysisResult?.detected_document_type,
      });
      const likelyShootingScheduleDocument = isLikelyShootingScheduleDocument(doc, {
        detected_document_type: doc.analysisResult?.detected_document_type,
      });
      const analysisPrompt = likelyScriptDocument
        ? SCRIPT_DOCUMENT_PROMPT
        : likelyShootingScheduleDocument
          ? SHOOTING_SCHEDULE_PROMPT
          : DOCUMENT_ANALYSIS_PROMPT;
      const analysisSchema = likelyScriptDocument
        ? SCRIPT_DOCUMENT_SCHEMA
        : likelyShootingScheduleDocument
          ? SHOOTING_SCHEDULE_SCHEMA
          : DOCUMENT_ANALYSIS_SCHEMA;

      let response;
      let retries = 0;
      const maxRetries = 5;
      
      while (retries <= maxRetries) {
        try {
          response = await ai.models.generateContent({
            model: "gemini-3.1-flash-lite-preview",
            contents: [
              {
                role: "user",
                parts: [
                  {
                    inlineData: {
                      mimeType: mimeType,
                      data: base64,
                    },
                  },
                  { text: analysisPrompt },
                ],
              }
            ],
            config: {
              responseMimeType: "application/json",
              responseSchema: analysisSchema as any
            }
          });
          break;
        } catch (err: any) {
          const isQuotaError = err.message?.includes("429") || 
                               err.status === "RESOURCE_EXHAUSTED" || 
                               JSON.stringify(err).includes("429") ||
                               JSON.stringify(err).includes("RESOURCE_EXHAUSTED");
          
          const isSpendingCap = err.message?.includes('spending cap') || JSON.stringify(err).includes('spending cap');
          if (isSpendingCap) {
            throw new Error("Gemini API spending cap reached. Please check your Google Cloud billing or use manual mapping.");
          }

          if (isQuotaError && retries < maxRetries) {
            retries++;
            const delay = Math.pow(2, retries) * 2000;
            toast.info(`Rate limit hit. Retrying in ${delay/1000}s... (Attempt ${retries}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          if (err.message?.includes("exceeds the maximum number of tokens") || 
              JSON.stringify(err).includes("exceeds the maximum number of tokens")) {
            throw new Error("Document is too large for AI analysis. Please try a smaller file.");
          }
          
          throw err;
        }
      }

      if (!response) throw new Error("No response from Gemini API");

      let parsed;
      try {
        parsed = parseGeminiJsonResponse(response.text || "{}");
      } catch (e) {
        console.error("Failed to parse Gemini response as JSON:", response.text);
        if (isLikelyScriptDocument(doc, null)) {
          parsed = {
            detected_document_type: "script",
            document_summary: "",
            script_data: {},
            uncertainties: ["Primary Gemini response was not valid JSON."],
          };
        } else if (isLikelyShootingScheduleDocument(doc, null)) {
          parsed = {
            detected_document_type: "shooting_schedule",
            document_summary: "",
            shooting_schedule_data: {},
            uncertainties: ["Primary Gemini response was not valid JSON."],
          };
        } else {
          throw new Error("Invalid JSON response from Gemini API");
        }
      }

      if (!likelyScriptDocument && isLikelyScriptDocument(doc, parsed) && !hasUsefulScriptData(parsed)) {
        try {
          const scriptResponse = await ai.models.generateContent({
            model: "gemini-3.1-flash-lite-preview",
            contents: [
              {
                role: "user",
                parts: [
                  {
                    inlineData: {
                      mimeType: mimeType,
                      data: base64,
                    },
                  },
                  { text: SCRIPT_DOCUMENT_PROMPT },
                ],
              }
            ],
            config: {
              responseMimeType: "application/json",
              responseSchema: SCRIPT_DOCUMENT_SCHEMA as any
            }
          });

          const scriptParsed = parseGeminiJsonResponse(scriptResponse.text || "{}");

          parsed = {
            ...parsed,
            detected_document_type: parsed?.detected_document_type || "script",
            document_summary: scriptParsed?.document_summary || parsed?.document_summary,
            uncertainties: [
              ...(Array.isArray(parsed?.uncertainties) ? parsed.uncertainties : []),
              ...(Array.isArray(scriptParsed?.uncertainties) ? scriptParsed.uncertainties : []),
            ],
            script_data: scriptParsed?.script_data || parsed?.script_data,
          };
        } catch (scriptError) {
          console.error("Script-specific fallback analysis failed:", scriptError);
        }
      }

      if (isLikelyScriptDocument(doc, parsed) && parsed?.script_data) {
        const previousScriptData = doc.analysisResult?.script_data;
        const mergedScriptData = sanitizeScriptData(
          mergeScriptData(previousScriptData, parsed.script_data)
        );

        parsed = {
          ...parsed,
          detected_document_type: parsed?.detected_document_type || "script",
          script_data: {
            ...mergedScriptData,
            scenes: hydrateScriptScenes(mergedScriptData),
          },
        };

        const missingSceneNumbers = getMissingScriptSceneNumbers(parsed.script_data);

        if (missingSceneNumbers.length > 0) {
          const recoveredScenes: any[] = [];

          for (const batch of chunkSceneNumbers(missingSceneNumbers, 6)) {
            try {
              const batchResponse = await ai.models.generateContent({
                model: "gemini-3.1-flash-lite-preview",
                contents: [
                  {
                    role: "user",
                    parts: [
                      {
                        inlineData: {
                          mimeType,
                          data: base64,
                        },
                      },
                      { text: buildScriptSceneBatchPrompt(batch) },
                    ],
                  },
                ],
                config: {
                  responseMimeType: "application/json",
                  responseSchema: SCRIPT_SCENE_BATCH_SCHEMA as any,
                },
              });

              const batchParsed = parseGeminiJsonResponse(batchResponse.text || "{}");
              if (Array.isArray(batchParsed?.scenes)) {
                recoveredScenes.push(...batchParsed.scenes);
              }
            } catch (batchError) {
              console.error("Script scene batch extraction failed:", batchError);
            }
          }

          if (recoveredScenes.length > 0) {
            parsed = {
              ...parsed,
              script_data: {
                ...parsed.script_data,
                scenes: mergeScriptScenes(parsed.script_data?.scenes || [], recoveredScenes),
              },
            };
          }
        }
      }

      if (isLikelyShootingScheduleDocument(doc, parsed) && !hasUsefulShootingScheduleData(parsed)) {
        try {
          const scheduleRecoveryResponse = await ai.models.generateContent({
            model: "gemini-3.1-flash-lite-preview",
            contents: [
              {
                role: "user",
                parts: [
                  {
                    inlineData: {
                      mimeType,
                      data: base64,
                    },
                  },
                  { text: SHOOTING_SCHEDULE_RECOVERY_PROMPT },
                ],
              },
            ],
            config: {
              responseMimeType: "application/json",
              responseSchema: SHOOTING_SCHEDULE_RECOVERY_SCHEMA as any,
            },
          });

          const recoveredSchedule = parseGeminiJsonResponse(scheduleRecoveryResponse.text || "{}");

          parsed = {
            ...parsed,
            detected_document_type: recoveredSchedule?.detected_document_type || parsed?.detected_document_type || "shooting_schedule",
            document_summary: recoveredSchedule?.document_summary || parsed?.document_summary,
            uncertainties: [
              ...(Array.isArray(parsed?.uncertainties) ? parsed.uncertainties : []),
              ...(Array.isArray(recoveredSchedule?.uncertainties) ? recoveredSchedule.uncertainties : []),
            ],
            shooting_schedule_data: {
              ...(parsed?.shooting_schedule_data || {}),
              ...(recoveredSchedule?.shooting_schedule_data || {}),
              warnings: [
                ...(Array.isArray(parsed?.shooting_schedule_data?.warnings) ? parsed.shooting_schedule_data.warnings : []),
                ...(Array.isArray(recoveredSchedule?.shooting_schedule_data?.warnings) ? recoveredSchedule.shooting_schedule_data.warnings : []),
              ],
            },
          };
        } catch (scheduleRecoveryError) {
          console.error("Shooting schedule recovery analysis failed:", scheduleRecoveryError);
        }
      }

      if (isLikelyShootingScheduleDocument(doc, parsed) && !hasUsefulShootingScheduleCastData(parsed)) {
        try {
          const scheduleCastRecoveryResponse = await ai.models.generateContent({
            model: "gemini-3.1-flash-lite-preview",
            contents: [
              {
                role: "user",
                parts: [
                  {
                    inlineData: {
                      mimeType,
                      data: base64,
                    },
                  },
                  { text: SHOOTING_SCHEDULE_CAST_RECOVERY_PROMPT },
                ],
              },
            ],
            config: {
              responseMimeType: "application/json",
              responseSchema: SHOOTING_SCHEDULE_CAST_RECOVERY_SCHEMA as any,
            },
          });

          const recoveredCastSchedule = parseGeminiJsonResponse(scheduleCastRecoveryResponse.text || "{}");
          const existingCastMembers = Array.isArray(parsed?.shooting_schedule_data?.cast_members)
            ? parsed.shooting_schedule_data.cast_members
            : [];
          const recoveredCastMembers = Array.isArray(recoveredCastSchedule?.shooting_schedule_data?.cast_members)
            ? recoveredCastSchedule.shooting_schedule_data.cast_members
            : [];

          parsed = {
            ...parsed,
            detected_document_type:
              recoveredCastSchedule?.detected_document_type || parsed?.detected_document_type || "shooting_schedule",
            document_summary: recoveredCastSchedule?.document_summary || parsed?.document_summary,
            uncertainties: [
              ...(Array.isArray(parsed?.uncertainties) ? parsed.uncertainties : []),
              ...(Array.isArray(recoveredCastSchedule?.uncertainties) ? recoveredCastSchedule.uncertainties : []),
            ],
            shooting_schedule_data: {
              ...(parsed?.shooting_schedule_data || {}),
              warnings: [
                ...(Array.isArray(parsed?.shooting_schedule_data?.warnings) ? parsed.shooting_schedule_data.warnings : []),
                ...(Array.isArray(recoveredCastSchedule?.shooting_schedule_data?.warnings)
                  ? recoveredCastSchedule.shooting_schedule_data.warnings
                  : []),
              ],
              cast_members: mergeShootingScheduleCastMembers(existingCastMembers, recoveredCastMembers),
            },
          };
        } catch (scheduleCastRecoveryError) {
          console.error("Shooting schedule cast recovery analysis failed:", scheduleCastRecoveryError);
        }
      }

      const updatedDoc: ProjectDocument = { 
        ...doc, 
        analysisResult: parsed,
        summary: parsed.document_summary,
        episode: parsed.global_fields?.episode_identifiers?.[0] || parsed.script_data?.episode_number || parsed.post_schedule_data?.episodes?.[0]?.episode_number,
        sceneCount: parsed.script_data?.scene_count,
        extractedData: [
          { prompt: "Detected Type", answer: parsed.detected_document_type },
          { prompt: "Classification Confidence", answer: String(parsed.classification_confidence) },
          ...(parsed.global_fields?.project_title ? [{ prompt: "Project Title", answer: parsed.global_fields.project_title }] : []),
          ...(parsed.global_fields?.show_title ? [{ prompt: "Show Title", answer: parsed.global_fields.show_title }] : []),
          ...(parsed.global_fields?.series_title ? [{ prompt: "Series Title", answer: parsed.global_fields.series_title }] : []),
          ...(parsed.global_fields?.production_company ? [{ prompt: "Production Company", answer: parsed.global_fields.production_company }] : []),
          ...(parsed.global_fields?.client ? [{ prompt: "Client", answer: parsed.global_fields.client }] : []),
          ...(parsed.global_fields?.vendor ? [{ prompt: "Vendor", answer: parsed.global_fields.vendor }] : []),
          ...(parsed.script_data?.characters?.length ? [{ prompt: "Characters", answer: parsed.script_data.characters.join(", ") }] : []),
          ...(parsed.script_data?.locations?.length ? [{ prompt: "Locations", answer: parsed.script_data.locations.join(", ") }] : []),
          ...(parsed.script_data?.scene_count ? [{ prompt: "Scene Count", answer: String(parsed.script_data.scene_count) }] : []),
          ...(parsed.script_data?.vfx_indicators?.length ? [{ prompt: "VFX Indicators", answer: parsed.script_data.vfx_indicators.join(", ") }] : []),
          ...(parsed.document_summary ? [{ prompt: "Summary", answer: parsed.document_summary }] : []),
        ],
        // Clear old fields if they existed
        vfxScenes: undefined
      };

      if (updatedDoc.driveFileId) {
        delete updatedDoc.content;
      }

      if (onSaveDocument) {
        await onSaveDocument(updatedDoc);
      } else {
        setDocuments(prev => prev.map(d => d.id === doc.id ? updatedDoc : d));
      }

      if (onPersistScriptStructure && updatedDoc.analysisResult?.script_data) {
        await onPersistScriptStructure(updatedDoc);
      }

      if (isLikelyScriptDocument(doc, parsed) && !hasUsefulScriptData(parsed)) {
        toast.warning("Analysis completed, but no scene breakdown was extracted from this script.");
      }

      if (isLikelyShootingScheduleDocument(doc, parsed) && !hasUsefulShootingScheduleData(parsed)) {
        toast.warning("Schedule recognised, but the detailed shoot-day breakdown was not extracted yet.");
      }
      
      // Auto-expand the document to show results
      // setExpandedDocs(prev => ({ ...prev, [doc.id]: true }));

    } catch (error: any) {
      console.error("Error analyzing document:", error);
      const docLabel = doc.sheetName || doc.name || "document";
      let errorMessage = `Failed to analyze ${docLabel}. See console for details.`;
      const isQuotaError = error.message?.includes("429") || 
                           error.status === "RESOURCE_EXHAUSTED" || 
                           JSON.stringify(error).includes("429") ||
                           JSON.stringify(error).includes("RESOURCE_EXHAUSTED");
      
      const isSpendingCapError = error.message?.includes("spending cap") ||
                                 JSON.stringify(error).includes("spending cap");
      
      if (isSpendingCapError) {
        errorMessage = `Failed to analyze ${docLabel}: your Google Cloud project has exceeded its spending cap. Please check your billing settings in the Google Cloud Console.`;
      } else if (isQuotaError) {
        errorMessage = `Failed to analyze ${docLabel}: Gemini API quota exceeded. Please wait a moment and try again.`;
      } else {
        const detail =
          String(
            error?.message ||
            error?.details ||
            error?.error ||
            ""
          ).trim();
        if (detail) {
          errorMessage = `Failed to analyze ${docLabel}: ${detail.slice(0, 220)}`;
        }
      }
      toast.error(errorMessage);
    } finally {
      // Running state is managed by the shared worker pool.
    }
  };

  if (!uiVisible) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between mb-6 shadow-sm shrink-0">
        <SectionPillHeader
          icon={FileText}
          title="Documents"
          description="Manage project documents, scripts, and bid files"
        />
        
        <div className="flex items-start gap-3">
          <button
            onClick={async () => {
              if (driveConnected) {
                toast.success("Google Drive is connected");
                return;
              }
              try {
                const res = await fetch(`/api/auth/google/url?uid=${user?.uid}`);
                const data = await res.json();
                if (!res.ok) {
                  toast.error(data.error || "Failed to get auth URL");
                  return;
                }
                const authWindow = window.open(data.url, 'google_auth', 'width=600,height=700');
                
                const handleMessage = (event: MessageEvent) => {
                  if (event.origin !== window.location.origin) return;
                  if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
                    window.removeEventListener('message', handleMessage);
                    window.location.reload();
                  }
                };
                window.addEventListener('message', handleMessage);
              } catch (error) {
                console.error("Failed to get auth URL", error);
                toast.error("Failed to connect to Google Drive");
              }
            }}
            className={cn(
              "px-3 py-1.5 rounded-lg transition-colors text-xs font-medium flex items-center gap-2",
              driveConnected 
                ? "bg-transparent border border-slate-200 text-slate-500" 
                : "border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 shadow-sm"
            )}
          >
            {driveConnected ? (
              <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Drive" className="w-3.5 h-3.5" referrerPolicy="no-referrer" />
            ) : (
              <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Drive" className="w-3.5 h-3.5 grayscale opacity-70" referrerPolicy="no-referrer" />
            )}
            {driveConnected 
              ? "Google Drive Connected"
              : "Connect Google Drive"}
          </button>
        </div>
      </div>

      {importResult && (
        <ImportResultReport
          key={`inline-import-result-${importResult.bidDate || "no-date"}-${importResult.version || "no-version"}-${importResult.totalShots}-${importResult.totalAssets}`}
          result={importResult}
          onApplyDuplicateResolutions={applyDuplicateImportResolutions}
          onClose={() => {
            persistImportResult(null);
          }}
        />
      )}

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-6 min-w-0">
      {/* ... rest of the component ... */}
      {user && isAdmin && driveConnected && !setup.driveFolderId && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Drive" className="mt-0.5 h-5 w-5" referrerPolicy="no-referrer" />
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Google Drive setup moved to Project Settings</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Create or link the project drive from <span className="font-semibold">Project Settings → Integrations</span> so new and legacy projects use the same connection flow.
              </p>
            </div>
          </div>
        </div>
      )}
      {driveConnected && (
        <>
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                <button 
                  onClick={() => {
                    const targetUrl = currentFolderId 
                      ? `https://drive.google.com/drive/folders/${currentFolderId}`
                      : setup.driveFolderId 
                        ? `https://drive.google.com/drive/folders/${setup.driveFolderId}`
                        : 'https://drive.google.com/drive/my-drive';
                    window.open(targetUrl, '_blank');
                  }}
                  className="px-4 py-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-900/60 rounded-md shadow-sm flex items-center gap-1.5 hover:bg-indigo-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Drive" className="w-3.5 h-3.5" referrerPolicy="no-referrer" />
                  {setup.driveFolderName || "Google Drive"}
                  <ExternalLink className="w-3 h-3 ml-1 text-indigo-400" />
                </button>
              </div>
              <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text" 
                  placeholder="Search Drive..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-[11px] border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                />
              </div>
              <button
                onClick={handleCreateManagementFolders}
                disabled={isCreatingManagementFolders || !setup.driveFolderId}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {isCreatingManagementFolders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Folder className="w-3.5 h-3.5" />}
                Create Management Folders
              </button>
              <input
                type="file"
                ref={driveUploadRef}
                onChange={handleUploadToDrive}
                className="hidden"
              />
              <button
                onClick={() => driveUploadRef.current?.click()}
                disabled={isUploadingToDrive || !currentFolderId}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {isUploadingToDrive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload to Drive
              </button>
              <button
                onClick={() => setIsNewFolderModalOpen(true)}
                disabled={isCreatingFolder || !currentFolderId}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {isCreatingFolder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
                New Folder
              </button>
            </div>
          </div>
          </div>

	          <div
	            className={cn(
	              "relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[300px] flex flex-col transition-colors",
	              isDriveDropActive ? "border-[#ffcc01] bg-amber-50/30" : ""
	            )}
	            onDragEnter={handleDriveDragEnter}
	            onDragOver={handleDriveDragOver}
	            onDragLeave={handleDriveDragLeave}
	            onDrop={handleDriveDrop}
	          >
	            {isDriveDropActive ? (
	              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-amber-50/90 text-center">
	                <div className="rounded-xl border border-dashed border-[#ffcc01] bg-white/95 dark:bg-slate-900/95 px-6 py-4 shadow-sm">
	                  <Upload className="mx-auto h-6 w-6 text-[#d4a500]" />
	                  <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Drop file to upload</p>
	                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
	                    It will be added to {folderPath[folderPath.length - 1]?.name || setup.driveFolderName || "the current project folder"}.
	                  </p>
	                </div>
	              </div>
	            ) : null}
	            {/* Breadcrumb inside the white box */}
            <div className="px-3 py-1.5 bg-slate-50/50 dark:bg-slate-800/70 border-bottom border-slate-100 dark:border-slate-800 flex items-center gap-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">
              <button 
                onClick={() => handleBreadcrumbClick(-1)}
                className="hover:text-indigo-600 transition-colors flex items-center gap-1.5"
              >
                <Folder className="w-3.5 h-3.5" />
                Project Root
              </button>
              {folderPath.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <ChevronRight className="w-3 h-3 text-slate-400" />
                  <button 
                    onClick={() => handleBreadcrumbClick(index)}
                    className="hover:text-indigo-600 transition-colors truncate max-w-[150px] flex items-center gap-1.5"
                  >
                    <Folder 
                      className="w-3 h-3" 
                      style={{ color: folder.color ? (folder.color.startsWith('#') ? folder.color : `#${folder.color}`) : '#6366f1' }} 
                      fill={folder.color ? (folder.color.startsWith('#') ? `${folder.color}20` : `#${folder.color}20`) : '#6366f120'}
                    />
                    {folder.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
        <div className="flex-1 overflow-auto">
          {!setup.driveFolderId ? (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Drive" className="w-8 h-8 grayscale opacity-50" referrerPolicy="no-referrer" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">Google Drive not set up</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                Click the "Setup Google Drive" button in the project header to create your project folder structure.
              </p>
            </div>
          ) : loadingDrive ? (
            <div className="h-full flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : driveLoadError ? (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center">
              <div className="w-12 h-12 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mb-3">
                <AlertCircle className="w-6 h-6 text-amber-500" />
              </div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">Google Drive folder couldn’t load</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-4">{driveLoadError}</p>
              <button
                onClick={() => currentFolderId && fetchDriveFiles(currentFolderId)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <RefreshCw className="w-4 h-4" />
                Try again
              </button>
            </div>
          ) : visibleDriveFiles.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center">
              <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                <FileText className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {searchQuery ? "No files match your search" : "This folder is empty"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {visibleDriveFiles.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase())).map((file) => {
                const linkedDoc = documents.find(d => d.driveFileId === file.id);
                const analyzedDoc = linkedDoc?.analysisResult ? linkedDoc : null;
                const isWorking = pullingFileId === file.id || (linkedDoc ? isDocRunningAnalysis(linkedDoc.id) : false);

                return (
                <div 
                  key={file.id}
                  onClick={() => file.mimeType === 'application/vnd.google-apps.folder' ? handleFolderClick(file) : window.open(file.webViewLink, '_blank')}
                  className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      {file.mimeType === 'application/vnd.google-apps.folder' ? (
                        <Folder 
                          className="w-4 h-4" 
                          style={{ color: file.folderColorRgb ? `#${file.folderColorRgb}` : '#6366f1' }} 
                          fill={file.folderColorRgb ? `#${file.folderColorRgb}20` : '#6366f120'}
                        />
                      ) : (
                        <img src={file.iconLink} alt="" className="w-4 h-4" referrerPolicy="no-referrer" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors">{file.name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Modified {new Date(file.modifiedTime).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className={cn(
                    "flex items-center gap-2 transition-opacity",
                    analyzedDoc || isWorking
                      ? "opacity-100" 
                      : "opacity-0 group-hover:opacity-100"
                  )}>
                    {file.mimeType !== 'application/vnd.google-apps.folder' && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const doc = analyzedDoc;
                          if (!doc) return null;
                          
                          const type = doc.analysisResult?.detected_document_type;
                          const episode = doc.episode;
                          
                          const typeLabels: Record<string, string> = {
                            'quote': 'Quote',
                            'bid': 'Bid',
                            'script': 'Script',
                            'shooting_schedule': 'Shooting Schedule',
                            'post_schedule': 'Post Schedule',
                            'dood': 'DOODs',
                            'legacy_bid_csv': 'Bid CSV'
                          };
                          
                          const label = typeLabels[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : null);
                          
                          return (
                            <div className="flex items-center gap-1.5 mr-1">
                              {type === 'script' && episode && (
                                <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold rounded border border-slate-200 dark:border-slate-700 uppercase tracking-tight">
                                  {formatEpisodeBadgeValue(episode)}
                                </span>
                              )}
                              {label && (
                                <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded border border-indigo-100 uppercase tracking-tight">
                                  {label}
                                </span>
                              )}
                              <div className="relative">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActionMenuFileId(actionMenuFileId === file.id ? null : file.id);
                                  }}
                                  className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded transition-colors"
                                  title="Use extracted data"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                                {actionMenuFileId === file.id && (
                                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50">
                                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                                      Import Actions
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUseExtractedData(file, 'shots');
                                        setActionMenuFileId(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-800 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center gap-2"
                                    >
                                      <Film className="w-3 h-3" />
                                      Import Shots
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUseExtractedData(file, 'assets');
                                        setActionMenuFileId(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-800 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center gap-2"
                                    >
                                      <LayoutGrid className="w-3 h-3" />
                                      Import Assets
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUseExtractedData(file, 'top_sheet');
                                        setActionMenuFileId(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-800 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center gap-2"
                                    >
                                      <FileText className="w-3 h-3" />
                                      Import Top Sheet
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUseExtractedData(file, 'onset');
                                        setActionMenuFileId(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-800 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center gap-2"
                                    >
                                      <Calendar className="w-3 h-3" />
                                      Import Onset Days/Rates
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUseExtractedData(file, 'shooting_schedule');
                                        setActionMenuFileId(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-800 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center gap-2"
                                    >
                                      <Calendar className="w-3 h-3" />
                                      Import Shooting Schedule
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePullDriveFile(file);
                          }}
                          disabled={pullingFileId === file.id}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="Pull into app"
                        >
                          {pullingFileId === file.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download
                              className={cn(
                                "w-4 h-4",
                                Boolean(linkedDoc)
                                  ? "text-emerald-500"
                                  : "text-slate-400"
                              )}
                            />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {(documents?.length || 0) > 0 && (
        <div className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-slate-800">Project Documents</h3>
              {totalBackgroundAnalysisCount > 0 && (
                <div className="inline-flex items-center gap-2 rounded-md border border-indigo-100 dark:border-indigo-900/60 bg-indigo-50 dark:bg-indigo-950/30 px-2.5 py-1 text-[11px] font-medium text-indigo-700 dark:text-indigo-200">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Scanning in background
                  {runningAnalysisIds.length ? ` • ${runningAnalysisIds.length} running` : ""}
                  {analysisQueue.length ? ` • ${analysisQueue.length} queued` : ""}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => queueDocumentsForAnalysis(docsMissingAnalysis, 'bulk')}
                disabled={docsMissingAnalysis.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2 text-xs font-semibold text-indigo-700 dark:text-indigo-200 shadow-sm transition-colors hover:bg-indigo-100 dark:hover:bg-indigo-950/50 disabled:cursor-not-allowed disabled:border-slate-200 dark:disabled:border-slate-700 disabled:bg-slate-50 dark:disabled:bg-slate-900 disabled:text-slate-400 dark:disabled:text-slate-500"
                title={docsMissingAnalysis.length > 0 ? "Queue all unanalysed documents for background scanning" : "All supported documents already have analysis"}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Scan Missing
                {docsMissingAnalysis.length > 0 && (
                    <span className="rounded-md bg-white/80 dark:bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700 dark:text-indigo-200">
                      {docsMissingAnalysis.length}
                    </span>
                  )}
              </button>
              <button
                onClick={() => setDocumentSort((current) => current === "imported_desc" ? "name_asc" : "imported_desc")}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 shadow-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
                title="Toggle document sort"
              >
                <ArrowUpDown className="h-3.5 w-3.5" />
                {getDocumentSortLabel()}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {(() => {
              // Group documents by parentDriveFileId
              const groupedDocs: Record<string, ProjectDocument[]> = {};
              const standaloneDocs: ProjectDocument[] = [];

              documents.forEach(doc => {
                if (doc.parentDriveFileId) {
                  if (!groupedDocs[doc.parentDriveFileId]) groupedDocs[doc.parentDriveFileId] = [];
                  groupedDocs[doc.parentDriveFileId].push(doc);
                } else {
                  standaloneDocs.push(doc);
                }
              });

              const sortedStandaloneDocs = sortDocuments(standaloneDocs);
              const sortedGroupedEntries = Object.entries(groupedDocs)
                .map(([parentId, docs]) => [parentId, sortDocuments(docs)] as const)
                .sort(([, docsA], [, docsB]) => {
                  if (documentSort === "name_asc") {
                    const aName = docsA[0]?.name || "";
                    const bName = docsB[0]?.name || "";
                    return aName.localeCompare(bName);
                  }

                  return getDocumentSortValue(docsB[0]) - getDocumentSortValue(docsA[0]);
                });

              // Create a combined list where grouped docs are represented by their parent
              const renderDoc = (doc: ProjectDocument, isSubDoc = false) => {
              const hasExtractedData = doc.analysisResult || doc.extractedData || doc.summary || doc.vfxScenes;
              const isExpanded = expandedDocs[doc.id];
              const isCSV = doc.type === 'text/csv' || doc.name.toLowerCase().endsWith('.csv') || (doc.type === 'application/json' && !!doc.driveFileId);
              const detectedBadge = getDetectedDocBadge(doc, { isSubDoc });
              const DetectedIcon = detectedBadge.Icon;
              const isAnalyzingThisDoc = isDocRunningAnalysis(doc.id);
              const isQueuedForAnalysis = isDocQueuedForAnalysis(doc.id);
              const canAnalyzeThisDoc = supportsAiAnalysis(doc);

              return (
                <div key={doc.id} className={cn(
                  "flex flex-col rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-all",
                  isSubDoc
                    ? "ml-6 bg-amber-50/70 dark:bg-amber-950/20 border-amber-100 dark:border-amber-900/40"
                    : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                )}>
                  <div 
                    className={cn(
                      "flex relative group cursor-pointer",
                      "h-14 w-full"
                    )}
                    onClick={(e) => hasExtractedData && toggleDocExpanded(doc.id, e)}
                  >
                    <div className={cn(
                      "p-3 flex-1 flex min-w-0 flex-row items-center justify-between py-0"
                    )}>
                      <div className={cn(
                        "flex min-w-0 flex-1 flex-row items-center gap-3"
                      )}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center shadow-sm shrink-0",
                            detectedBadge.tileClass
                          )}>
                            <DetectedIcon className={cn("w-5 h-5", detectedBadge.iconClass)} />
                          </div>
                          <h3 className={cn(
                            "text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight",
                            "truncate"
                          )}>
                            {isSubDoc ? (doc.sheetName || doc.name) : doc.name}
                            {doc.episode && doc.type !== "shooting_schedule" && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-600 inline-block align-middle">
                                {formatEpisodeBadgeValue(doc.episode)}
                              </span>
                            )}
                          </h3>
                        </div>
                        <p className={cn(
                          "flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs truncate",
                          "mb-0"
                        )}>
                          <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap", detectedBadge.pillClass)}>
                            <DetectedIcon className={cn("w-3 h-3", detectedBadge.iconClass)} />
                            {detectedBadge.label}
                          </span>
                          {doc.type} {doc.importedAt && `• Imported ${new Date(doc.importedAt).toLocaleString()}`}
                        </p>
                      </div>
                      
                      <div className={cn(
                        "flex items-center gap-2",
                        "ml-auto"
                      )}>
                        {doc.sceneCount !== undefined && (
                          <span className="px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium whitespace-nowrap">
                            {doc.sceneCount} Scenes
                          </span>
                        )}
                        
                        <div className="flex items-center space-x-2 shrink-0">
                          {!doc.type.startsWith('video/') && hasExtractedData && (
                            <button
                              onClick={(e) => toggleDocExpanded(doc.id, e)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              {isExpanded ? "Hide Analysis" : "View Analysis"}
                            </button>
                          )}
                          {isCSV && (doc.file || doc.content || doc.driveFileId) && (
                            <div className="flex items-center gap-1">
                              {(() => {
                                const { label, type } = getDocTypeInfo(doc);
                                const ActionIcon = type === 'top_sheet' ? Download : Settings2;
                                return (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openDocumentMappings(doc, type as any); }}
                                    className="flex items-center px-3 py-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-xs font-medium transition-colors"
                                  >
                                    <ActionIcon className="w-4 h-4 mr-2" /> {label}
                                  </button>
                                );
                              })()}
                            </div>
                          )}

                          {canAnalyzeThisDoc && (
                            <button
                              onClick={(e) => { e.stopPropagation(); analyzeDocument(doc); }}
                              disabled={isAnalyzingThisDoc || isQueuedForAnalysis}
                              className={cn(
                                "p-1.5 rounded-lg transition-colors disabled:opacity-50",
                                isQueuedForAnalysis
                                  ? "text-amber-500 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30"
                                  : doc.analysisResult
                                    ? "text-emerald-500 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                                    : "text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-slate-800"
                              )}
                              title={
                                isAnalyzingThisDoc
                                  ? "Analyzing now"
                                  : isQueuedForAnalysis
                                    ? "Queued for background analysis"
                                    : doc.analysisResult
                                      ? "Re-analyze with AI"
                                      : "Analyze with AI"
                              }
                            >
                              {isAnalyzingThisDoc ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isQueuedForAnalysis ? (
                                <History className="w-4 h-4" />
                              ) : (
                                <Sparkles className={cn("w-4 h-4", doc.analysisResult ? "text-emerald-500 fill-emerald-500/20" : "text-slate-400")} />
                              )}
                            </button>
                          )}
                          
                          {isSubDoc && (() => {
                            const status = getMappingStatus(doc);
                            return (
                              <div className="p-1.5 relative" title={status.title}>
                                <Database className="w-4 h-4 text-slate-200" />
                                <div 
                                  className="absolute top-1.5 left-1.5 overflow-hidden"
                                  style={{ clipPath: `inset(${100 - (status.percentage * 100)}% 0 0 0)` }}
                                >
                                  <Database className={cn("w-4 h-4", status.color)} />
                                </div>
                              </div>
                            );
                          })()}

                          <button
                            onClick={(e) => { e.stopPropagation(); removeDocument(doc.id); }}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {!isSubDoc && doc.thumbnail && (
                      <div className={cn(
                        "relative bg-slate-100 dark:bg-slate-800 flex-shrink-0 flex items-center justify-center",
                        "w-14 h-14 border-r border-slate-200 dark:border-slate-800"
                      )}>
                        <img 
                          src={doc.thumbnail} 
                          alt="Thumbnail" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>

                  {/* Expanded Content Area */}
                  {isExpanded && (hasExtractedData || doc.analysisResult) && (
                    <div className="border-t border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950/60">
                      {doc.analysisResult ? (
                        <DocumentAnalysisView result={doc.analysisResult} />
                      ) : doc.extractedData ? (
                        <div className="space-y-4">
                          {doc.extractedData.map((item, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                              <h5 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center">
                                <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                                {item.prompt}
                              </h5>
                              <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{item.answer}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Fallback for older documents */}
                          {doc.summary && (
                            <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                              <h5 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center">
                                <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                                Episode Overview
                              </h5>
                              <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{doc.summary}</div>
                            </div>
                          )}
                          {doc.vfxScenes && (
                            <div className="bg-indigo-50 dark:bg-indigo-950/30 p-4 rounded-lg border border-indigo-100 dark:border-indigo-900/60 shadow-sm">
                              <h5 className="text-sm font-bold text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
                                <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                                VFX Scenes Extraction
                              </h5>
                              <div className="text-sm text-indigo-900 dark:text-indigo-100 whitespace-pre-wrap">{doc.vfxScenes}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              };

              // First, render standalone docs
              const standaloneElements = sortedStandaloneDocs.map(doc => renderDoc(doc));

              // Then, render grouped docs
              const groupedElements = sortedGroupedEntries.map(([parentId, subDocs]) => {
                const isExpanded = expandedDocs[`group-${parentId}`];
                const parentName = subDocs[0].name.includes(' - ') 
                  ? subDocs[0].name.split(' - ').slice(0, -1).join(' - ') 
                  : subDocs[0].name;

                return (
                  <div key={`group-${parentId}`} className="flex flex-col gap-2">
                    <div 
                      onClick={() => setExpandedDocs(prev => ({ ...prev, [`group-${parentId}`]: !prev[`group-${parentId}`] }))}
                      className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-center shadow-sm">
                          <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{parentName}</h3>
                            {(() => {
                              const groupBadge = getDetectedDocBadge(subDocs[0], { isGroupedParent: true });
                              const GroupIcon = groupBadge.Icon;
                              return (
                                <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap", groupBadge.pillClass)}>
                                  <GroupIcon className={cn("w-3 h-3", groupBadge.iconClass)} />
                                  {groupBadge.label}
                                </span>
                              );
                            })()}
                          </div>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider">{(subDocs?.length || 0)} Sheets Imported</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGroupImportAction(subDocs, false);
                          }}
                          className="flex items-center px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 rounded-lg text-xs font-medium transition-colors"
                        >
                          <Download className="w-4 h-4 mr-2" /> Import Data
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGroupImportAction(subDocs, true);
                          }}
                          className="flex items-center px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-950/50 rounded-lg text-xs font-medium transition-colors"
                        >
                          <Plus className="w-4 h-4 mr-2" /> Merge Data
                        </button>
                        <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-200 bg-white dark:bg-slate-900 px-2 py-1 rounded-full shadow-sm border border-indigo-100 dark:border-indigo-900/60">
                          {isExpanded ? "Hide Sheets" : "Show Sheets"}
                        </span>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-indigo-600" /> : <ChevronRight className="w-4 h-4 text-indigo-600" />}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeGroup(parentId); }}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2"
                          title="Remove all sheets in this group"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="flex flex-col gap-2">
                        {subDocs.map(doc => renderDoc(doc, true))}
                      </div>
                    )}
                  </div>
                );
              });

              return [...standaloneElements, ...groupedElements];
            })()}
          </div>
        </div>
      )}
        </div>

        <aside className="hidden xl:block xl:justify-self-end xl:w-full xl:max-w-[380px]">
          <div className="sticky top-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Document Intelligence</div>
            {documentInsights.chips.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {documentInsights.chips.map((chip) => {
                  const ChipIcon = chip.icon;
                  return (
                    <span key={chip.label} className={cn("inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em]", chip.className)}>
                      <ChipIcon className="h-3 w-3" />
                      {chip.label}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                Add documents from Drive or upload new ones to start building document intelligence.
              </div>
            )}
            {documentInsights.summaryRows.length > 0 && (
              <div className="mt-4 space-y-2">
                {documentInsights.summaryRows.map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-3 border-t border-slate-100 dark:border-slate-800 pt-2 first:border-t-0 first:pt-0">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">{row.label}</span>
                    <span className="text-right text-[12px] font-medium leading-relaxed text-slate-600 dark:text-slate-200">{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {scheduleImportDraft && (
        <ShootingScheduleImportModal
          draft={scheduleImportDraft}
          onClose={() => setScheduleImportDraft(null)}
          onChange={setScheduleImportDraft}
          onConfirm={() => {
            if (onImportShootingSchedule && scheduleImportDraft) {
              onImportShootingSchedule({
                ...scheduleImportDraft,
                status: "confirmed",
                updatedAt: new Date().toISOString(),
              });
            } else {
              toast.error("Project schedule import is not available here.");
            }
            setScheduleImportDraft(null);
          }}
        />
      )}

      {/* CSV Import Modal */}
      {importModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-800">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-950/40 rounded-xl flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {importModal.type === 'top_sheet'
                      ? 'Review Top Sheet'
                      : `Import ${importModal.type.charAt(0).toUpperCase() + importModal.type.slice(1)}`}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{importModal.fileName}</p>
                </div>
              </div>
              <button onClick={closeImportModal} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-400 dark:text-slate-500">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {importModal.isLoadingSheet ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                  <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">Scanning Document...</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
                    Extracting data, analyzing structure, and preparing columns for mapping. This may take a moment for large files.
                  </p>
                </div>
              ) : importModal.type === 'top_sheet' ? (
                <div className="space-y-6">
                  {(() => {
                    const preview = getLegacyTopSheetPreview(importModal.allRows, importModal.fileName);
                    const getStatusMeta = (status: "found" | "empty" | "ignored" | "not_found") => {
                      if (status === "found") {
                        return {
                          icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
                          label: "Extracted",
                          tone: "text-emerald-700 bg-emerald-50 border-emerald-200"
                        };
                      }
                      if (status === "ignored") {
                        return {
                          icon: <AlertCircle className="w-4 h-4 text-amber-500" />,
                          label: "Ignored placeholder",
                          tone: "text-amber-700 bg-amber-50 border-amber-200"
                        };
                      }
                      if (status === "empty") {
                        return {
                          icon: <AlertCircle className="w-4 h-4 text-slate-400" />,
                          label: "Empty",
                          tone: "text-slate-600 bg-slate-50 border-slate-200"
                        };
                      }
                      return {
                        icon: <AlertCircle className="w-4 h-4 text-slate-400" />,
                        label: "Not found",
                        tone: "text-slate-600 bg-slate-50 border-slate-200"
                      };
                    };

                    return (
                      <>
                        <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex gap-3">
                          <FileText className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-bold text-sky-800">Legacy Top Sheet Extraction Preview</h4>
                            <p className="text-xs text-sky-700 mt-1">
                              This sheet uses row-based extraction rather than column mappings. Review the captured sections below before importing the quote.
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {preview.sections.map((section) => {
                            const status = getStatusMeta(section.status);
                            return (
                              <div key={section.label} className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
                                  <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{section.label}</div>
                                  <div className={cn("inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-medium", status.tone)}>
                                    {status.icon}
                                    {status.label}
                                  </div>
                                </div>
                                <div className="p-4">
                                  <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words min-h-[2.5rem]">
                                    {section.value || <span className="text-slate-400 dark:text-slate-500 italic">No usable content captured.</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-800/60">
                            <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Imported Cost Reference</h3>
                          </div>
                          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Shot Total</div>
                              <div className="text-sm text-slate-800 dark:text-slate-100">{preview.importedReference.costs?.shotTotal !== undefined ? `£${preview.importedReference.costs.shotTotal.toLocaleString()}` : "—"}</div>
                            </div>
                            <div>
                              <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Asset Total</div>
                              <div className="text-sm text-slate-800 dark:text-slate-100">{preview.importedReference.costs?.assetTotal !== undefined ? `£${preview.importedReference.costs.assetTotal.toLocaleString()}` : "—"}</div>
                            </div>
                            <div>
                              <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Onset Total</div>
                              <div className="text-sm text-slate-800 dark:text-slate-100">{preview.importedReference.costs?.onsetTotal !== undefined ? `£${preview.importedReference.costs.onsetTotal.toLocaleString()}` : "—"}</div>
                            </div>
                            <div>
                              <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Grand Total</div>
                              <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">{preview.importedReference.costs?.grandTotal !== undefined ? `£${preview.importedReference.costs.grandTotal.toLocaleString()}` : "—"}</div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row gap-4">
                      <div className="flex gap-3 flex-1">
                        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-bold text-amber-800">Map CSV Columns</h4>
                          <p className="text-xs text-amber-700 mt-1">
                            We've detected this as <strong>{importModal.type.replace('_', ' ').charAt(0).toUpperCase() + importModal.type.replace('_', ' ').slice(1)}</strong> data. 
                            Please verify the column mappings below. The app will learn these for future imports.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 shrink-0">
                        <button
                          onClick={() => {
                            const newMappings: Record<string, string> = { ...mappings };
                            const learned = learnedMappings[importModal.type] || {};
                            
                            importModal.headers.forEach((header, index) => {
                              if (!header) return;
                              let mappedId = autoMapHeader(header, importModal.type, index);
                              if (!mappedId && learned[header.trim().toLowerCase()]) {
                                mappedId = learned[header.trim().toLowerCase()];
                              }
                              if (mappedId && !Object.values(newMappings).includes(mappedId)) {
                                newMappings[header] = mappedId;
                              }
                            });
                            
                            setMappings(newMappings);
                            toast.success("Applied automatic mappings based on rules and history.");
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center shadow-sm border bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-900/60 hover:bg-indigo-50 dark:hover:bg-slate-800"
                        >
                          <Wand2 className="w-3 h-3 mr-1.5" />
                          Auto Map Columns
                        </button>
                        <button
                          onClick={() => {
                            setMappings({});
                            setLearnedMappings(prev => ({ ...prev, [importModal.type]: {} }));
                            saveMappingPreference(importModal.type, {});
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center shadow-sm border bg-white dark:bg-slate-900 text-red-600 dark:text-red-300 border-red-200 dark:border-red-900/60 hover:bg-red-50 dark:hover:bg-slate-800"
                        >
                          <Trash2 className="w-3 h-3 mr-1.5" />
                          Clear Mappings
                        </button>
                      </div>
                    </div>
                  </div>
                {importModal.type === 'key' && (
                  <div className="flex border-b border-slate-200 dark:border-slate-800 mb-4">
                    <button
                      onClick={() => setActiveRateTab('shots')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeRateTab === 'shots' 
                          ? 'border-indigo-500 text-indigo-600' 
                          : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-700'
                      }`}
                    >
                      Shot Rates
                    </button>
                    <button
                      onClick={() => setActiveRateTab('assets')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeRateTab === 'assets' 
                          ? 'border-indigo-500 text-indigo-600' 
                          : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-700'
                      }`}
                    >
                      Asset Rates
                    </button>
                  </div>
                )}
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-800/50 flex items-center justify-between">
                    <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                      <Table2 className="w-4 h-4" />
                      {importModal.type === 'key' 
                        ? (activeRateTab === 'shots' ? 'Data Preview (Rows 1 & 2)' : 'Data Preview (Rows 5 & 6)')
                        : 'Data Preview (First 3 Rows)'}
                    </h3>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                      {importModal.type === 'key' ? '2 total rows detected' : `${importModal.rows.length} total rows detected`}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px] border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/70">
                          {(importModal.type === 'key' 
                            ? importModal.headers.filter(h => activeRateTab === 'shots' ? h.startsWith('Shot Rate:') : h.startsWith('Asset Rate:'))
                            : importModal.headers
                          ).map((h, i) => {
                            const mappedFieldId = mappings[h];
                            const allFields = [
                              ...SHOT_FIELDS, ...ASSET_FIELDS, ...ONSET_FIELDS, 
                              ...SHOT_RATE_FIELDS, ...ASSET_RATE_FIELDS, ...TOP_SHEET_FIELDS
                            ];
                            const field = allFields.find(f => f.id === mappedFieldId);
                            
                            return (
                              <th key={i} className="px-3 py-2 text-left border-b border-slate-200 dark:border-slate-800 whitespace-nowrap">
                                <div className="font-bold text-slate-500 dark:text-slate-300">{h}</div>
                                {field ? (
                                  <div className="text-[9px] text-indigo-600 dark:text-indigo-300 font-medium mt-0.5 flex items-center gap-1">
                                    <CheckCircle2 className="w-2.5 h-2.5" />
                                    Maps to: {field.label}
                                  </div>
                                ) : (
                                  <div className="text-[9px] text-slate-400 dark:text-slate-500 font-normal mt-0.5 italic">
                                    Not mapped
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {importModal.rows.slice(0, 3).map((row, i) => (
                          <tr key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                            {(importModal.type === 'key' 
                              ? importModal.headers.filter(h => activeRateTab === 'shots' ? h.startsWith('Shot Rate:') : h.startsWith('Asset Rate:'))
                              : importModal.headers
                            ).map((h, j) => {
                              const val = row[h];
                              const mappedFieldId = mappings[h];
                              
                              // Handle thumbnail preview in the main table
                              if (mappedFieldId === 'thumbnail' && importModal.images) {
                                const headerIndex = importModal.rawHeaders?.indexOf(h) ?? -1;
                                const targetRow = importModal.isTransposed ? headerIndex : row.__nativeRow;
                                const targetCol = importModal.isTransposed ? row.__nativeRowIndex : headerIndex;
                                const img = importModal.images.find(img => img.row === targetRow && img.col === targetCol);
                                if (img) {
                                  return (
                                    <td key={j} className="px-3 py-2">
                                      <img src={img.dataUrl} alt="Preview" className="w-10 h-6 object-cover rounded bg-slate-100 dark:bg-slate-800" />
                                    </td>
                                  );
                                }
                              }

                              return (
                                <td key={j} className={cn(
                                  "px-3 py-2 whitespace-nowrap max-w-[150px] truncate",
                                  mappedFieldId ? "text-indigo-700 dark:text-indigo-300 font-medium bg-indigo-50/30 dark:bg-indigo-950/20" : "text-slate-600 dark:text-slate-300"
                                )}>
                                  {String(val || "")}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {importModal.type === 'shots' && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="text-[10px] font-bold text-indigo-900 uppercase tracking-wider">Onset Supervision Detection</h4>
                        <p className="text-xs text-indigo-700">
                          {(() => {
                            const shotCodeHeader = Object.keys(mappings).find(h => mappings[h] === 'shotCode');
                            const onsetDaysHeader = Object.keys(mappings).find(h => mappings[h] === 'project_onsetSupervision');
                            
                            if (!shotCodeHeader) return "Map 'Shot Code' to enable detection";
                            
                            const onsetRow = importModal.rows.find(row => {
                              const code = (row[shotCodeHeader] ?? "").toString().trim().toLowerCase();
                              const normalized = code.replace(/[^a-z0-9]/g, '');
                              return normalized === 'onsetsupervision' || normalized === 'onsetvfxsupervision' || normalized === 'onset';
                            });

                            if (!onsetRow) return "No 'Onset Supervision' row found in Shot Code column";
                            
                            let days = 0;
                            if (onsetDaysHeader) {
                              days = parseNumber(onsetRow[onsetDaysHeader]);
                            } else {
                              const qtyHeader = Object.keys(mappings).find(h => mappings[h] === 'quantity');
                              if (qtyHeader) {
                                days = parseNumber(onsetRow[qtyHeader]);
                              }
                            }
                            
                            return `Detected: ${days} days (from row with code 'Onset Supervision')`;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(importModal.type === 'shots' ? SHOT_FIELDS : 
                    importModal.type === 'assets' ? ASSET_FIELDS : 
                    importModal.type === 'onset' ? ONSET_FIELDS : 
                    importModal.type === 'key' ? (activeRateTab === 'shots' ? SHOT_RATE_FIELDS : ASSET_RATE_FIELDS) :
                    TOP_SHEET_FIELDS).map((field) => (
                    <div key={field.id} className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        {field.label} {('required' in field && field.required) && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={Object.keys(mappings).find(h => mappings[h] === field.id) || ""}
                        onChange={(e) => {
                          const header = e.target.value;
                          const newMappings = { ...mappings };
                          // Remove old mapping for this field
                          Object.keys(newMappings).forEach(h => {
                            if (newMappings[h] === field.id) delete newMappings[h];
                          });
                          if (header) {
                            newMappings[header] = field.id;
                            // Learn this mapping immediately
                            setLearnedMappings(prev => ({
                              ...prev,
                              [importModal.type]: {
                                ...prev[importModal.type],
                                [header.trim().toLowerCase()]: field.id
                              }
                            }));
                            // Persist to Firestore
                            saveMappingPreference(importModal.type, {
                              ...(learnedMappings[importModal.type] || {}),
                              [header.trim().toLowerCase()]: field.id
                            });
                          }
                          setMappings(newMappings);
                          
                          // Update allMappings in importModal state
                          setImportModal(prev => prev ? ({
                            ...prev,
                            allMappings: {
                              ...(prev.allMappings || {}),
                              [prev.type]: newMappings
                            }
                          }) : null);
                        }}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none"
                      >
                        <option value="">-- Select Column --</option>
                        {importModal.headers.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Duplicate Warning */}
                {(importModal.type === 'shots' || importModal.type === 'assets') && importModal.rows.some(row => {
                  const codeField = importModal.type === 'shots' ? 'shotCode' : 'assetCode';
                  const header = Object.keys(mappings).find(h => mappings[h] === codeField);
                  if (!header) return false;
                  const code = row[header];
                  return importModal.type === 'shots' 
                    ? existingShots.some(s => s.shotCode === code)
                    : existingAssets.some(a => a.assetCode === code);
                }) && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                    <div>
                      <h4 className="text-sm font-bold text-red-800">Potential Duplicates Detected</h4>
                      <p className="text-xs text-red-700 mt-1">
                        Some {importModal.type === 'shots' ? 'shots' : 'assets'} in this CSV already exist in the project. 
                        Choose whether to merge them or create new entries.
                      </p>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end items-center">
              <div className="flex gap-3">
                <button
                  onClick={closeImportModal}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sheet Selection Modal */}
      {sheetSelection && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
                  <Table2 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Select Sheets to Import</h2>
                  <p className="text-xs text-slate-500">{sheetSelection.fileName}</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setSheetSelection(null);
                  setSelectedSheetIds([]);
                }} 
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-2 border-b border-slate-100 bg-white flex items-center justify-between">
              <button 
                onClick={() => {
                  if ((selectedSheetIds?.length || 0) === (sheetSelection.sheets?.length || 0)) {
                    setSelectedSheetIds([]);
                  } else {
                    setSelectedSheetIds(sheetSelection.sheets.map(s => s.id));
                  }
                }}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                {(selectedSheetIds?.length || 0) === (sheetSelection.sheets?.length || 0) ? "Deselect All" : "Select All"}
              </button>
              <span className="text-xs text-slate-500">{(selectedSheetIds?.length || 0)} selected</span>
            </div>
            
            <div className="p-4 max-h-[400px] overflow-y-auto">
              <div className="grid grid-cols-1 gap-2">
                {sheetSelection.sheets.map((sheet) => (
                  <button
                    key={sheet.id}
                    onClick={() => {
                      setSelectedSheetIds(prev => 
                        prev.includes(sheet.id) 
                          ? prev.filter(id => id !== sheet.id) 
                          : [...prev, sheet.id]
                      );
                    }}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-xl border transition-all text-left group",
                      selectedSheetIds.includes(sheet.id)
                        ? "border-indigo-300 bg-indigo-50"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                        selectedSheetIds.includes(sheet.id)
                          ? "bg-indigo-100 text-indigo-600"
                          : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
                      )}>
                        <FileSpreadsheet className="w-4 h-4" />
                      </div>
                      <span className={cn(
                        "text-sm font-medium transition-colors",
                        selectedSheetIds.includes(sheet.id) ? "text-indigo-700" : "text-slate-700"
                      )}>{sheet.name}</span>
                    </div>
                    <div className={cn(
                      "w-5 h-5 rounded-full border flex items-center justify-center transition-all",
                      selectedSheetIds.includes(sheet.id)
                        ? "bg-indigo-600 border-indigo-600"
                        : "border-slate-300 bg-white"
                    )}>
                      {selectedSheetIds.includes(sheet.id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-between gap-3">
              <button
                onClick={() => {
                  setSheetSelection(null);
                  setSelectedSheetIds([]);
                }}
                className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                disabled={(selectedSheetIds?.length || 0) === 0}
                onClick={async () => {
                  const file = { id: sheetSelection.fileId, name: sheetSelection.fileName, mimeType: 'application/vnd.google-apps.spreadsheet' };
                  const selectedSheets = sheetSelection.sheets.filter(s => selectedSheetIds.includes(s.id));
                  setSheetSelection(null);
                  setSelectedSheetIds([]);
                  
                  for (const sheet of selectedSheets) {
                    await handlePullDriveFile(file, sheet.id, `${file.name} - ${sheet.name}`, sheet.name);
                  }
                }}
                className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-all shadow-md shadow-indigo-200 disabled:opacity-50 disabled:shadow-none text-sm"
              >
                Import {(selectedSheetIds?.length || 0)} {(selectedSheetIds?.length || 0) === 1 ? 'Sheet' : 'Sheets'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {isNewFolderModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
                  <FolderPlus className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Create New Folder</h2>
                  <p className="text-xs text-slate-500">Enter a name for the new folder</p>
                </div>
              </div>
              <button onClick={() => setIsNewFolderModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <input
                type="text"
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                placeholder="Folder name"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setIsNewFolderModalOpen(false)}
                className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || isCreatingFolder}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center shadow-md disabled:opacity-50 text-sm"
              >
                {isCreatingFolder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {importProgress && (
        <ImportProgressModal
          key="import-progress"
          progress={importProgress}
          onClose={() => {
            updateImportProgress(null);
          }}
        />
      )}
    </div>
  );
}
